(() => {
  const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
  const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
  const sizeAliases = {
    XS: ['XS', 'X-SMALL', 'EXTRA SMALL', 'EXTRA-SMALL'],
    S: ['S', 'SMALL'],
    M: ['M', 'MEDIUM'],
    L: ['L', 'LARGE'],
    XL: ['XL', 'X-LARGE', 'EXTRA LARGE', 'EXTRA-LARGE'],
    '2XL': ['2XL', '2X', '2X LARGE', '2X-LARGE', 'XXL', 'XX-LARGE'],
    '3XL': ['3XL', '3X', '3X LARGE', '3X-LARGE', 'XXXL', 'XXX-LARGE'],
  };
  const storagePrefix = 'inknfiber_custom_design_';
  const legacyStoragePrefix = 'inknfiber-custom-design:';
  const latestStorageKey = `${storagePrefix}latest`;
  const guidelinesSessionKey = 'inknfiber_placement_guidelines_accepted';
  const maxSinglePreviewStorageLength = 150000;
  const maxSavedJsonLength = 1000000;
  const backendBaseUrl = (() => {
    const configuredUrl = String(window.InkNfiberConfig?.backendUrl || '').trim();
    if (!configuredUrl) return '';

    try {
      return new URL(configuredUrl, window.location.origin).toString().replace(/\/$/, '');
    } catch (_error) {
      return '';
    }
  })();
  const backendEndpoint = (path) => {
    const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${path || ''}`;
    return backendBaseUrl ? `${backendBaseUrl}${normalizedPath}` : normalizedPath;
  };
  const customDesignUploadEndpoint = backendEndpoint('/custom-design-upload');
  const customDesignFileUploadEndpoint = backendEndpoint('/custom-design-file-upload');
  const customDesignAssetEndpoint = backendEndpoint('/custom-design-preview');
  const aiDesignGenerateEndpoint = backendEndpoint('/ai-design-generate');
  const aiBackgroundRemoveEndpoint = backendEndpoint('/ai-background-remove');
  const aiPerformanceDimensions = { fast: 1024, balanced: 1536, quality: 2048 };
  const aiSessionCache = window.InkNfiberAiSessionCache || (window.InkNfiberAiSessionCache = {
    designs: new Map(),
    backgrounds: new Map(),
  });
  const aiPromptHelper = window.InkNfiberAiDesignPromptHelper || (window.InkNfiberAiDesignPromptHelper = (() => {
    const styleInstructions = {
      'Clean Vector': 'Clean vector illustration, bold clean outlines',
      Vintage: 'Vintage illustration with lightly distressed print texture',
      Cartoon: 'Original cartoon illustration with expressive shapes',
      Minimal: 'Minimal graphic design with simple bold shapes',
      'Bold Graphic': 'Bold graphic illustration with strong contrast',
      Mascot: 'Original mascot illustration with dynamic character energy',
      Retro: 'Retro inspired illustration with classic print-shop styling',
      'Realistic Illustration': 'Realistic illustration with polished print-ready detail',
    };
    const outputIntros = {
      'T-Shirt Design': 'Original t-shirt design of',
      'DTF Transfer': 'Original DTF transfer design of',
      'Sticker Style': 'Original sticker-style design of',
      'Logo Style': 'Original logo-style mark of',
      'Mascot Design': 'Original mascot design of',
    };
    const riskyTerms = [
      'adidas', 'barbie', 'batman', 'celebrity', 'coca cola', 'coca-cola', 'disney', 'donald duck',
      'dragon ball', 'fortnite', 'harry potter', 'hello kitty', 'logo', 'marvel', 'mickey',
      'mickey mouse', 'minecraft', 'nba', 'nfl', 'nike', 'nintendo', 'pokemon',
      'spongebob', 'star wars', 'super mario', 'superman', 'taylor swift', 'trademark',
    ];
    const warning = 'This may be rejected because it references a brand, copyrighted character, celebrity, or logo. Try describing an original design instead.';
    const normalizeOption = (value, options, fallback) => (options.includes(String(value || '').trim()) ? String(value || '').trim() : fallback);

    return {
      buildAiDesignPrompt(userPrompt, options = {}) {
        const originalPrompt = String(userPrompt || '').trim();
        const style = normalizeOption(options.style, Object.keys(styleInstructions), 'Clean Vector');
        const outputType = normalizeOption(options.outputType, Object.keys(outputIntros), 'T-Shirt Design');
        const includeText = Boolean(options.includeText);
        const enhancedPrompt = originalPrompt
          ? `${outputIntros[outputType]} ${originalPrompt}. ${styleInstructions[style]}, centered composition, high contrast, print-ready DTF artwork, transparent background. ${includeText ? 'If text is requested, keep lettering clean, readable, and original.' : 'No text, no letters, no words.'} Avoid brand names, copyrighted characters, celebrities, logos, and trademarked designs.`
          : '';
        return {
          originalPrompt,
          enhancedPrompt,
          style,
          outputType,
          includeText,
          warning: this.getAiPromptSafetyWarning(originalPrompt),
        };
      },
      getAiPromptSafetyWarning(userPrompt) {
        const prompt = String(userPrompt || '').toLowerCase();
        return riskyTerms.some((term) => prompt.includes(term)) ? warning : '';
      },
    };
  })());
  const designerDebug = new URLSearchParams(window.location.search).get('designer_debug') === '1';
  const defaultShirtColorPalette = [
    { label: 'White', hex: '#F8F8F8', color: '#F8F8F8', enabled: true },
    { label: 'Black', hex: '#434141', color: '#434141', enabled: true },
    { label: 'Royal Blue', hex: '#3D428B', color: '#3D428B', enabled: true },
    { label: 'Lime Flurecent', hex: '#BFFF00', color: '#BFFF00', enabled: true },
    { label: 'Orange Flurecent', hex: '#FF6A00', color: '#FF6A00', enabled: true },
    { label: 'Flame Red', hex: '#C91F24', color: '#C91F24', enabled: true },
    { label: 'Forest Green', hex: '#1F5F3A', color: '#1F5F3A', enabled: true },
    { label: 'Light Blue', hex: '#8EC9F5', color: '#8EC9F5', enabled: true },
    { label: 'Dark Grey', hex: '#54585A', color: '#54585A', enabled: true },
    { label: 'Yellow', hex: '#FFD100', color: '#FFD100', enabled: true },
    { label: 'Maroon Red', hex: '#6E1F2F', color: '#6E1F2F', enabled: true },
    { label: 'Navy Blue', hex: '#1F355E', color: '#1F355E', enabled: true },
  ];

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const round = (value) => Math.round(value * 10) / 10;
  const normalize = (value) => String(value || '').trim().toUpperCase();
  const compact = (value) => normalize(value).replace(/[^A-Z0-9]/g, '');
  const DEFAULT_BLACK_HEX = '#434141';
  const transformDebugLog = (...args) => {
    if (designerDebug) console.log('[InkNfiber designer]', ...args);
  };

  function generateId(prefix = 'design') {
    if (window.crypto?.randomUUID) return `${prefix}-${window.crypto.randomUUID()}`;
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function imageDimensionsFromSrc(src) {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve({
        naturalWidth: image.naturalWidth || image.width || 0,
        naturalHeight: image.naturalHeight || image.height || 0,
      });
      image.onerror = () => resolve({ naturalWidth: 0, naturalHeight: 0 });
      image.src = src;
    });
  }

  function dataUrlToBlob(dataUrl) {
    const [header, base64Data] = String(dataUrl || '').split(',');
    const mimeMatch = header?.match(/^data:([^;]+);base64$/);
    if (!mimeMatch || !base64Data) return null;

    const binary = atob(base64Data);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return new Blob([bytes], { type: mimeMatch[1] });
  }

  function normalizeAiPerformanceMode(value) {
    const mode = String(value || '').trim().toLowerCase();
    return ['fast', 'balanced', 'quality'].includes(mode) ? mode : 'fast';
  }

  function aiMaxDimensionForMode(mode) {
    return aiPerformanceDimensions[normalizeAiPerformanceMode(mode)] || aiPerformanceDimensions.fast;
  }

  function aiFileCacheKey(file, mode, scope = 'background') {
    return [
      scope,
      normalizeAiPerformanceMode(mode),
      file?.name || 'artwork',
      file?.type || '',
      file?.size || 0,
      file?.lastModified || 0,
    ].join(':');
  }

  function aiPromptCacheKey(prompt, mode, scope = 'design') {
    return [scope, normalizeAiPerformanceMode(mode), String(prompt || '').trim().toLowerCase()].join(':');
  }

  function aiCanvasToBlob(canvas, type = 'image/png', quality = 0.86) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Unable to optimize image before AI processing.'));
      }, type, quality);
    });
  }

  async function optimizeImageForAi(file, maxDimension) {
    if (!file || !allowedTypes.includes(file.type) || !maxDimension || maxDimension <= 0) {
      return { file, optimized: false };
    }

    let imageBitmap = null;
    let image = null;
    let objectUrl = '';

    try {
      if ('createImageBitmap' in window) {
        imageBitmap = await createImageBitmap(file);
      } else {
        objectUrl = URL.createObjectURL(file);
        image = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('Unable to prepare image before AI processing.'));
          img.src = objectUrl;
        });
      }

      const sourceWidth = imageBitmap?.width || image?.naturalWidth || image?.width || 0;
      const sourceHeight = imageBitmap?.height || image?.naturalHeight || image?.height || 0;
      const longestSide = Math.max(sourceWidth, sourceHeight);
      if (!sourceWidth || !sourceHeight || longestSide <= maxDimension) {
        return { file, optimized: false, width: sourceWidth, height: sourceHeight };
      }

      const scale = maxDimension / longestSide;
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) return { file, optimized: false, width: sourceWidth, height: sourceHeight };
      context.drawImage(imageBitmap || image, 0, 0, width, height);

      const outputType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
      const blob = await aiCanvasToBlob(canvas, outputType, 0.86);
      const extension = outputType === 'image/jpeg' ? 'jpg' : 'png';
      const baseName = (file.name || 'artwork').replace(/\.[a-z0-9]+$/i, '') || 'artwork';
      return {
        file: new File([blob], `${baseName}-ai-${maxDimension}.${extension}`, { type: outputType }),
        optimized: true,
        width,
        height,
        originalWidth: sourceWidth,
        originalHeight: sourceHeight,
      };
    } catch (_error) {
      return { file, optimized: false };
    } finally {
      if (imageBitmap?.close) imageBitmap.close();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  }

  function parseVariants(designer) {
    const variantsScript = designer.querySelector('[data-custom-product-variants]');
    if (!variantsScript) return [];

    try {
      const parsedVariants = JSON.parse(variantsScript.textContent || '[]');
      if (!Array.isArray(parsedVariants)) return [];

      return parsedVariants.map((variant) => ({
        ...variant,
        price: moneyToCents(variant.price_cents ?? variant.price),
      }));
    } catch (_) {
      return [];
    }
  }

  function parseFeeProduct(designer, fallback = {}, selector = '[data-custom-design-fee-product]') {
    const feeProductScript = designer.querySelector(selector);
    let parsedProduct = {};

    if (feeProductScript) {
      try {
        parsedProduct = JSON.parse(feeProductScript.textContent || '{}') || {};
      } catch (_) {
        parsedProduct = {};
      }
    }

    const parsedVariants = Array.isArray(parsedProduct.variants) ? parsedProduct.variants : [];
    const variants = parsedVariants.map((variant) => ({
      ...variant,
      id: String(variant.id || ''),
      price: moneyToCents(variant.price_cents ?? variant.price),
      available: variant.available !== false,
    })).filter((variant) => variant.id);

    if (!variants.length && fallback.variantId) {
      variants.push({
        id: String(fallback.variantId),
        title: fallback.title || 'Custom Design Upload Fee',
        option1: fallback.title || 'Custom Design Upload Fee',
        option2: '',
        option3: '',
        available: fallback.available === true,
        price: moneyToCents(fallback.priceCents),
      });
    }

    return {
      id: String(parsedProduct.id || fallback.productId || ''),
      title: parsedProduct.title || fallback.title || 'Custom Design Upload Fee',
      handle: parsedProduct.handle || fallback.handle || '',
      variants,
    };
  }

  function normalizeSwatchHex(rawHex) {
    let hex = String(rawHex || '').trim();

    try {
      if (/%[0-9a-fA-F]{2}/.test(hex)) {
        hex = decodeURIComponent(hex);
      }
    } catch (_) {
      // Continue with the raw value when decoding fails.
    }

    if (!hex) return '';

    const normalized = hex.startsWith('#') ? hex : `#${hex}`;
    const body = normalized.replace('#', '');
    if (!/^[0-9a-fA-F]{3,6}$/.test(body)) return '';
    if (body.length === 3) {
      return `#${body.split('').map((character) => character + character).join('').toUpperCase()}`;
    }
    if (body.length !== 6) return '';
    return `#${body.toUpperCase()}`;
  }

  function parseShirtColorPalette(designer) {
    const paletteScript = designer?.querySelector?.('[data-inknfiber-shirt-colors]');
    if (!paletteScript) {
      return {
        source: 'fallback',
        palette: defaultShirtColorPalette.map((entry) => ({ ...entry })),
      };
    }

    try {
      const parsed = JSON.parse(paletteScript.textContent || '[]');
      if (!Array.isArray(parsed)) throw new Error('Shirt color palette should be an array');

      const normalized = parsed
        .map((entry, index) => {
          const hex = normalizeSwatchHex(entry?.hex);
          return {
            label: String(entry?.label || '').trim(),
            hex,
            color: hex,
            enabled: entry?.enabled !== false,
            index,
          };
        })
        .filter((item) => item.label && item.hex);

      const resolved = normalized.map((entry) => ({
        label: entry.label,
        hex: entry.hex,
        color: entry.color,
        enabled: entry.enabled,
        index: entry.index,
      }));

      if (resolved.length) {
        return {
          source: 'theme-editor',
          palette: resolved,
        };
      }
    } catch (_) {
      // Fallback handled below.
    }

    return {
      source: 'fallback',
      palette: defaultShirtColorPalette.map((entry) => ({ ...entry })),
    };
  }

  function moneyToCents(value) {
    if (value === undefined || value === null || value === '') return 0;

    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return 0;
      return Math.round(value);
    }

    const stringValue = String(value).trim();
    if (!stringValue) return 0;

    const cleaned = stringValue.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed)) return 0;

    return stringValue.includes('.') ? Math.round(parsed * 100) : Math.round(parsed);
  }

  function normalizePlacementName(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  function optionMatchesSize(value, size) {
    const normalized = normalize(value);
    const compacted = compact(value);

    return (sizeAliases[size] || [size]).some((alias) => {
      return normalized === normalize(alias) || compacted === compact(alias);
    });
  }

  function variantSizeValues(variant) {
    const titleParts = String(variant.title || '')
      .split(/[\/|,]/)
      .map((part) => part.trim())
      .filter(Boolean);

    return [variant.option1, variant.option2, variant.option3, variant.title, ...titleParts].filter(Boolean);
  }

  function findVariantForSize(variants, size) {
    const availableVariants = variants.filter((variant) => variant.available !== false);
    return availableVariants.find((variant) => variantSizeValues(variant).some((option) => optionMatchesSize(option, size))) || null;
  }

  function storageKeys(designId) {
    return [`${storagePrefix}${designId}`, `${legacyStoragePrefix}${designId}`];
  }

  function canonicalStorageKey(designId) {
    return `${storagePrefix}${designId}`;
  }

  function serializedLength(value) {
    try {
      return JSON.stringify(value).length;
    } catch (_) {
      return Infinity;
    }
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format((Number(cents) || 0) / 100);
  }

  function customDesignSizePriceTable(xsXlPrice, twoXlPrice, threeXlPrice) {
    return {
      XS: xsXlPrice,
      S: xsXlPrice,
      M: xsXlPrice,
      L: xsXlPrice,
      XL: xsXlPrice,
      '2XL': twoXlPrice,
      '3XL': threeXlPrice,
    };
  }

  function parseCustomDesignPriceTables(designer) {
    const readPrice = (attribute, fallback) => designer.getAttribute(attribute) || fallback;
    const legacyXsXlPrice = readPrice('data-custom-design-price-xs-xl-cents', '1999');
    const legacyTwoXlPrice = readPrice('data-custom-design-price-2xl-cents', '2499');
    const legacyThreeXlPrice = readPrice('data-custom-design-price-3xl-cents', '2999');

    return {
      white: customDesignSizePriceTable(
        moneyToCents(readPrice('data-custom-design-white-xs-xl-price-cents', legacyXsXlPrice)),
        moneyToCents(readPrice('data-custom-design-white-2xl-price-cents', legacyTwoXlPrice)),
        moneyToCents(readPrice('data-custom-design-white-3xl-price-cents', legacyThreeXlPrice))
      ),
      color: customDesignSizePriceTable(
        moneyToCents(readPrice('data-custom-design-color-xs-xl-price-cents', legacyXsXlPrice)),
        moneyToCents(readPrice('data-custom-design-color-2xl-price-cents', legacyTwoXlPrice)),
        moneyToCents(readPrice('data-custom-design-color-3xl-price-cents', legacyThreeXlPrice))
      ),
    };
  }

  function fileIsAllowed(file) {
    if (!file) return false;

    const name = file.name.toLowerCase();
    const hasAllowedExtension = allowedExtensions.some((extension) => name.endsWith(extension));
    return allowedTypes.includes(file.type) && hasAllowedExtension;
  }

  function debugLog(designer, message, details = {}) {
    if (!designerDebug) return;

    console.debug('[InkNfiber custom designer]', message, details);
  }

  function placementGuidelinesAccepted() {
    try {
      return sessionStorage.getItem(guidelinesSessionKey) === 'true';
    } catch (_) {
      return false;
    }
  }

  function findDesigners(root = document) {
    const designers = [];

    if (root instanceof Element && root.matches('[data-inknfiber-custom-designer]')) {
      designers.push(root);
    }

    root.querySelectorAll?.('[data-inknfiber-custom-designer]').forEach((designer) => designers.push(designer));
    return designers;
  }

  function initPlacementGuidelines(root = document) {
    findDesigners(root).forEach((designer) => {
      const placementModal = designer.querySelector('[data-placement-guidelines-modal]');
      if (!placementModal) {
        debugLog(designer, 'Placement modal not found.');
        return;
      }

      const placementCheckbox = placementModal.querySelector('[data-placement-guidelines-checkbox]');
      const placementProceed = placementModal.querySelector('[data-placement-guidelines-proceed]');
      const placementCancelButtons = Array.from(placementModal.querySelectorAll('[data-placement-guidelines-cancel]'));
      const placementImage = placementModal.querySelector('[data-placement-guidelines-image]');
      const placementImageError = placementModal.querySelector('[data-placement-guidelines-image-error]');

      const showImageError = () => {
        if (placementImageError) placementImageError.hidden = false;
      };

      const closePlacementGuidelines = () => {
        placementModal.hidden = true;
        designer.classList.remove('is-placement-modal-open');
        document.documentElement.classList.remove('inknfiber-placement-modal-is-open');
        document.body.classList.remove('inknfiber-placement-modal-is-open');
      };

      const openPlacementGuidelines = () => {
        if (placementGuidelinesAccepted()) {
          debugLog(designer, 'Placement modal skipped because session acceptance exists.', {
            key: guidelinesSessionKey,
          });
          return;
        }

        placementModal.hidden = false;
        designer.classList.add('is-placement-modal-open');
        document.documentElement.classList.add('inknfiber-placement-modal-is-open');
        document.body.classList.add('inknfiber-placement-modal-is-open');

        if (placementProceed) {
          placementProceed.disabled = !placementCheckbox?.checked;
        }

        window.requestAnimationFrame(() => {
          placementModal.querySelector('.inknfiber-placement-modal__dialog')?.focus();
        });

        debugLog(designer, 'Placement modal opened.', {
          key: guidelinesSessionKey,
          imageSrc: placementImage?.getAttribute('src') || '',
        });
      };

      if (placementModal.dataset.modalInitialized !== 'true') {
        placementModal.dataset.modalInitialized = 'true';

        placementCheckbox?.addEventListener('change', () => {
          if (placementProceed) placementProceed.disabled = !placementCheckbox.checked;
        });

        placementProceed?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (!placementCheckbox?.checked) return;

          try {
            sessionStorage.setItem(guidelinesSessionKey, 'true');
          } catch (_) {
            // If session storage is blocked, closing still lets the customer proceed in this page session.
          }

          closePlacementGuidelines();
        });

        placementCancelButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (window.history.length > 1) {
              window.history.back();
            } else {
              window.location.href = '/';
            }
          });
        });

        placementImage?.addEventListener('error', showImageError);
      }

      if (placementImage?.complete && placementImage.naturalWidth === 0) {
        showImageError();
      }

      openPlacementGuidelines();
    });
  }

  function initCustomDesigners(root = document) {
    findDesigners(root).forEach((designer) => {
    if (designer.dataset.initialized === 'true') return;

    designer.dataset.initialized = 'true';

    const defaultVariantId = designer.dataset.productVariantId;
    const enableRotation = designer.dataset.enableRotation === 'true';
    const aiPhotoshootEnabled = designer.dataset.aiPhotoshootEnabled === 'true';
    const aiBackgroundRemoverEnabled = designer.dataset.aiBackgroundRemoverEnabled === 'true';
    const aiPerformanceMode = normalizeAiPerformanceMode(designer.dataset.aiPerformanceMode);
    const aiBackgroundMaxDimension = aiMaxDimensionForMode(aiPerformanceMode);
    const sleeveViewsEnabled = designer.dataset.enableSleeveViews === 'true';
    const variants = parseVariants(designer);
    const customDesignPriceTables = parseCustomDesignPriceTables(designer);
    const readIntegerSetting = (key, fallback) => {
      const parsed = Number(designer.dataset[key]);
      return Number.isFinite(parsed) ? Math.round(parsed) : fallback;
    };
    const customDesignPrintRateCentsPerSqIn = Math.max(0, Math.round(Number(designer.dataset.customDesignPrintRateCentsPerSqIn || 3)) || 3);
    const customDesignMinimumPrintChargeCents = Math.max(0, Math.round(Number(designer.dataset.customDesignMinimumPrintChargeCents || 300)) || 300);
    const customDesignPrintConfig = {
      minimumChargeCents: Math.max(0, readIntegerSetting('customDesignPrintMinimumChargeCents', 325)),
      maximumChargeCents: Math.max(0, readIntegerSetting('customDesignPrintMaximumChargeCents', 725)),
      wasteMultiplierPercent: Math.max(100, readIntegerSetting('customDesignPrintWasteMultiplierPercent', 120)),
      machineRateCentsPerMinute: Math.max(0, readIntegerSetting('customDesignPrintMachineRateCentsPerMinute', 10)),
      handlingSetupCents: Math.max(0, readIntegerSetting('customDesignPrintHandlingSetupCents', 125)),
      profitMultiplierPercent: Math.max(100, readIntegerSetting('customDesignPrintProfitMultiplierPercent', 165)),
      roundingIncrementCents: Math.max(1, readIntegerSetting('customDesignPrintRoundingIncrementCents', 25)),
    };
    customDesignPrintConfig.maximumChargeCents = Math.max(
      customDesignPrintConfig.minimumChargeCents,
      customDesignPrintConfig.maximumChargeCents
    );
    const feeProduct = parseFeeProduct(designer, {
      productId: designer.dataset.feeProductId || '',
      variantId: designer.dataset.feeVariantId || '',
      priceCents: designer.dataset.feePriceCents,
      title: designer.dataset.feeTitle || 'Custom Design Upload Fee',
      available: designer.dataset.feeAvailable === 'true',
    });
    const aiFeeProduct = aiPhotoshootEnabled ? parseFeeProduct(designer, {
      productId: designer.dataset.aiFeeProductId || '',
      variantId: designer.dataset.aiFeeVariantId || '',
      priceCents: designer.dataset.aiFeePriceCents,
      title: designer.dataset.aiFeeTitle || 'AI Design Generation Fee',
      available: designer.dataset.aiFeeAvailable === 'true',
    }, '[data-ai-design-fee-product]') : {
      id: '',
      title: '',
      variants: [],
    };
    const estimatorCheckoutPricingEnabled = false;
    const estimatorCheckoutPriceMode = 'reference_only';
    const priceAdjustmentProduct = parseFeeProduct(designer, {
      productId: designer.dataset.priceAdjustmentProductId || '',
      variantId: designer.dataset.priceAdjustmentVariantId || '',
      priceCents: designer.dataset.priceAdjustmentCents,
      title: designer.dataset.priceAdjustmentTitle || 'Estimator price adjustment',
      available: designer.dataset.priceAdjustmentAvailable === 'true',
    }, '[data-custom-design-price-adjustment-product]');
    const feeProductId = feeProduct.id;
    const feeTitle = feeProduct.title;
    const feeVariants = feeProduct.variants;
    const aiFeeProductId = aiFeeProduct.id;
    const aiFeeTitle = aiFeeProduct.title;
    const aiFeeVariant = aiFeeProduct.variants[0] || null;
    const priceAdjustmentVariant = priceAdjustmentProduct.variants[0] || null;
    const priceAdjustmentVariantId = priceAdjustmentVariant?.id || '';
    const priceAdjustmentCents = moneyToCents(priceAdjustmentVariant?.price);
    const priceAdjustmentAvailable = Boolean(priceAdjustmentVariant?.available);
    const sizeVariantMap = Object.fromEntries(
      sizes.map((size) => [size, findVariantForSize(variants, size) || null])
    );
    const productHasSizeVariants = sizes.some((size) => Boolean(sizeVariantMap[size]));
    const searchParams = new URLSearchParams(window.location.search);
    const debugEnabled = designerDebug;
    const editDesignId = searchParams.get('edit_design');
    const reorderId = searchParams.get('reorder');
    const reorderToken = searchParams.get('reorder_token');
    const firstAvailableVariant = variants.find((variant) => variant.available !== false) || null;
    const firstAvailableVariantId = firstAvailableVariant?.id || (!variants.length ? defaultVariantId : '');

    const uploadInput = designer.querySelector('[data-design-upload]');
    const fileName = designer.querySelector('[data-file-name]');
    const uploadError = designer.querySelector('[data-upload-error]');
    const backgroundRemovalStatus = designer.querySelector('[data-background-removal-status]');
    const artworkQualityContainer = designer.querySelector('[data-artwork-quality-check]');
    const artworkChecker = window.InkNfiberArtworkQualityChecker || null;
    const artworkCheckerEnabled = designer.dataset.artworkCheckerEnabled !== 'false';
    const artworkCheckerOptions = {
      maxFileSizeMb: Number(designer.dataset.artworkCheckerMaxFileSizeMb || 25) || 25,
      minRecommendedLongestSide: Number(designer.dataset.artworkCheckerMinLongestSide || 1000) || 1000,
      requirePass: designer.dataset.requireArtworkQualityPass !== 'false',
      requireTransparent: designer.dataset.requireTransparentBackground !== 'false',
      debug: designerDebug,
      fileSizeBlocks: false,
    };
    const elementList = designer.querySelector('[data-element-list]');
    const uploadDropZone = designer.querySelector('[data-upload-drop-zone]');
    const elementsRoot = designer.querySelector('[data-design-elements]');
    const textInput = designer.querySelector('[data-text-input]');
    const textFont = designer.querySelector('[data-text-font]');
    const textColor = designer.querySelector('[data-text-color]');
    const addTextButton = designer.querySelector('[data-add-text]');
    const symbolPicker = designer.querySelector('[data-symbol-picker]');
    const aiPromptInput = designer.querySelector('[data-ai-prompt]');
    const aiDesignStyleSelect = designer.querySelector('[data-ai-design-style]');
    const aiOutputTypeSelect = designer.querySelector('[data-ai-output-type]');
    const aiIncludeTextInput = designer.querySelector('[data-ai-include-text]');
    const aiPromptWarning = designer.querySelector('[data-ai-prompt-warning]');
    const aiGenerateButton = designer.querySelector('[data-generate-ai-design]');
    const aiStatus = designer.querySelector('[data-ai-status]');
    const aiPreview = designer.querySelector('[data-ai-preview]');
    const aiPreviewImage = designer.querySelector('[data-ai-preview-image]');
    const aiAddButton = designer.querySelector('[data-add-ai-design]');
    const toolbarButtons = Array.from(designer.querySelectorAll('[data-customizer-tool], [data-customizer-toolbar-target]'));
    const toolPanels = Array.from(designer.querySelectorAll('[data-customizer-panel]'));
    const productsPanel = designer.querySelector('[data-customizer-panel="products"]');
    const sizesPanel = designer.querySelector('[data-customizer-panel="sizes"]');
    const notesPanel = designer.querySelector('[data-customizer-panel="notes"]');
    const accordionToggles = Array.from(designer.querySelectorAll('[data-designer-accordion-toggle]'));
    const accordionPanels = Array.from(designer.querySelectorAll('[data-designer-accordion-panel]'));
    const notesModal = designer.querySelector('[data-design-notes-modal]');
    const notesOpenButtons = Array.from(designer.querySelectorAll('[data-design-notes-open]'));
    const notesCloseButtons = Array.from(designer.querySelectorAll('[data-design-notes-close]'));
    const notesSaveButton = notesPanel?.querySelector('[data-design-notes-save]') || designer.querySelector('[data-design-notes-save]');
    const designNoteFront = notesPanel?.querySelector('[data-design-note-front]') || designer.querySelector('[data-design-note-front]');
    const designNoteBack = notesPanel?.querySelector('[data-design-note-back]') || designer.querySelector('[data-design-note-back]');
    const designNoteExtra = notesPanel?.querySelector('[data-design-note-extra]') || designer.querySelector('[data-design-note-extra]');
    const reviewButton = designer.querySelector('[data-review-design]');
    const reviewModal = designer.querySelector('[data-review-design-modal]');
    const reviewCloseButtons = Array.from(designer.querySelectorAll('[data-review-design-close]'));
    const reviewAddToCartButton = designer.querySelector('[data-review-add-to-cart]');
    const reviewDialog = designer.querySelector('.inknfiber-custom-designer__review-dialog');
    const reviewPreviewImage = designer.querySelector('[data-review-preview-image]');
    const reviewDetails = designer.querySelector('[data-review-details]');
    const reviewSizes = designer.querySelector('[data-review-sizes]');
    const reviewNotes = designer.querySelector('[data-review-notes]');
    const reviewNotesList = designer.querySelector('[data-review-notes-list]');
    const reviewStatus = designer.querySelector('[data-review-status]');
    const mockup = designer.querySelector('[data-mockup]');
    const mockupCanvas = designer.querySelector('[data-mockup-canvas]');
    const safeArea = designer.querySelector('[data-safe-area]');
    const emptyState = designer.querySelector('[data-empty-state]');
    const mobilePreviewMediaQuery = window.matchMedia('(max-width: 749px)');
    const viewLabel = designer.querySelector('[data-active-view-label]');
    const viewButtons = Array.from((productsPanel || designer).querySelectorAll('[data-view-button]'));
    const mockupImages = Array.from(designer.querySelectorAll('[data-mockup-image]'));
    const shirtFallback = designer.querySelector('[data-shirt-fallback]');
    const colorSelect = (productsPanel || designer).querySelector('[data-shirt-color]');
    const colorSwatchButtons = Array.from((productsPanel || designer).querySelectorAll('[data-shirt-color-option]'));
    const shirtColorPaletteSource = parseShirtColorPalette(designer);
    let shirtColorPalette = shirtColorPaletteSource.palette;
    if (shirtColorPaletteSource.source === 'fallback' && colorSwatchButtons.length) {
      shirtColorPalette = colorSwatchButtons.map((button, index) => ({
        label: String(button.dataset.value || '').trim(),
        color: normalizeSwatchHex(button.dataset.color || ''),
        hex: normalizeSwatchHex(button.dataset.color || ''),
        enabled: true,
        index,
      }))
        .filter((item) => item.label && item.hex);
    }
    if (designerDebug && !designer.dataset.inknfiberShirtColorsInitialized) {
      transformDebugLog('shirt color palette loaded', {
        paletteSource: shirtColorPaletteSource.source,
        palette: shirtColorPalette.map((color) => ({
          label: color.label,
          hex: color.hex,
          enabled: color.enabled,
        })),
        enabledCount: shirtColorPalette.filter((color) => color.enabled).length,
      });
      window.inknfiberShirtColorPalette = shirtColorPalette.map((color) => ({ ...color }));
      designer.dataset.inknfiberShirtColorsInitialized = 'true';
    }
    const placementSelect = (productsPanel || designer).querySelector('[data-print-placement]');
    const sizeRows = Array.from((sizesPanel || designer).querySelectorAll('[data-size-row]'));
    const sizeInputs = Object.fromEntries(
      sizeRows.map((row) => [row.dataset.size, row.querySelector('[data-size-quantity]')])
    );
    const pricingCategoryLabel = designer.querySelector('[data-custom-design-price-category]');
    const pricingGroupDisplays = {
      xsXl: designer.querySelector('[data-custom-design-price-group="xs-xl"]'),
      twoXl: designer.querySelector('[data-custom-design-price-group="2xl"]'),
      threeXl: designer.querySelector('[data-custom-design-price-group="3xl"]'),
    };
    const sizeRange = designer.querySelector('[data-design-size]');
    const rotationRange = designer.querySelector('[data-design-rotation]');
    const undoButton = designer.querySelector('[data-undo-transform]');
    const redoButton = designer.querySelector('[data-redo-transform]');
    const resetButton = designer.querySelector('[data-reset-design]');
    const addButton = designer.querySelector('[data-add-custom-order]');
    const viewCartLink = designer.querySelector('[data-view-cart]');
    const status = designer.querySelector('[data-designer-status]');
    const saveDesignButton = designer.querySelector('[data-save-custom-design]');
    const restoreSavedDesignButton = designer.querySelector('[data-restore-custom-design]');
    const clearSavedDesignButton = designer.querySelector('[data-clear-custom-design]');
    const saveDesignStatus = designer.querySelector('[data-save-design-status]');
    const summaryColor = designer.querySelector('[data-summary-color]');
    const summarySizes = designer.querySelector('[data-summary-sizes]');
    const summaryQuantity = designer.querySelector('[data-summary-quantity]');
    const summaryView = designer.querySelector('[data-summary-view]');
    const summaryPlacement = designer.querySelector('[data-summary-placement]');
    const summaryFile = designer.querySelector('[data-summary-file]');
    const summaryUploadCount = designer.querySelector('[data-summary-upload-count]');
    const summaryDesignSurcharge = designer.querySelector('[data-summary-design-surcharge]');
    const summaryDesignTotal = designer.querySelector('[data-summary-design-total]');
    const summaryAiDesignSurcharge = designer.querySelector('[data-summary-ai-design-surcharge]');
    const summaryTextCount = designer.querySelector('[data-summary-text-count]');
    const summarySymbolCount = designer.querySelector('[data-summary-symbol-count]');
    const summaryProductSubtotal = designer.querySelector('[data-summary-product-subtotal]');
    const summaryEstimatorNote = designer.querySelector('[data-summary-estimator-note]');
    const summaryBulkDiscountTier = designer.querySelector('[data-summary-bulk-discount-tier]');
    const summaryBulkDiscountSavings = designer.querySelector('[data-summary-bulk-discount-savings]');
    const summaryProductTotalAfterDiscount = designer.querySelector('[data-summary-product-total-after-discount]');
    const summaryBulkDiscountNotice = designer.querySelector('[data-summary-bulk-discount-notice]');
    const summaryTotal = designer.querySelector('[data-summary-total] strong');
    const artworkEstimateBody = designer.querySelector('[data-artwork-cost-estimate-body]');
    const artworkEstimator = window.InkNfiberArtworkCostEstimator || null;
    const artworkEstimatorConfig = artworkEstimator?.configFromDataset?.(designer.dataset, 5) || null;
    const originalUploadFiles = new Map();
    const artworkQualityResults = new Map();
    const restoredObjectUrls = new Set();
    let lastCustomDesignUpload = null;
    let mockupRenderToken = 0;
    let lastShirtColorDebugState = '';
    let selectedShirtColorState = null;
    let placeholderRefreshFrame = 0;
    let placeholderRefreshTimer = 0;
    let autoSaveTimer = 0;
    let artworkEstimateTimer = 0;
    let printChargeAnalysisTimer = 0;
    let printChargeAnalysisPromise = null;
    let printChargeAnalysisKey = '';
    let printChargeAnalysisResult = null;
    let latestArtworkEstimate = null;
    let latestArtworkEstimateKey = '';
    let autoSaveEnabled = false;
    let generatedAiDesign = null;
    let reviewPreviewObjectUrl = '';

    const state = {
      designId: editDesignId || generateId('design'),
      activeView: 'front',
      activeElementId: '',
      elements: [],
      placementsByView: {
        front: 'Full Front',
        back: 'Full Back',
        'left-sleeve': 'Left Sleeve',
        'right-sleeve': 'Right Sleeve',
      },
      dragging: false,
      dragPointerId: null,
      dragStart: null,
      transformMode: '',
      transformPointerId: null,
      transformStart: null,
      interactionElementId: '',
      history: {
        undo: [],
        redo: [],
      },
      designNotes: {
        front: '',
        back: '',
        extra: '',
      },
    };

    const placementDefinitions = {
      'Left Chest': {
        view: 'front',
        dimensions: '5"W x 5"H',
        widthInches: 5,
        heightInches: 5,
        geometry: { centerXPercent: 62, centerYPercent: 31, widthPercent: 15, heightPercent: 12.6 },
      },
      'Across Chest': {
        view: 'front',
        dimensions: '12"W x 4"H',
        widthInches: 12,
        heightInches: 4,
        geometry: { centerXPercent: 50, centerYPercent: 34, widthPercent: 36, heightPercent: 10.1 },
      },
      'Medium Front': {
        view: 'front',
        dimensions: '8"W x 8"H',
        widthInches: 8,
        heightInches: 8,
        geometry: { centerXPercent: 51, centerYPercent: 39, widthPercent: 24, heightPercent: 20.2 },
      },
      'Full Front': {
        view: 'front',
        dimensions: '12"W x 14"H',
        widthInches: 12,
        heightInches: 14,
        geometry: { centerXPercent: 51, centerYPercent: 45, widthPercent: 37, heightPercent: 36 },
      },
      'Across Shoulder': {
        view: 'back',
        dimensions: '12"W x 5"H',
        widthInches: 12,
        heightInches: 5,
        geometry: { centerXPercent: 50, centerYPercent: 23, widthPercent: 35, heightPercent: 13 },
      },
      'Medium Back': {
        view: 'back',
        dimensions: '8"W x 8"H',
        widthInches: 8,
        heightInches: 8,
        geometry: { centerXPercent: 50, centerYPercent: 32.5, widthPercent: 30, heightPercent: 25.1 },
      },
      'Full Back': {
        view: 'back',
        dimensions: '12"W x 14"H',
        widthInches: 12,
        heightInches: 14,
        geometry: { centerXPercent: 50, centerYPercent: 39.5, widthPercent: 43, heightPercent: 42 },
      },
      'Left Sleeve': {
        view: 'left-sleeve',
        dimensions: '3"W x 3"H',
        widthInches: 3,
        heightInches: 3,
        geometry: { centerXPercent: 25, centerYPercent: 30, widthPercent: 13, heightPercent: 13, rotation: 16 },
      },
      'Right Sleeve': {
        view: 'right-sleeve',
        dimensions: '3"W x 3"H',
        widthInches: 3,
        heightInches: 3,
        geometry: { centerXPercent: 75, centerYPercent: 30, widthPercent: 13, heightPercent: 13, rotation: -16 },
      },
    };

    const placementsByView = {
      front: ['Left Chest', 'Across Chest', 'Medium Front', 'Full Front'],
      back: ['Across Shoulder', 'Medium Back', 'Full Back'],
      'left-sleeve': ['Left Sleeve'],
      'right-sleeve': ['Right Sleeve'],
    };

    const allViewKeys = ['front', 'back', 'left-sleeve', 'right-sleeve'];
    const sleeveViewKeys = ['left-sleeve', 'right-sleeve'];
    const availableViewKeys = sleeveViewsEnabled ? allViewKeys : ['front', 'back'];

    function normalizeViewKey(value) {
      const normalized = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/_/g, '-')
        .replace(/\s+/g, '-');

      return allViewKeys.includes(normalized) ? normalized : 'front';
    }

    function isSleeveView(view) {
      return sleeveViewKeys.includes(normalizeViewKey(view));
    }

    function viewIsEnabled(view) {
      return availableViewKeys.includes(normalizeViewKey(view));
    }

    function fallbackView(view, reason = 'view-fallback') {
      const normalizedView = normalizeViewKey(view);
      if (viewIsEnabled(normalizedView)) return normalizedView;

      if (designerDebug) {
        transformDebugLog('sleeve view disabled fallback', {
          reason,
          enable_sleeve_views: sleeveViewsEnabled,
          requestedView: view,
          normalizedView,
          fallbackView: 'front',
          availableViews: availableViewKeys,
        });
      }

      return 'front';
    }

    function placementView(placement) {
      const canonicalPlacement = canonicalPlacementValue(placement);
      return placementDefinitions[canonicalPlacement]?.view || 'front';
    }

    function availablePlacementOptions(view = state.activeView) {
      const safeView = fallbackView(view, 'available-placement-options');
      return (placementsByView[safeView] || placementsByView.front).filter((placement) => {
        return viewIsEnabled(placementView(placement));
      });
    }

    function ensureActiveStateAllowed(reason = 'state-sync') {
      const previousView = state.activeView;
      state.activeView = fallbackView(state.activeView, reason);

      if (previousView !== state.activeView && designerDebug) {
        transformDebugLog('active view normalized', {
          reason,
          previousView,
          nextView: state.activeView,
          enable_sleeve_views: sleeveViewsEnabled,
          availableViews: availableViewKeys,
        });
      }

      state.elements.forEach((element) => {
        if (!element.view) return;

        const previousElementView = element.view;
        const normalizedElementView = normalizeViewKey(element.view);
        if (viewIsEnabled(normalizedElementView)) {
          element.view = normalizedElementView;
          return;
        }

        element.view = 'front';

        if (designerDebug) {
          transformDebugLog('element view normalized because sleeve views are disabled', {
            reason,
            elementId: element.id,
            elementType: element.type,
            previousView: previousElementView,
            nextView: element.view,
          });
        }
      });

      const active = activeElement();
      if (active?.view) active.view = normalizeViewKey(active.view);
      if (active?.view && !viewIsEnabled(active.view)) {
        state.activeElementId = '';
      }
    }

    viewButtons.forEach((button) => {
      const enabled = viewIsEnabled(button.dataset.viewButton);
      button.hidden = !enabled;
      button.disabled = !enabled;
      button.setAttribute('aria-hidden', enabled ? 'false' : 'true');
    });

    if (designerDebug) {
      transformDebugLog('sleeve view configuration', {
        enable_sleeve_views: sleeveViewsEnabled,
        availableViews: availableViewKeys,
        renderedViewButtons: viewButtons.map((button) => ({
          view: button.dataset.viewButton,
          hidden: button.hidden,
          disabled: button.disabled,
        })),
      });
    }

    const placementAliases = {
      'center chest': 'Across Chest',
      centerchest: 'Across Chest',
      'across chest': 'Across Chest',
      acrosschest: 'Across Chest',
      'medium front': 'Medium Front',
      mediumfront: 'Medium Front',
      'across shoulder': 'Across Shoulder',
      acrossshoulder: 'Across Shoulder',
      'back collar': 'Across Shoulder',
      backcollar: 'Across Shoulder',
      'upper back': 'Medium Back',
      upperback: 'Medium Back',
      'medium back': 'Medium Back',
      mediumback: 'Medium Back',
    };

    const placementFeeLabels = {
      'left chest': 'Left Chest',
      'center chest': 'Across Chest',
      'across chest': 'Across Chest',
      'medium front': 'Medium Front',
      'full front': 'Full Front',
      'across shoulder': 'Across Shoulder',
      'back collar': 'Across Shoulder',
      'upper back': 'Medium Back',
      'medium back': 'Medium Back',
      'full back': 'Full Back',
      'left sleeve': 'Left Sleeve',
      'right sleeve': 'Right Sleeve',
    };

    const mockupSourceDimensions = {
      front: { width: 528, height: 628 },
      back: { width: 528, height: 630 },
    };

    if (designerDebug) {
      window.inknfiberPlacementConfig = Object.fromEntries(
        Object.entries(placementDefinitions).map(([key, definition]) => [
          key,
          {
            key: normalizePlacementName(key),
            label: placementDisplayName(key),
            view: definition.view,
            widthIn: definition.widthInches,
            heightIn: definition.heightInches,
            ...definition.geometry,
          },
        ])
      );
    }

    function canonicalPlacementValue(value) {
      const trimmedValue = String(value || '').trim();
      const normalizedValue = normalizePlacementName(trimmedValue);
      const compactedValue = compact(trimmedValue).toLowerCase();

      return placementAliases[normalizedValue] || placementAliases[compactedValue] || trimmedValue;
    }

    function placementMappingDetails(value) {
      const rawValue = String(value || '').trim();
      const canonicalValue = canonicalPlacementValue(rawValue);
      const rawKey = normalizePlacementName(rawValue);
      const canonicalKey = normalizePlacementName(canonicalValue);

      return {
        rawValue,
        rawKey,
        canonicalValue,
        canonicalKey,
        mapped: Boolean(rawValue) && rawKey !== canonicalKey,
        legacyCenterChestMapped: rawKey === 'center chest' || compact(rawValue) === 'CENTERCHEST',
        legacyBackCollarMapped: rawKey === 'back collar' || compact(rawValue) === 'BACKCOLLAR',
        legacyUpperBackMapped: rawKey === 'upper back' || compact(rawValue) === 'UPPERBACK',
      };
    }

    function placementDisplayName(placementValue = placementSelect?.value || 'Left Chest') {
      const canonicalValue = canonicalPlacementValue(placementValue) || 'Left Chest';
      return placementFeeLabels[normalizePlacementName(canonicalValue)] || canonicalValue;
    }

    function feeVariantPlacementValues(variant) {
      const titleParts = String(variant.title || '')
        .split(/[\/|,]/)
        .map((part) => part.trim())
        .filter(Boolean);

      return [variant.option1, variant.option2, variant.option3, variant.title, ...titleParts].filter(Boolean);
    }

    function selectedPlacementFeeVariant() {
      if (!feeVariants.length) return null;
      if (feeVariants.length === 1) return feeVariants[0];

      const canonicalPlacementName = normalizePlacementName(canonicalPlacementValue(placementSelect?.value || ''));
      const selectedPlacementNames = new Set([
        canonicalPlacementName,
        normalizePlacementName(placementDisplayName()),
      ]);
      if (canonicalPlacementName === 'across chest') selectedPlacementNames.add('center chest');
      if (canonicalPlacementName === 'across shoulder') selectedPlacementNames.add('back collar');
      if (canonicalPlacementName === 'medium back') selectedPlacementNames.add('upper back');

      return feeVariants.find((variant) => {
        return feeVariantPlacementValues(variant).some((value) => selectedPlacementNames.has(normalizePlacementName(value)));
      }) || null;
    }

    function selectedPlacementFeeDetails() {
      const variant = selectedPlacementFeeVariant();
      const priceCents = moneyToCents(variant?.price);

      return {
        productId: feeProductId,
        productTitle: feeTitle,
        variants: feeVariants,
        variant,
        variantId: variant?.id || '',
        variantTitle: variant?.title || '',
        available: Boolean(variant?.available),
        priceCents,
        placementLabel: placementDisplayName(),
      };
    }

    function selectedSizeQuantities() {
      return Object.fromEntries(
        sizes.map((size) => [size, clamp(Number(sizeInputs[size]?.value || 0), 0, 99)])
      );
    }

    function selectedSizes() {
      return Object.entries(selectedSizeQuantities())
        .filter(([, quantity]) => quantity > 0)
        .map(([size, quantity]) => ({ size, quantity }));
    }

    function totalQuantity() {
      return selectedSizes().reduce((sum, item) => sum + item.quantity, 0);
    }

    function sizeBreakdown() {
      const breakdown = selectedSizes().map((item) => `${item.size}-${item.quantity}`).join(', ');
      return breakdown || 'None selected';
    }

    function imageElements() {
      return state.elements.filter((element) => element.type === 'image');
    }

    function uploadedDesignCount() {
      return imageElements().length;
    }

    function aiImageElements() {
      if (!aiPhotoshootEnabled) return [];
      return imageElements().filter((element) => element.aiGenerated === true);
    }

    function aiDesignCount() {
      return aiImageElements().length;
    }

    function aiDesignImageKeys() {
      return aiImageElements().map((element) => element.aiImageKey || element.originalDesignKey || '').filter(Boolean);
    }

    function aiDesignPrompts() {
      return aiImageElements().map((element) => element.aiPrompt || '').filter(Boolean);
    }

    function aiDesignOriginalPrompts() {
      return aiImageElements().map((element) => element.aiOriginalPrompt || '').filter(Boolean);
    }

    function aiDesignPromptOptions() {
      return aiImageElements()
        .map((element) => [element.aiPromptStyle, element.aiPromptOutputType, element.aiPromptIncludeText ? 'text allowed' : 'no text'].filter(Boolean).join(' / '))
        .filter(Boolean);
    }

    function backgroundRemovedImageElements() {
      if (!aiBackgroundRemoverEnabled) return [];
      return imageElements().filter((element) => element.backgroundRemoved === true);
    }

    function backgroundRemovedImageKeys() {
      return backgroundRemovedImageElements()
        .map((element) => element.backgroundRemovedImageKey || '')
        .filter(Boolean);
    }

    function textElements() {
      return state.elements.filter((element) => element.type === 'text');
    }

    function symbolElements() {
      return state.elements.filter((element) => element.type === 'symbol');
    }

    function accordionPanelForToggle(toggle) {
      const panelId = toggle.getAttribute('aria-controls');
      if (!panelId) return null;

      if (window.CSS?.escape) {
        return designer.querySelector(`#${CSS.escape(panelId)}`);
      }

      return accordionPanels.find((panel) => panel.id === panelId) || null;
    }

    function syncOpenAccordionPanelHeights() {
      accordionPanels.forEach((panel) => {
        if (panel.hidden || !panel.classList.contains('is-open')) return;
        panel.style.setProperty('--accordion-panel-height', `${panel.scrollHeight}px`);
      });
    }

    function setAccordionPanel(toggle, shouldOpen) {
      const panel = accordionPanelForToggle(toggle);
      if (!panel) return;

      toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      toggle.classList.toggle('is-open', shouldOpen);
      panel.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');

      if (shouldOpen) {
        panel.hidden = false;
        panel.style.setProperty('--accordion-panel-height', `${panel.scrollHeight}px`);
        window.requestAnimationFrame(() => {
          panel.classList.add('is-open');
          panel.style.setProperty('--accordion-panel-height', `${panel.scrollHeight}px`);
        });
        return;
      }

      panel.style.setProperty('--accordion-panel-height', `${panel.scrollHeight}px`);
      window.requestAnimationFrame(() => {
        panel.classList.remove('is-open');
        panel.style.setProperty('--accordion-panel-height', '0px');
      });

      const hideCollapsedPanel = (event) => {
        if (event.target !== panel || event.propertyName !== 'max-height') return;
        panel.removeEventListener('transitionend', hideCollapsedPanel);
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      };

      panel.addEventListener('transitionend', hideCollapsedPanel);
      window.setTimeout(() => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, 280);
    }

    function initAccordionControls() {
      accordionToggles.forEach((toggle) => {
        const panel = accordionPanelForToggle(toggle);
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';

        if (panel) {
          panel.hidden = !isOpen;
          panel.classList.toggle('is-open', isOpen);
          panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
          panel.style.setProperty('--accordion-panel-height', isOpen ? `${panel.scrollHeight}px` : '0px');
        }

        toggle.addEventListener('click', () => {
          const shouldOpen = toggle.getAttribute('aria-expanded') !== 'true';
          setAccordionPanel(toggle, shouldOpen);
        });
      });
    }

    function setActiveToolbarTarget(target = 'products') {
      toolbarButtons.forEach((button) => {
        const active = (button.dataset.customizerTool || button.dataset.customizerToolbarTarget) === target;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function syncToolPanels(target = 'products') {
      if (!toolbarButtons.length) {
        toolPanels.forEach((panel) => {
          panel.hidden = false;
          panel.classList.add('is-active');
        });
        return;
      }

      toolPanels.forEach((panel) => {
        const active = panel.dataset.customizerPanel === target;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      accordionToggles.forEach((toggle) => {
        const panel = accordionPanelForToggle(toggle);
        const parentTarget = toggle.closest('[data-customizer-panel]')?.dataset.customizerPanel;
        if (parentTarget && parentTarget !== target) {
          setAccordionPanel(toggle, false);
          if (panel) panel.hidden = true;
        }
      });
    }

    function openToolbarTarget(target = 'products') {
      setActiveToolbarTarget(target);
      syncToolPanels(target);

      const targetElement = designer.querySelector(`[data-customizer-panel="${target}"]`);
      const toggle = targetElement?.querySelector?.('[data-designer-accordion-toggle]');
      if (toggle) {
        if (toolbarButtons.length) {
          accordionToggles.forEach((otherToggle) => {
            if (otherToggle !== toggle) setAccordionPanel(otherToggle, false);
          });
        }
        setAccordionPanel(toggle, true);
      }

      targetElement?.scrollIntoView?.({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }

    function openDesignNotesModal() {
      openToolbarTarget('notes');
      if (designNoteFront) designNoteFront.value = state.designNotes.front || '';
      if (designNoteBack) designNoteBack.value = state.designNotes.back || '';
      if (designNoteExtra) designNoteExtra.value = state.designNotes.extra || '';
      window.requestAnimationFrame(() => {
        (designNoteFront || designNoteBack || designNoteExtra || notesPanel).focus?.();
      });
    }

    function closeDesignNotesModal() {
      if (!notesModal) return;
      notesModal.classList.remove('is-open');
      document.documentElement.classList.remove('inknfiber-custom-notes-is-open');
      document.body.classList.remove('inknfiber-custom-notes-is-open');
      notesModal.hidden = true;
      setActiveToolbarTarget('products');
    }

    function syncDesignNotesFromFields() {
      state.designNotes.front = String(designNoteFront?.value || '').trim();
      state.designNotes.back = String(designNoteBack?.value || '').trim();
      state.designNotes.extra = String(designNoteExtra?.value || '').trim();
    }

    function syncDesignNotesToFields() {
      if (designNoteFront) designNoteFront.value = state.designNotes.front || '';
      if (designNoteBack) designNoteBack.value = state.designNotes.back || '';
      if (designNoteExtra) designNoteExtra.value = state.designNotes.extra || '';
    }

    function saveDesignNotes() {
      syncDesignNotesFromFields();
      setActiveToolbarTarget('notes');
      setStatus('Design notes saved with this custom order.', 'success');
      saveCurrentDesign({ includePreviews: false, showStatus: false });
    }

    function designNotesSummary() {
      return [
        state.designNotes.front ? `Front: ${state.designNotes.front}` : '',
        state.designNotes.back ? `Back: ${state.designNotes.back}` : '',
        state.designNotes.extra ? `Extra: ${state.designNotes.extra}` : '',
      ].filter(Boolean).join(' | ');
    }

    function activeElement() {
      return state.elements.find((element) => element.id === state.activeElementId) || null;
    }

    function elementById(elementId) {
      return state.elements.find((element) => element.id === elementId) || null;
    }

    function visibleElementsForActiveView() {
      return state.elements.filter((element) => !element.view || element.view === state.activeView);
    }

    function hasVisibleElementsForActiveView() {
      return visibleElementsForActiveView().length > 0;
    }

    function visibleLayerCount() {
      return visibleElementsForActiveView().length;
    }

    function isTypingOrControlTarget(target) {
      if (!(target instanceof Element)) return false;

      return Boolean(
        target.closest('input, textarea, select, button, [contenteditable], [role="textbox"]')
        || target.isContentEditable
      );
    }

    function debugLayerSelectionState(action = 'selection-state') {
      if (!designerDebug) return;

      transformDebugLog(`layer selection ${action}`, {
        selectedLayerId: state.activeElementId || '',
        layerCount: state.elements.length,
        visibleLayerCount: visibleLayerCount(),
        placeholderHidden: Boolean(emptyState && emptyState.hidden),
        selectedInActiveView: state.activeElementId
          ? Boolean(elementById(state.activeElementId) && (state.elements.find((element) => element.id === state.activeElementId)?.view || state.activeView) === state.activeView)
          : false,
      });
    }

    async function runArtworkQualityCheck(file, elementId = '') {
      if (!artworkCheckerEnabled || !artworkChecker || !artworkQualityContainer) return;
      if (!file) {
        artworkQualityResults.clear();
        artworkChecker.reset(artworkQualityContainer);
        return;
      }

      try {
        const result = await artworkChecker.analyzeFile(file, artworkCheckerOptions);
        if (elementId) artworkQualityResults.set(elementId, result);
        artworkChecker.reset(artworkQualityContainer);
        return result;
      } catch (error) {
        if (designerDebug) {
          transformDebugLog('artwork quality check failed', {
            fileName: file?.name || '',
            error: error?.message || error,
          });
        }
      }
    }

    async function confirmArtworkQualityBeforeCart() {
      if (!artworkCheckerEnabled || !artworkChecker?.openModal) return true;

      const uploadedImages = imageElements()
        .map((element) => ({
          element,
          file: originalUploadFiles.get(element.id),
        }))
        .filter((item) => item.file);

      if (!uploadedImages.length) {
        if (designerDebug) {
          transformDebugLog('artwork quality modal skipped', {
            reason: imageElements().length ? 'no-original-file-for-restored-artwork' : 'no-uploaded-images',
            imageLayerCount: imageElements().length,
          });
        }
        return true;
      }

      const results = [];
      for (const item of uploadedImages) {
        const cachedResult = artworkQualityResults.get(item.element.id);
        if (cachedResult) {
          results.push(cachedResult);
        } else {
          const result = await runArtworkQualityCheck(item.file, item.element.id);
          if (result) results.push(result);
        }
      }

      const summary = artworkChecker.summarizeResults?.(results, artworkCheckerOptions);
      if (designerDebug) {
        transformDebugLog('artwork quality modal opening', {
          filesChecked: results.map((result) => result.fileName),
          overallQualityStatus: summary?.strictPass ? 'pass' : 'review',
          continueAllowed: summary?.continueAllowed,
        });
      }

      const allowed = await artworkChecker.openModal(results, artworkCheckerOptions);
      if (designerDebug) {
        transformDebugLog('artwork quality modal closed', {
          continueAllowed: allowed,
        });
      }

      return allowed;
    }

    function activeElementNode() {
      if (!state.interactionElementId) return null;
      const safeId = window.CSS?.escape ? CSS.escape(state.interactionElementId) : state.interactionElementId.replace(/"/g, '\\"');
      return elementsRoot?.querySelector(`[data-element-id="${safeId}"]`) || null;
    }

    function applyElementNodeTransform(element) {
      const node = activeElementNode();
      if (!node || !element) return;

      node.style.left = `${element.x}%`;
      node.style.top = `${element.y}%`;
      node.style.width = element.type === 'image' ? `${element.scale}%` : '';
      node.style.transform = `translate(-50%, -50%) rotate(${element.rotation}deg)`;

      if (element.type !== 'image') {
        const textNode = node.querySelector('span:not([data-transform-handle])');
        if (textNode) textNode.style.fontSize = `${Math.max(14, element.scale)}px`;
      }
    }

    function activeTransformFeedbackNode(elementId = state.interactionElementId || state.activeElementId) {
      return elementNodeById(elementId)?.querySelector('[data-transform-feedback]') || null;
    }

    function formatRotationDegrees(value) {
      const rounded = Math.round(Number(value || 0));
      return `${rounded}°`;
    }

    function elementSizeInchesLabel(element) {
      const measurements = productionMeasurements(element);
      return measurements.designSize;
    }

    function showTransformFeedback(element, mode = state.transformMode) {
      const feedback = activeTransformFeedbackNode(element?.id);
      if (!feedback || !element) return;

      const label = mode === 'rotate'
        ? formatRotationDegrees(element.rotation)
        : elementSizeInchesLabel(element);

      feedback.textContent = label;
      feedback.hidden = false;
      feedback.dataset.mode = mode || '';

      if (designerDebug) {
        transformDebugLog('transform feedback updated', {
          mode,
          elementId: element.id,
          activeView: state.activeView,
          rotationDegrees: round(element.rotation),
          sizeInches: elementSizeInchesLabel(element),
        });
      }
    }

    function hideTransformFeedback() {
      elementsRoot?.querySelectorAll('[data-transform-feedback]').forEach((feedback) => {
        feedback.hidden = true;
        feedback.textContent = '';
        feedback.dataset.mode = '';
      });
    }

    function clientPoint(event) {
      if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
        return { x: event.clientX, y: event.clientY };
      }

      const touch = event.touches?.[0] || event.changedTouches?.[0];
      return {
        x: touch?.clientX || 0,
        y: touch?.clientY || 0,
      };
    }

    function viewDisplayName(view = state.activeView) {
      const names = {
        front: 'Front',
        back: 'Back',
        'left-sleeve': 'Left Sleeve',
        'right-sleeve': 'Right Sleeve',
      };

      return names[view] || 'Front';
    }

    function approvedColorLabelForValue(value) {
      const colorAliases = {
        NAVY: 'Navy Blue',
        NAVY_BLUE: 'Navy Blue',
        RED: 'Flame Red',
        FLAME: 'Flame Red',
        FLAME_RED: 'Flame Red',
        ROYAL: 'Royal Blue',
        ROYAL_BLUE: 'Royal Blue',
        MAROON: 'Maroon Red',
        MAROON_RED: 'Maroon Red',
        GRAY: 'Dark Grey',
        GREY: 'Dark Grey',
        'SPORT GRAY': 'Dark Grey',
        'SPORT GREY': 'Dark Grey',
        'DARK GRAY': 'Dark Grey',
        'LIME FLUORESCENT': 'Lime Flurecent',
        'ORANGE FLUORESCENT': 'Orange Flurecent',
      };

      return colorAliases[normalize(value)] || value;
    }

    function colorButtonForValue(value) {
      const resolved = resolveShirtColorSelection(value);
      if (!resolved?.label) return colorSwatchButtons[0] || null;

      const buttonByLabel = colorSwatchButtons.find((button) => normalize(button.dataset.value) === normalize(resolved.label));
      if (buttonByLabel) return buttonByLabel;

      const resolvedHex = normalizeSwatchHex(resolved.color);
      return colorSwatchButtons.find((button) => normalizeSwatchHex(button.dataset.color || '') === resolvedHex)
        || colorSwatchButtons[0]
        || null;
    }

    function calibratedColorForLabel(label) {
      const colorMap = {
        WHITE: '#f8f8f8',
        BLACK: '#434141',
        ROYAL: '#3D428B',
        'ROYAL BLUE': '#3D428B',
        'LIME FLURECENT': '#BFFF00',
        'LIME FLUORESCENT': '#BFFF00',
        'ORANGE FLURECENT': '#FF6A00',
        'ORANGE FLUORESCENT': '#FF6A00',
        'FLAME RED': '#C91F24',
        RED: '#C91F24',
        'FOREST GREEN': '#1F5F3A',
        'LIGHT BLUE': '#8EC9F5',
        'DARK GREY': '#54585A',
        'DARK GRAY': '#54585A',
        YELLOW: '#FFD100',
        MAROON: '#6E1F2F',
        'MAROON RED': '#6E1F2F',
        NAVY: '#1F355E',
        'NAVY BLUE': '#1F355E',
      };

      return colorMap[normalize(label)] || '';
    }

    function shirtColors() {
      return shirtColorPalette.length ? shirtColorPalette : defaultShirtColorPalette;
    }

    function enabledShirtColors() {
      const enabled = shirtColors().filter((color) => color.enabled !== false);
      return enabled.length ? enabled : [];
    }

    function activeShirtColors() {
      const enabled = shirtColors().filter((color) => color.enabled !== false);
      return enabled.length ? enabled : shirtColors();
    }

    function resolveShirtColorSelection(rawValue) {
      const requestedLabel = String(rawValue || '').trim();
      const aliasLabel = approvedColorLabelForValue(requestedLabel);

      const hasEnabledColors = enabledShirtColors().length > 0;
      const matchingPalette = hasEnabledColors ? enabledShirtColors() : activeShirtColors();
      const defaultEntry = matchingPalette[0];

      if (!requestedLabel) {
        return {
          ...defaultEntry,
          color: defaultEntry?.color || defaultEntry?.hex || '',
          key: normalize(defaultEntry?.label || 'Black'),
          fallbackUsed: false,
          source: defaultEntry ? (hasEnabledColors ? 'first-enabled-color' : 'first-available-color') : 'hardcoded-black',
        };
      }

      const exactMatch = matchingPalette.find((color) => normalize(color.label) === normalize(requestedLabel));
      if (exactMatch) {
        return {
          ...exactMatch,
          color: exactMatch.color || exactMatch.hex || '',
          key: normalize(exactMatch.label),
          fallbackUsed: false,
          source: 'palette',
          sourceLabel: exactMatch.label,
        };
      }

      const aliasMatch = matchingPalette.find((color) => normalize(color.label) === normalize(aliasLabel));
      if (aliasMatch) {
        return {
          ...aliasMatch,
          color: aliasMatch.color || aliasMatch.hex || '',
          key: normalize(aliasMatch.label),
          fallbackUsed: false,
          source: 'palette-alias',
          sourceLabel: aliasMatch.label,
        };
      }

      const requestedHex = normalizeSwatchHex(requestedLabel);
      if (requestedHex) {
        const matchByHex = matchingPalette.find((color) => normalizeSwatchHex(color.hex) === requestedHex);
        if (matchByHex) {
          return {
            ...matchByHex,
            color: matchByHex.color || matchByHex.hex || '',
            key: normalize(matchByHex.label),
            fallbackUsed: true,
            source: 'palette-hex',
            sourceLabel: matchByHex.label,
          };
        }
      }

      const mappedLegacyHex = normalizeSwatchHex(calibratedColorForLabel(requestedLabel)) || normalizeSwatchHex(calibratedColorForLabel(aliasLabel));
      if (mappedLegacyHex) {
        const legacyMatch = matchingPalette.find((color) => normalizeSwatchHex(color.hex) === mappedLegacyHex);
        if (legacyMatch) {
          return {
            ...legacyMatch,
            color: legacyMatch.color || legacyMatch.hex || '',
            key: normalize(legacyMatch.label),
            fallbackUsed: true,
            source: 'legacy-color-map',
            sourceLabel: legacyMatch.label,
          };
        }
      }

      return {
        ...(defaultEntry || { label: requestedLabel, color: normalizeSwatchHex(calibratedColorForLabel(requestedLabel)) || DEFAULT_BLACK_HEX }),
        color: normalizeSwatchHex(defaultEntry?.color || defaultEntry?.hex || normalizedColorForLabelOrEmpty(requestedLabel)),
        key: normalize(defaultEntry?.label || requestedLabel || 'Black'),
        fallbackUsed: true,
        source: defaultEntry ? (hasEnabledColors ? 'default-enabled-color' : 'default-color') : 'hardcoded-black',
        sourceLabel: defaultEntry?.label || requestedLabel || 'Black',
      };
    }

    function normalizedColorForLabelOrEmpty(requestedLabel) {
      const requestedHex = normalizeSwatchHex(requestedLabel);
      const legacyMapHex = normalizeSwatchHex(calibratedColorForLabel(requestedLabel));
      return requestedHex || legacyMapHex || '';
    }

    function setSelectedShirtColor(rawValue, options = {}) {
      const selected = resolveShirtColorSelection(rawValue || 'Black');
      const label = selected.label || rawValue || 'Black';
      const color = normalizeSwatchHex(selected.color) || DEFAULT_BLACK_HEX;
      const nextState = {
        key: normalize(label),
        label,
        color,
        fallbackUsed: selected.fallbackUsed || false,
        source: selected.source || 'fallback',
        sourceLabel: selected.sourceLabel || label,
      };
      selectedShirtColorState = nextState;

      if (colorSelect && !options.skipInputSync) {
        colorSelect.value = label;
      }

      if (designerDebug) {
        transformDebugLog('shirt color selected', {
          selectedShirtColorLabel: nextState.label,
          selectedShirtColorHex: nextState.color,
          selectedShirtColorKey: nextState.key,
          selectedShirtColorSource: nextState.source,
          selectedShirtColorSourceLabel: nextState.sourceLabel,
          selectedShirtColorFallbackUsed: nextState.fallbackUsed,
        });
      }

      return nextState;
    }

    function selectedColorData() {
      const requestedColor = colorSelect?.value || selectedShirtColorState?.label || 'Black';

      if (!selectedShirtColorState || normalize(selectedShirtColorState.label) !== normalize(requestedColor)) {
        return setSelectedShirtColor(requestedColor);
      }

      return selectedShirtColorState;
    }

    function usesWhiteShirtPricing() {
      const selected = selectedColorData();
      const normalizedLabel = normalize(selected.label || selected.key);
      const normalizedHex = normalizeSwatchHex(selected.color || selected.hex);
      return normalizedLabel === 'WHITE' || normalizedHex === '#FFFFFF';
    }

    function selectedCustomDesignPriceTable() {
      return usesWhiteShirtPricing() ? customDesignPriceTables.white : customDesignPriceTables.color;
    }

    function updateShirtPriceDisplays() {
      const priceTable = selectedCustomDesignPriceTable();
      const category = usesWhiteShirtPricing() ? 'White' : 'Color';

      if (pricingCategoryLabel) pricingCategoryLabel.textContent = `${category} shirt prices`;
      if (pricingGroupDisplays.xsXl) pricingGroupDisplays.xsXl.textContent = `${formatMoney(priceTable.XS)} each`;
      if (pricingGroupDisplays.twoXl) pricingGroupDisplays.twoXl.textContent = `${formatMoney(priceTable['2XL'])} each`;
      if (pricingGroupDisplays.threeXl) pricingGroupDisplays.threeXl.textContent = `${formatMoney(priceTable['3XL'])} each`;

      sizeRows.forEach((row) => {
        const display = row.querySelector('[data-size-display-price]');
        if (display) display.textContent = formatMoney(priceTable[row.dataset.size] || 0);
      });
    }

    function placeholderColorForShirt(hex) {
      const shirtColor = parseHexColor(hex || DEFAULT_BLACK_HEX);
      const luminance = colorLuminance(shirtColor);
      return {
        color: luminance >= 0.55 ? '#111111' : '#ffffff',
        shadow: luminance >= 0.55 ? '0 1px 2px rgba(255, 255, 255, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.35)',
      };
    }

    function refreshPlaceholderLayout(reason = 'render') {
      if (!safeArea || !emptyState) return;

      if (placeholderRefreshFrame) {
        window.cancelAnimationFrame(placeholderRefreshFrame);
        placeholderRefreshFrame = 0;
      }

      if (placeholderRefreshTimer) {
        window.clearTimeout(placeholderRefreshTimer);
        placeholderRefreshTimer = 0;
      }

      if (emptyState.hidden || emptyState.classList.contains('is-hidden')) return;

      fitPlaceholderToSafeArea();

      placeholderRefreshFrame = window.requestAnimationFrame(() => {
        placeholderRefreshFrame = 0;
        fitPlaceholderToSafeArea();
      });

      placeholderRefreshTimer = window.setTimeout(() => {
        placeholderRefreshTimer = 0;
        fitPlaceholderToSafeArea();
      }, 220);

      if (designerDebug) {
        const box = safeArea.getBoundingClientRect();
        transformDebugLog('placeholder refresh scheduled', {
          reason,
          activeView: state.activeView,
          activePlacement: placementSelect?.value || '',
          placeholderHidden: Boolean(emptyState.hidden || emptyState.classList.contains('is-hidden')),
          placeholderBoxRect: {
            width: round(box.width),
            height: round(box.height),
            x: round(box.x),
            y: round(box.y),
          },
        });
      }
    }

    function fitPlaceholderToSafeArea() {
      if (!safeArea || !emptyState) return;
      if (emptyState.hidden || emptyState.classList.contains('is-hidden')) return;

      const placeholder = emptyState;
      const placeholderText = 'Upload artwork to preview it here.';
      const selected = selectedColorData();
      const contrast = placeholderColorForShirt(selected.color);
      const areaRect = safeArea.getBoundingClientRect();
      const availableWidth = Math.max(48, Math.floor(Math.max(safeArea.clientWidth || areaRect.width, 48)));
      const availableHeight = Math.max(48, Math.floor(Math.max(safeArea.clientHeight || areaRect.height, 48)));
      const baseWidth = Math.max(0, availableWidth);
      const baseHeight = Math.max(0, availableHeight);

      if (!baseWidth || !baseHeight) {
        placeholder.style.color = contrast.color;
        placeholder.style.textShadow = contrast.shadow;
        return;
      }

      const outerPadding = clamp(Math.round(Math.min(baseWidth, baseHeight) * 0.08), 6, 18);
      const innerWidth = Math.max(40, baseWidth - outerPadding * 2);
      const innerHeight = Math.max(28, baseHeight - outerPadding * 2);
      const minFont = 11;
      const maxFont = Math.max(minFont, Math.min(30, Math.floor(Math.min(innerWidth / 10, innerHeight / 2.2))));

      placeholder.textContent = placeholderText;
      placeholder.style.position = 'absolute';
      placeholder.style.top = '50%';
      placeholder.style.left = '50%';
      placeholder.style.transform = 'translate(-50%, -50%)';
      placeholder.style.boxSizing = 'border-box';
      placeholder.style.display = 'flex';
      placeholder.style.alignItems = 'center';
      placeholder.style.justifyContent = 'center';
      placeholder.style.flexDirection = 'column';
      placeholder.style.width = `calc(100% - ${outerPadding * 2}px)`;
      placeholder.style.height = `calc(100% - ${outerPadding * 2}px)`;
      placeholder.style.maxWidth = `calc(100% - ${outerPadding * 2}px)`;
      placeholder.style.maxHeight = `calc(100% - ${outerPadding * 2}px)`;
      placeholder.style.overflow = 'hidden';
      placeholder.style.padding = `${outerPadding}px`;
      placeholder.style.margin = '0 auto';
      placeholder.style.wordBreak = 'break-word';
      placeholder.style.whiteSpace = 'normal';
      placeholder.style.textAlign = 'center';
      placeholder.style.lineHeight = '1.15';
      placeholder.style.hyphens = 'auto';
      placeholder.style.webkitHyphens = 'auto';
      placeholder.style.letterSpacing = '0';
      placeholder.style.color = contrast.color;
      placeholder.style.textShadow = contrast.shadow;
      placeholder.style.visibility = 'hidden';

      for (let fontSize = maxFont; fontSize >= minFont; fontSize -= 1) {
        placeholder.style.fontSize = `${fontSize}px`;
        if (placeholder.scrollWidth <= availableWidth + 1 && placeholder.scrollHeight <= availableHeight + 1) {
          break;
        }
      }

      placeholder.style.visibility = 'visible';
    }

    function debugShirtColorRenderState() {
      if (!designerDebug) return;

      const color = selectedColorData();
      const usesImageRenderPath = state.activeView === 'front' || state.activeView === 'back';
      const renderPath = usesImageRenderPath ? `mockup-${state.activeView}` : 'fallback-shirt-shape';
      const frontMockupColor = color.color;
      const backMockupColor = color.color;
      const enabledPaletteColors = shirtColors().filter((paletteColor) => paletteColor.enabled !== false);
      const debugState = `${color.key}|${color.label}|${color.color}|${renderPath}|${color.source}|${color.fallbackUsed ? 'fallback' : 'direct'}|${enabledPaletteColors.length}`;

      if (lastShirtColorDebugState === debugState) return;
      lastShirtColorDebugState = debugState;

      transformDebugLog('shirt color render state', {
        selectedShirtColorLabel: color.label,
        selectedShirtColorKey: color.key,
        selectedShirtColorHex: color.color,
        mockupRenderColorUsed: color.color,
        shirtRenderPath: renderPath,
        frontBackRenderPathUsesSameColor: frontMockupColor === backMockupColor,
        shirtColorSource: color.source,
        selectedShirtColorFallbackUsed: color.fallbackUsed,
        parsedShirtColors: shirtColors().map((paletteColor) => ({
          label: paletteColor.label,
          hex: paletteColor.hex,
          enabled: paletteColor.enabled !== false,
        })),
        enabledShirtColors: enabledPaletteColors.map((paletteColor) => ({
          label: paletteColor.label,
          hex: paletteColor.hex,
        })),
      });
    }

    function syncColorSwatches() {
      const color = selectedColorData();
      if (colorSelect && normalize(colorSelect.value) !== normalize(color.label)) {
        setSelectedShirtColor(color.label, { skipInputSync: true });
      } else if (!selectedShirtColorState) {
        setSelectedShirtColor(color.label, { skipInputSync: true });
      }

      colorSwatchButtons.forEach((button) => {
        const isActive = normalize(button.dataset.value) === normalize(color.label);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }

    function syncPlacementOptions(
      preferredPlacement = state.placementsByView[state.activeView] || placementSelect?.value
    ) {
      if (!placementSelect) return;

      ensureActiveStateAllowed('sync-placement-options');
      const mapping = placementMappingDetails(preferredPlacement);
      const normalizedPreferredPlacement = mapping.canonicalValue;
      const preferredPlacementDefinition = placementDefinitions[normalizedPreferredPlacement];
      const preferredPlacementDisabled = Boolean(preferredPlacementDefinition && !viewIsEnabled(preferredPlacementDefinition.view));
      if (preferredPlacementDisabled) {
        const previousView = state.activeView;
        state.activeView = 'front';

        if (designerDebug) {
          transformDebugLog('sleeve placement disabled fallback', {
            preferredPlacement,
            normalizedPreferredPlacement,
            disabledPlacementView: preferredPlacementDefinition.view,
            previousView,
            nextView: state.activeView,
            fallbackPlacement: placementsByView.front[0],
          });
        }
      }

      const options = availablePlacementOptions(state.activeView);
      const nextPlacement = options.includes(normalizedPreferredPlacement) ? normalizedPreferredPlacement : options[0];

      placementSelect.replaceChildren();
      options.forEach((placement) => {
        const option = document.createElement('option');
        option.value = placement;
        option.textContent = placementDisplayName(placement);
        placementSelect.append(option);
      });
      placementSelect.value = nextPlacement;
      state.placementsByView[state.activeView] = nextPlacement;

      transformDebugLog('placement options synced', {
        selectedView: state.activeView,
        preferredPlacement,
        nextPlacement,
        placementLabel: placementDisplayName(nextPlacement),
        placementLabelMapping: mapping,
        oldCenterChestMappedToAcrossChest: mapping.legacyCenterChestMapped,
        oldBackCollarMappedToAcrossShoulder: mapping.legacyBackCollarMapped,
        oldUpperBackMappedToMediumBack: mapping.legacyUpperBackMapped,
        sleevePlacementDisabledFallback: preferredPlacementDisabled,
        enable_sleeve_views: sleeveViewsEnabled,
        availableViews: availableViewKeys,
        availablePlacements: options,
      });
    }

    function containRect(containerWidth, containerHeight, sourceWidth, sourceHeight) {
      const width = Math.max(Number(sourceWidth) || 1, 1);
      const height = Math.max(Number(sourceHeight) || 1, 1);
      const scale = Math.min(containerWidth / width, containerHeight / height);
      const renderedWidth = width * scale;
      const renderedHeight = height * scale;

      return {
        x: (containerWidth - renderedWidth) / 2,
        y: (containerHeight - renderedHeight) / 2,
        width: renderedWidth,
        height: renderedHeight,
      };
    }

    function activeMockupImage() {
      const usesRealMockupImage = state.activeView === 'front' || state.activeView === 'back';
      if (!usesRealMockupImage) return null;

      return mockupImages.find((image) => image.dataset.mockupImage === state.activeView) || null;
    }

    function renderedPlacementBounds() {
      const mockupRect = mockup?.getBoundingClientRect();
      if (!mockupRect) return { x: 0, y: 0, width: 1, height: 1, source: 'mockup' };

      const image = activeMockupImage();
      if (image) {
        const fallbackDimensions = mockupSourceDimensions[state.activeView] || mockupSourceDimensions.front;
        const naturalWidth = image.naturalWidth || fallbackDimensions.width;
        const naturalHeight = image.naturalHeight || fallbackDimensions.height;
        const rect = containRect(mockupRect.width, mockupRect.height, naturalWidth, naturalHeight);

        return {
          ...rect,
          source: 'mockup-image',
          naturalWidth,
          naturalHeight,
        };
      }

      if (shirtFallback && !shirtFallback.hidden) {
        const fallbackRect = shirtFallback.getBoundingClientRect();
        if (fallbackRect.width > 0 && fallbackRect.height > 0) {
          return {
            x: fallbackRect.left - mockupRect.left,
            y: fallbackRect.top - mockupRect.top,
            width: fallbackRect.width,
            height: fallbackRect.height,
            source: 'shirt-fallback',
          };
        }
      }

      return {
        x: 0,
        y: 0,
        width: mockupRect.width,
        height: mockupRect.height,
        source: 'mockup',
      };
    }

    function placementRectFromGeometry(definition, bounds) {
      const geometry = definition.geometry || {};
      const width = bounds.width * (Number(geometry.widthPercent || 1) / 100);
      const height = bounds.height * (Number(geometry.heightPercent || 1) / 100);
      const centerX = bounds.x + bounds.width * (Number(geometry.centerXPercent || 50) / 100);
      const centerY = bounds.y + bounds.height * (Number(geometry.centerYPercent || 50) / 100);
      const rotation = Number(geometry.rotation || 0);

      return {
        top: centerY - height / 2,
        left: centerX,
        width,
        height,
        centerX,
        centerY,
        transform: `translateX(-50%)${rotation ? ` rotate(${rotation}deg)` : ''}`,
      };
    }

    function updateSafeAreaPlacement() {
      if (!safeArea || !placementSelect) return;

      const mapping = placementMappingDetails(placementSelect.value);
      const placementValue = placementDefinitions[mapping.canonicalValue] ? mapping.canonicalValue : 'Left Chest';
      const definition = placementDefinitions[placementValue] || placementDefinitions['Left Chest'];
      const renderedBounds = renderedPlacementBounds();
      const printRect = placementRectFromGeometry(definition, renderedBounds);
      safeArea.style.setProperty('--safe-area-top', `${printRect.top}px`);
      safeArea.style.setProperty('--safe-area-left', `${printRect.left}px`);
      safeArea.style.setProperty('--safe-area-width', `${printRect.width}px`);
      safeArea.style.setProperty('--safe-area-height', `${printRect.height}px`);
      safeArea.style.setProperty('--safe-area-transform', printRect.transform);
      safeArea.dataset.placement = placementValue;
      safeArea.dataset.printDimensions = definition.dimensions;
      safeArea.dataset.printWidth = `${definition.widthInches}" W`;
      safeArea.dataset.printHeight = `${definition.heightInches}" H`;
      safeArea.setAttribute('aria-label', `${placementDisplayName(placementValue)} print area ${definition.dimensions}`);

      if (designerDebug) {
        const printBox = safeArea.getBoundingClientRect();
        const mockupBox = mockup?.getBoundingClientRect();
        transformDebugLog('print placement applied', {
          selectedView: state.activeView,
          selectedPlacementKey: normalizePlacementName(placementValue),
          selectedPlacementLabel: placementDisplayName(placementValue),
          printAreaConfigUsed: definition,
          renderedMockupImageBounds: {
            source: renderedBounds.source,
            x: round(renderedBounds.x),
            y: round(renderedBounds.y),
            width: round(renderedBounds.width),
            height: round(renderedBounds.height),
            naturalWidth: renderedBounds.naturalWidth || null,
            naturalHeight: renderedBounds.naturalHeight || null,
          },
          finalOffsetsApplied: {
            top: `${round(printRect.top)}px`,
            left: `${round(printRect.left)}px`,
            centerXPercent: definition.geometry.centerXPercent,
            centerYPercent: definition.geometry.centerYPercent,
          },
          printBox: {
            x: round(printBox.x),
            y: round(printBox.y),
            width: round(printBox.width),
            height: round(printBox.height),
            centerX: round(printRect.centerX),
            centerY: round(printRect.centerY),
            relativeX: mockupBox ? round(printBox.x - mockupBox.x) : null,
            relativeY: mockupBox ? round(printBox.y - mockupBox.y) : null,
          },
          placementLabelMapping: mapping,
          oldCenterChestMappedToAcrossChest: mapping.legacyCenterChestMapped,
          oldBackCollarMappedToAcrossShoulder: mapping.legacyBackCollarMapped,
          oldUpperBackMappedToMediumBack: mapping.legacyUpperBackMapped,
        });
      }
    }

    function setStatus(message = '', type = '') {
      if (!status) return;

      status.textContent = message;
      status.classList.remove('inknfiber-custom-designer__status--error', 'inknfiber-custom-designer__status--success', 'inknfiber-custom-designer__status--warning');

      if (type) {
        status.classList.add(`inknfiber-custom-designer__status--${type}`);
      }
    }

    function transformSnapshot() {
      return state.elements.map((element) => ({
        id: element.id,
        x: element.x,
        y: element.y,
        scale: element.scale,
        rotation: element.rotation,
      }));
    }

    function updateHistoryButtons() {
      if (undoButton) undoButton.disabled = state.history.undo.length === 0;
      if (redoButton) redoButton.disabled = state.history.redo.length === 0;
    }

    function pushTransformHistory() {
      state.history.undo.push(transformSnapshot());
      if (state.history.undo.length > 40) state.history.undo.shift();
      state.history.redo = [];
      updateHistoryButtons();
    }

    function applyTransformSnapshot(snapshot) {
      snapshot.forEach((savedElement) => {
        const element = state.elements.find((item) => item.id === savedElement.id);
        if (!element) return;

        element.x = savedElement.x;
        element.y = savedElement.y;
        element.scale = savedElement.scale;
        element.rotation = savedElement.rotation;
      });

      renderDesign();
    }

    function undoTransform() {
      if (!state.history.undo.length) return;

      state.history.redo.push(transformSnapshot());
      applyTransformSnapshot(state.history.undo.pop());
      updateHistoryButtons();
    }

    function redoTransform() {
      if (!state.history.redo.length) return;

      state.history.undo.push(transformSnapshot());
      applyTransformSnapshot(state.history.redo.pop());
      updateHistoryButtons();
    }

    function unmatchedSelectedSizes() {
      if (!productHasSizeVariants) return [];
      return selectedSizes().filter((item) => !sizeVariantMap[item.size]).map((item) => item.size);
    }

    function hasMappedSizeVariants() {
      return productHasSizeVariants && selectedSizes().every((item) => Boolean(sizeVariantMap[item.size]));
    }

    function shouldUseFallbackMode() {
      return productHasSizeVariants && Boolean(firstAvailableVariantId) && selectedSizes().length > 0 && !hasMappedSizeVariants();
    }

    function linePriceForSize(size) {
      const configuredPrice = moneyToCents(selectedCustomDesignPriceTable()[size]);
      const mappedPrice = moneyToCents(sizeVariantMap[size]?.price);
      const fallbackPrice = moneyToCents(firstAvailableVariant?.price);
      return configuredPrice || mappedPrice || fallbackPrice || 0;
    }

    function shopifyVariantPriceForSize(size) {
      const mappedPrice = moneyToCents(sizeVariantMap[size]?.price);
      const fallbackPrice = moneyToCents(firstAvailableVariant?.price);
      return mappedPrice || fallbackPrice || 0;
    }

    function priceDebugRows() {
      return selectedSizes().map((item) => {
        const variant = sizeVariantMap[item.size] || null;
        const priceCents = linePriceForSize(item.size);
        const checkoutVariantPriceCents = shopifyVariantPriceForSize(item.size);

        return {
          size: item.size,
          quantity: item.quantity,
          variantId: variant?.id || firstAvailableVariantId || null,
          variantTitle: variant?.title || firstAvailableVariant?.title || null,
          priceCents,
          configuredDisplayPriceCents: moneyToCents(selectedCustomDesignPriceTable()[item.size]),
          checkoutVariantPriceCents,
          lineTotalCents: priceCents * item.quantity,
        };
      });
    }

    function productSubtotalCents() {
      return selectedSizes().reduce((sum, item) => {
        return sum + linePriceForSize(item.size) * item.quantity;
      }, 0);
    }

    function shopifyVariantSubtotalCents() {
      return selectedSizes().reduce((sum, item) => {
        return sum + shopifyVariantPriceForSize(item.size) * item.quantity;
      }, 0);
    }

    function checkoutProductSubtotalCents() {
      return Math.max(productSubtotalCents(), shopifyVariantSubtotalCents());
    }

    function productPriceCorrectionCents() {
      return Math.max(0, productSubtotalCents() - shopifyVariantSubtotalCents());
    }

    function bulkDiscountEstimate() {
      const quantity = totalQuantity();
      const productSubtotal = productSubtotalCents();
      let percent = 0;
      let tier = 'none';
      let label = 'Not eligible yet';
      let requiresQuote = false;

      if (quantity >= 101) {
        tier = 'b2b-required';
        label = 'B2B / Request Quote required';
        requiresQuote = true;
      } else if (quantity >= 50) {
        percent = 12;
        tier = '50-100';
        label = 'Eligible bulk discount: 12% off';
      } else if (quantity >= 10) {
        percent = 7;
        tier = '10-49';
        label = 'Eligible bulk discount: 7% off';
      }

      const estimatedSavings = requiresQuote ? 0 : Math.round(productSubtotal * (percent / 100));

      return {
        quantity,
        percent,
        tier,
        label,
        eligible: percent > 0,
        requiresQuote,
        requiresManualApproval: percent > 0,
        productSubtotalCents: productSubtotal,
        estimatedSavingsCents: estimatedSavings,
        productTotalAfterDiscountCents: Math.max(0, productSubtotal - estimatedSavings),
      };
    }

    function designFeeSubtotalCents() {
      return 0;
    }

    function aiFeeDetails() {
      if (!aiPhotoshootEnabled) {
        return {
          productId: '',
          productTitle: '',
          variant: null,
          variantId: '',
          variantTitle: '',
          available: false,
          priceCents: 0,
        };
      }

      const priceCents = moneyToCents(aiFeeVariant?.price);
      return {
        productId: aiFeeProductId,
        productTitle: aiFeeTitle,
        variant: aiFeeVariant,
        variantId: aiFeeVariant?.id || '',
        variantTitle: aiFeeVariant?.title || '',
        available: Boolean(aiFeeVariant?.available),
        priceCents,
      };
    }

    function aiDesignFeeSubtotalCents() {
      if (!aiPhotoshootEnabled) return 0;
      const details = aiFeeDetails();
      if (!details.variant || !details.available) return 0;
      return details.priceCents * aiDesignCount();
    }

    function aiDesignFeeSummaryText() {
      if (!aiPhotoshootEnabled) return '';

      if (!aiFeeProductId || !aiFeeVariant) {
        return 'AI design fee product is not configured.';
      }

      const details = aiFeeDetails();
      if (!details.available) {
        return 'AI design fee product is not available.';
      }

      const count = aiDesignCount();
      return `${formatMoney(details.priceCents)} \u00d7 ${count} = ${formatMoney(aiDesignFeeSubtotalCents())}`;
    }

    function elementsForPrintView(view) {
      const normalizedView = normalizeViewKey(view);
      return state.elements.filter((element) => normalizeViewKey(element.view || 'front') === normalizedView);
    }

    function placementForPrintView(view) {
      const normalizedView = fallbackView(view, 'print-charge-placement');
      const options = availablePlacementOptions(normalizedView);
      const savedPlacement = canonicalPlacementValue(state.placementsByView[normalizedView]);
      return options.includes(savedPlacement) ? savedPlacement : options[0];
    }

    function definitionForPrintView(view) {
      const placement = placementForPrintView(view);
      return placementDefinitions[placement] || placementDefinitions['Full Front'];
    }

    function printChargeSourceKey(source) {
      const value = String(source || '');
      if (!value.startsWith('data:')) return value;
      return `data:${value.length}:${value.slice(-32)}`;
    }

    function printChargeStateKey() {
      return JSON.stringify({
        config: customDesignPrintConfig,
        fallbackRate: customDesignPrintRateCentsPerSqIn,
        placements: state.placementsByView,
        elements: state.elements.map((element) => ({
          id: element.id,
          type: element.type,
          view: normalizeViewKey(element.view || 'front'),
          x: Number(element.x || 50),
          y: Number(element.y || 50),
          scale: Number(element.scale || 0),
          rotation: Number(element.rotation || 0),
          content: element.content || '',
          fontFamily: element.fontFamily || '',
          color: element.color || '',
          src: printChargeSourceKey(element.src),
          naturalWidth: Number(element.naturalWidth || 0),
          naturalHeight: Number(element.naturalHeight || 0),
        })),
      });
    }

    function rotatedBounds(widthRatio, heightRatio, rotationDegrees) {
      const radians = (Number(rotationDegrees || 0) * Math.PI) / 180;
      const cosine = Math.abs(Math.cos(radians));
      const sine = Math.abs(Math.sin(radians));
      return {
        widthRatio: widthRatio * cosine + heightRatio * sine,
        heightRatio: widthRatio * sine + heightRatio * cosine,
      };
    }

    function elementFallbackBounds(element, definition) {
      const widthScale = clamp(Number(element.scale || 0) / 100, 0.01, 2);
      let widthRatio = widthScale;
      let heightRatio = widthScale;

      if (element.type === 'image') {
        const naturalWidth = Math.max(1, Number(element.naturalWidth || 0) || 1);
        const naturalHeight = Math.max(1, Number(element.naturalHeight || 0) || naturalWidth);
        heightRatio = widthRatio
          * (naturalHeight / naturalWidth)
          * (Number(definition.widthInches || 1) / Math.max(1, Number(definition.heightInches || 1)));
      } else {
        const content = String(element.content || element.name || '');
        const fontRatioToWidth = Math.max(14, Number(element.scale || 30)) / 200;
        widthRatio = Math.max(fontRatioToWidth, fontRatioToWidth * Math.max(1, content.length) * 0.58);
        heightRatio = fontRatioToWidth
          * (Number(definition.widthInches || 1) / Math.max(1, Number(definition.heightInches || 1)));
      }

      const rotated = rotatedBounds(widthRatio, heightRatio, element.rotation);
      const centerX = Number(element.x ?? 50) / 100;
      const centerY = Number(element.y ?? 50) / 100;
      const left = clamp(centerX - rotated.widthRatio / 2, 0, 1);
      const right = clamp(centerX + rotated.widthRatio / 2, 0, 1);
      const top = clamp(centerY - rotated.heightRatio / 2, 0, 1);
      const bottom = clamp(centerY + rotated.heightRatio / 2, 0, 1);

      return {
        left,
        right,
        top,
        bottom,
      };
    }

    function fallbackCompositionMetrics(view) {
      const definition = definitionForPrintView(view);
      const elements = elementsForPrintView(view);
      if (!elements.length) return null;

      const bounds = elements.reduce((union, element) => {
        const next = elementFallbackBounds(element, definition);
        return {
          left: Math.min(union.left, next.left),
          right: Math.max(union.right, next.right),
          top: Math.min(union.top, next.top),
          bottom: Math.max(union.bottom, next.bottom),
        };
      }, { left: 1, right: 0, top: 1, bottom: 0 });
      const widthInches = Math.max(0.01, Number(definition.widthInches || 0) * Math.max(0, bounds.right - bounds.left));
      const heightInches = Math.max(0.01, Number(definition.heightInches || 0) * Math.max(0, bounds.bottom - bounds.top));

      return {
        widthInches,
        heightInches,
        areaSqIn: widthInches * heightInches,
        coveragePercent: 100,
        colorCount: 1,
        complexity: 'Medium',
        analysisFallback: true,
      };
    }

    function roundPrintChargeCents(value) {
      const increment = customDesignPrintConfig.roundingIncrementCents;
      return Math.round(Math.max(0, Number(value || 0)) / increment) * increment;
    }

    function fallbackSidePrintCharge(view) {
      const metrics = fallbackCompositionMetrics(view);
      if (!metrics) return null;

      const fallbackRate = Math.max(0, customDesignPrintRateCentsPerSqIn);
      const fallbackMinimum = Math.max(
        customDesignPrintConfig.minimumChargeCents,
        customDesignMinimumPrintChargeCents
      );
      const printChargeCents = clamp(
        roundPrintChargeCents(metrics.areaSqIn * fallbackRate),
        fallbackMinimum,
        customDesignPrintConfig.maximumChargeCents
      );

      return {
        view: normalizeViewKey(view),
        label: viewDisplayName(view),
        placement: placementForPrintView(view),
        ...metrics,
        filmCostCents: 0,
        whiteInkCostCents: 0,
        colorInkCostCents: 0,
        rawMaterialCostCents: 0,
        materialWithWasteCents: 0,
        estimatedPrintTimeMinutes: 0,
        machineTimeCostCents: 0,
        handlingSetupCents: customDesignPrintConfig.handlingSetupCents,
        productionCostCents: 0,
        retailUnroundedCents: metrics.areaSqIn * fallbackRate,
        printChargeCents,
      };
    }

    function fallbackPrintChargeDetails(key = printChargeStateKey()) {
      const sides = availableViewKeys
        .map((view) => fallbackSidePrintCharge(view))
        .filter(Boolean);

      return {
        key,
        sides,
        totalAreaSqIn: sides.reduce((sum, side) => sum + side.areaSqIn, 0),
        totalEachCents: sides.reduce((sum, side) => sum + side.printChargeCents, 0),
        analysisFallback: sides.length > 0,
      };
    }

    async function drawPrintElementToCanvas(context, canvas, element) {
      const centerX = canvas.width * (Number(element.x ?? 50) / 100);
      const centerY = canvas.height * (Number(element.y ?? 50) / 100);
      const rotation = (Number(element.rotation || 0) * Math.PI) / 180;

      context.save();
      context.translate(centerX, centerY);
      context.rotate(rotation);

      try {
        if (element.type === 'image' && element.src) {
          const image = await loadCanvasImage(element.src);
          const width = canvas.width * (Number(element.scale || 42) / 100);
          const sourceWidth = image.naturalWidth || image.width || 1;
          const sourceHeight = image.naturalHeight || image.height || sourceWidth;
          const height = width * (sourceHeight / sourceWidth);
          const preparedImage = artworkEstimator?.prepareImageCanvas?.(image, width, height);
          context.drawImage(preparedImage || image, -width / 2, -height / 2, width, height);
          return true;
        }

        if (element.type === 'text' || element.type === 'symbol') {
          const fontSize = Math.max(1, (Math.max(14, Number(element.scale || 30)) / 200) * canvas.width);
          context.font = `900 ${fontSize}px ${element.fontFamily || 'Arial, Helvetica, sans-serif'}`;
          context.fillStyle = element.color || '#000000';
          context.textAlign = 'center';
          context.textBaseline = 'middle';
          context.fillText(element.content || element.name || '', 0, 0);
          return true;
        }
      } catch (error) {
        transformDebugLog('print charge composition element failed', {
          elementId: element.id,
          view: element.view,
          error: error?.message || error,
        });
      } finally {
        context.restore();
      }

      return false;
    }

    async function analyzePrintSide(view) {
      const elements = elementsForPrintView(view);
      if (!elements.length) return null;
      if (!artworkEstimator?.analyzeCanvas || !artworkEstimator?.calculate) {
        return fallbackSidePrintCharge(view);
      }

      const definition = definitionForPrintView(view);
      const maxCanvasSide = 560;
      const pixelsPerInch = Math.min(
        40,
        maxCanvasSide / Math.max(Number(definition.widthInches || 1), Number(definition.heightInches || 1))
      );
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(Number(definition.widthInches || 1) * pixelsPerInch));
      canvas.height = Math.max(1, Math.round(Number(definition.heightInches || 1) * pixelsPerInch));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return fallbackSidePrintCharge(view);

      let renderedElementCount = 0;
      for (const element of elements) {
        if (await drawPrintElementToCanvas(context, canvas, element)) renderedElementCount += 1;
      }
      if (!renderedElementCount) return fallbackSidePrintCharge(view);

      const analysis = artworkEstimator.analyzeCanvas(canvas, {
        maxSide: 280,
        cropToVisible: true,
      });
      if (analysis?.isFallback || analysis?.isEmpty || !analysis?.bounds) {
        return fallbackSidePrintCharge(view);
      }

      const widthInches = Math.max(0.01, Number(definition.widthInches || 0) * analysis.bounds.widthRatio);
      const heightInches = Math.max(0.01, Number(definition.heightInches || 0) * analysis.bounds.heightRatio);
      const areaSqIn = widthInches * heightInches;
      const materialEstimate = artworkEstimator.calculate({
        widthInches,
        heightInches,
        analysis,
        config: {
          ...(artworkEstimatorConfig || {}),
          filmWastePercent: 0,
          wasteMultiplierPercent: 100,
          laborSetupCents: 0,
          minimumChargeCents: 0,
          minPrintCharge: 0,
          profitMultiplierPercent: 100,
          lowComplexityMultiplierPercent: 100,
          mediumComplexityMultiplierPercent: 100,
          highComplexityMultiplierPercent: 100,
          enableAreaRateFallback: false,
        },
      });
      const coverageDecimal = clamp(Number(analysis.coveragePercent || 0) / 100, 0, 1);
      const estimatedPrintTimeMinutes = heightInches
        * (0.42 + (0.35 * coverageDecimal) + (0.15 * coverageDecimal * coverageDecimal));
      const rawMaterialCostCents = Math.max(0, Math.round(Number(materialEstimate.rawMaterialCost || 0) * 100));
      const materialWithWasteCents = rawMaterialCostCents
        * (customDesignPrintConfig.wasteMultiplierPercent / 100);
      const machineTimeCostCents = estimatedPrintTimeMinutes
        * customDesignPrintConfig.machineRateCentsPerMinute;
      const productionCostCents = materialWithWasteCents
        + machineTimeCostCents
        + customDesignPrintConfig.handlingSetupCents;
      const retailUnroundedCents = productionCostCents
        * (customDesignPrintConfig.profitMultiplierPercent / 100);
      const printChargeCents = clamp(
        roundPrintChargeCents(retailUnroundedCents),
        customDesignPrintConfig.minimumChargeCents,
        customDesignPrintConfig.maximumChargeCents
      );

      return {
        view: normalizeViewKey(view),
        label: viewDisplayName(view),
        placement: placementForPrintView(view),
        widthInches,
        heightInches,
        areaSqIn,
        coveragePercent: Number(analysis.coveragePercent || 0),
        colorCount: Number(analysis.colorCount || 1),
        complexity: analysis.complexity || 'Medium',
        filmCostCents: Math.round(Number(materialEstimate.filmCost || 0) * 100),
        whiteInkCostCents: Math.round(Number(materialEstimate.whiteInkCost || 0) * 100),
        colorInkCostCents: Math.round(Number(materialEstimate.colorInkCost || 0) * 100),
        rawMaterialCostCents,
        materialWithWasteCents,
        estimatedPrintTimeMinutes,
        machineTimeCostCents,
        handlingSetupCents: customDesignPrintConfig.handlingSetupCents,
        productionCostCents,
        retailUnroundedCents,
        printChargeCents,
        analysisFallback: false,
      };
    }

    function currentPrintChargeDetails() {
      const key = printChargeStateKey();
      if (printChargeAnalysisResult?.key === key) return printChargeAnalysisResult;
      return fallbackPrintChargeDetails(key);
    }

    async function refreshPrintChargeAnalysisNow(options = {}) {
      window.clearTimeout(printChargeAnalysisTimer);
      const key = printChargeStateKey();

      if (!options.force && printChargeAnalysisResult?.key === key) {
        return printChargeAnalysisResult;
      }
      if (printChargeAnalysisPromise && printChargeAnalysisKey === key) {
        return printChargeAnalysisPromise;
      }

      printChargeAnalysisKey = key;
      const promise = (async () => {
        let result;
        try {
          const sides = (await Promise.all(availableViewKeys.map((view) => analyzePrintSide(view)))).filter(Boolean);
          result = {
            key,
            sides,
            totalAreaSqIn: sides.reduce((sum, side) => sum + side.areaSqIn, 0),
            totalEachCents: sides.reduce((sum, side) => sum + side.printChargeCents, 0),
            analysisFallback: sides.some((side) => side.analysisFallback),
          };
        } catch (error) {
          transformDebugLog('print charge analysis failed; using artwork bounds fallback', error);
          result = fallbackPrintChargeDetails(key);
        }

        if (printChargeStateKey() === key) {
          printChargeAnalysisResult = result;
          renderPrintChargeInternalEstimate(result);
          updateSummaries();
        }
        return result;
      })();

      printChargeAnalysisPromise = promise;
      try {
        return await promise;
      } finally {
        if (printChargeAnalysisPromise === promise) {
          printChargeAnalysisPromise = null;
        }
      }
    }

    function schedulePrintChargeAnalysis() {
      window.clearTimeout(printChargeAnalysisTimer);
      if (!state.elements.length) {
        printChargeAnalysisResult = null;
        renderPrintChargeInternalEstimate(null);
        return;
      }

      printChargeAnalysisTimer = window.setTimeout(() => {
        refreshPrintChargeAnalysisNow();
      }, 300);
    }

    function selectedPrintAreaSqIn() {
      return currentPrintChargeDetails().totalAreaSqIn;
    }

    function printChargeEachCents() {
      return currentPrintChargeDetails().totalEachCents;
    }

    function printChargeTotalCents() {
      return printChargeEachCents() * totalQuantity();
    }

    function designFeeSummaryText() {
      const details = currentPrintChargeDetails();
      if (!details.sides.length) return formatMoney(0);
      return `${formatMoney(details.totalEachCents)} each`;
    }

    function printChargeSideSummary(details = currentPrintChargeDetails()) {
      return details.sides
        .map((side) => `${side.label}: ${formatMoney(side.printChargeCents)}`)
        .join(' + ');
    }

    function finalTotalCents() {
      if (totalQuantity() <= 0) return 0;
      return checkoutProductSubtotalCents() + finalDesignChargeCents() + aiDesignFeeSubtotalCents();
    }

    function estimatePriceCents(estimate) {
      return Math.max(0, Math.round(Number(estimate?.suggestedPrice || 0) * 100));
    }

    function artworkDesignPriceCents(estimate = latestArtworkEstimate) {
      return printChargeEachCents();
    }

    function artworkDesignTotalCents(estimate = latestArtworkEstimate) {
      return printChargeTotalCents();
    }

    function finalDesignChargeCents(estimate = latestArtworkEstimate) {
      return artworkDesignTotalCents(estimate);
    }

    function estimatorCheckoutDetails(estimate = latestArtworkEstimate) {
      const productSubtotalCents = checkoutProductSubtotalCents();
      const existingUploadFeeCents = designFeeSubtotalCents();
      const printDetails = currentPrintChargeDetails();
      const printAreaSqIn = printDetails.totalAreaSqIn;
      const printChargeEach = printDetails.totalEachCents;
      const printChargeTotal = printChargeTotalCents();
      const estimatorReferenceCents = estimatePriceCents(estimate);
      const finalDesignCharge = printChargeTotal;
      const finalPriceCents = productSubtotalCents + finalDesignCharge;
      const adjustmentCents = finalDesignCharge;

      return {
        basePriceCents: productSubtotalCents,
        productSubtotalCents,
        suggestedPriceCents: estimatorReferenceCents,
        estimatorReferenceCents,
        printAreaSqIn,
        printRateCentsPerSqIn: customDesignPrintRateCentsPerSqIn,
        printMinimumChargeCents: customDesignPrintConfig.minimumChargeCents,
        printMaximumChargeCents: customDesignPrintConfig.maximumChargeCents,
        printChargeEachCents: printChargeEach,
        printChargeTotalCents: printChargeTotal,
        printChargeSides: printDetails.sides,
        printChargeAnalysisFallback: printDetails.analysisFallback,
        printChargeSideSummary: printChargeSideSummary(printDetails),
        artworkPriceCents: printChargeEach,
        artworkPricePerShirtCents: printChargeEach,
        artworkTotalCents: printChargeTotal,
        totalQuantity: totalQuantity(),
        existingUploadFeeCents,
        finalDesignChargeCents: finalDesignCharge,
        finalPriceCents,
        adjustmentCents,
      };
    }

    function estimatorAdjustmentCents(estimate = latestArtworkEstimate) {
      if (bulkDiscountEstimate().requiresQuote) return 0;
      return estimatorCheckoutDetails(estimate).adjustmentCents;
    }

    function validateEstimatorAdjustmentProduct(details = estimatorCheckoutDetails()) {
      const requiredAdjustmentCents = productPriceCorrectionCents()
        + details.adjustmentCents;
      if (!requiredAdjustmentCents) return '';
      if (!validCartVariantId(priceAdjustmentVariantId) || !priceAdjustmentAvailable) {
        return 'Price adjustment product is not configured. Please contact store support.';
      }
      if (priceAdjustmentCents !== 1) {
        return 'Price adjustment product must be priced at $0.01. Please contact store support.';
      }
      return '';
    }

    function validate() {
      if (!defaultVariantId && !firstAvailableVariantId) {
        return 'Select a custom T-shirt product in this section before cart checkout can be enabled.';
      }

      if (!firstAvailableVariantId) {
        return 'The selected custom T-shirt product does not have an available variant.';
      }

      if (!feeProductId || !feeVariants.length) {
        return 'Design fee product is not configured.';
      }

      if (!state.elements.length) {
        return 'Upload a design, add text, or add a symbol before adding this custom order to cart.';
      }

      if (!imageElements().length) {
        return 'Upload an image design before adding this custom order to cart so production artwork can be attached to your order.';
      }

      if (imageElements().some((element) => !element.src && !element.originalDesignKey && !element.mockupPreviewKey)) {
        return 'Some uploaded artwork must be re-uploaded before checkout.';
      }

      if (!colorSelect?.value) {
        return 'Select a shirt color before adding this custom order to cart.';
      }

      if (totalQuantity() <= 0) {
        return 'Select at least one size quantity before adding this custom order to cart.';
      }

      if (bulkDiscountEstimate().requiresQuote) {
        return 'Orders above 100 shirts require B2B approval. Please request a quote.';
      }

      const estimatorAdjustmentError = validateEstimatorAdjustmentProduct();
      if (estimatorAdjustmentError) {
        return estimatorAdjustmentError;
      }

      if (!placementSelect?.value) {
        return 'Choose a print placement before adding this custom order to cart.';
      }

      const feeDetails = selectedPlacementFeeDetails();
      if (!feeDetails.variant) {
        return 'Design fee variant for this placement is not configured.';
      }

      if (!feeDetails.available) {
        return 'Design fee variant for this placement is not available.';
      }

      if (aiDesignCount() > 0) {
        const aiDetails = aiFeeDetails();
        if (!aiDetails.variant) {
          return 'AI design fee product is not configured.';
        }

        if (!validCartVariantId(aiDetails.variantId) || !aiDetails.available) {
          return 'AI design fee product is not available.';
        }
      }

      return '';
    }

    function debugValidation(disabledReason, fallbackMode) {
      if (!debugEnabled) return;

      const feeDetails = selectedPlacementFeeDetails();

      console.debug('[InkNfiber custom designer]', {
        productSelected: Boolean(defaultVariantId || variants.length),
        firstAvailableVariantId: firstAvailableVariantId || null,
        uploadedDesign: imageElements().length > 0,
        uploadedDesignCount: uploadedDesignCount(),
        feeProductId: feeProductId || null,
        feeProductTitle: feeTitle,
        feeVariants,
        selectedView: state.activeView,
        selectedPlacementKey: normalizePlacementName(placementSelect?.value || ''),
        selectedPlacementLabel: feeDetails.placementLabel,
        matchedFeeVariantId: feeDetails.variantId || null,
        matchedFeeVariantTitle: feeDetails.variantTitle || null,
        matchedFeeVariantPriceCents: feeDetails.priceCents,
        matchedFeeVariantAvailable: feeDetails.available,
        feeTitle,
        totalSelectedQuantity: totalQuantity(),
        sizeBreakdown: sizeBreakdown(),
        selectedSizeQuantities: selectedSizeQuantities(),
        matchedVariantIds: Object.fromEntries(sizes.map((size) => [size, sizeVariantMap[size]?.id || null])),
        customDesignPriceTables,
        selectedShirtPriceCategory: usesWhiteShirtPricing() ? 'white' : 'color',
        selectedCustomDesignPriceTable: selectedCustomDesignPriceTable(),
        variantPricesUsed: priceDebugRows(),
        matchedVariantsPerSize: Object.fromEntries(
          sizes.map((size) => [
            size,
            sizeVariantMap[size]
              ? {
                  id: sizeVariantMap[size].id,
                  title: sizeVariantMap[size].title,
                  priceCents: moneyToCents(sizeVariantMap[size].price),
                }
              : null,
          ])
        ),
        productSubtotalCents: productSubtotalCents(),
        bulkDiscountEstimate: bulkDiscountEstimate(),
        designFeeSubtotalCents: designFeeSubtotalCents(),
        fallbackMode,
        finalButtonDisabledReason: disabledReason || '',
        elementCounts: {
          uploads: imageElements().length,
          text: textElements().length,
          symbols: symbolElements().length,
        },
        finalTotalCents: finalTotalCents(),
      });
    }

    function updateCartButton() {
      const message = validate();
      const fallbackMode = shouldUseFallbackMode();

      addButton.disabled = Boolean(message);
      debugValidation(message, fallbackMode);

      if (message) {
        setStatus(message, message.includes('Select a custom') || message.includes('available variant') ? 'warning' : 'error');
        return;
      }

      if (fallbackMode && !status?.classList.contains('inknfiber-custom-designer__status--success')) {
        setStatus('Product variants could not be fully matched, fallback mode will be used.', 'warning');
        return;
      }

      if (!status?.classList.contains('inknfiber-custom-designer__status--success')) {
        setStatus('');
      }
    }

    function reviewValidationMessage() {
      if (!state.elements.length) {
        return 'Please upload or create a design before reviewing.';
      }

      if (totalQuantity() <= 0) {
        return 'Please select at least one size and quantity before reviewing.';
      }

      return validate();
    }

    function setReviewStatus(message = '', type = '') {
      if (!reviewStatus) return;
      reviewStatus.textContent = message;
      reviewStatus.hidden = !message;
      reviewStatus.classList.remove(
        'inknfiber-custom-designer__status--error',
        'inknfiber-custom-designer__status--success',
        'inknfiber-custom-designer__status--warning'
      );
      if (type) {
        reviewStatus.classList.add(`inknfiber-custom-designer__status--${type}`);
      }
    }

    function appendReviewDetail(label, value) {
      if (!reviewDetails) return;

      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label;
      description.textContent = value || 'None';
      row.append(term, description);
      reviewDetails.append(row);
    }

    function renderReviewSizes() {
      if (!reviewSizes) return;

      reviewSizes.replaceChildren();
      selectedSizes().forEach((item) => {
        const listItem = document.createElement('li');
        const unitPriceCents = linePriceForSize(item.size);
        listItem.textContent = `${item.size} x ${item.quantity} - ${formatMoney(unitPriceCents)} each (${formatMoney(unitPriceCents * item.quantity)} total)`;
        reviewSizes.append(listItem);
      });
    }

    function renderReviewNotes() {
      if (!reviewNotes || !reviewNotesList) return;

      syncDesignNotesFromFields();
      reviewNotesList.replaceChildren();

      [
        ['Front Design', state.designNotes.front],
        ['Back Design', state.designNotes.back],
        ['Extra Info', state.designNotes.extra],
      ].forEach(([label, value]) => {
        if (!value) return;
        const item = document.createElement('li');
        const strong = document.createElement('strong');
        strong.textContent = `${label}: `;
        item.append(strong, document.createTextNode(value));
        reviewNotesList.append(item);
      });

      reviewNotes.hidden = !reviewNotesList.children.length;
    }

    function populateReviewDetails() {
      if (!reviewDetails) return;

      const element = productionArtworkElement();
      const production = productionMeasurements(element);
      const estimatorDetails = estimatorCheckoutDetails();
      const discount = bulkDiscountEstimate();
      const uploadNames = imageElements().map((image) => image.name).filter(Boolean).join(', ');
      const printCharge = estimatorDetails.totalQuantity > 1
        ? `${formatMoney(estimatorDetails.printChargeEachCents)} each (${formatMoney(estimatorDetails.printChargeTotalCents)} total)`
        : formatMoney(estimatorDetails.printChargeTotalCents || estimatorDetails.printChargeEachCents);

      reviewDetails.replaceChildren();
      appendReviewDetail('Product color', selectedColorData().label);
      appendReviewDetail('Selected view', viewDisplayName());
      appendReviewDetail('Print placement', placementDisplayName());
      appendReviewDetail('Artwork size', production.designSize || 'Not available');
      appendReviewDetail(
        'Artwork position',
        production.position
          ? `X ${production.xToolX || '0"'} from left, Y ${production.xToolY || '0"'} from top`
          : 'Not available'
      );
      appendReviewDetail('Print charge', printCharge);
      if (estimatorDetails.printChargeSides.length > 1) {
        estimatorDetails.printChargeSides.forEach((side) => {
          appendReviewDetail(`${side.label} print`, formatMoney(side.printChargeCents));
        });
      }
      appendReviewDetail('Shirt subtotal', formatMoney(discount.productSubtotalCents));
      appendReviewDetail('Final checkout price', formatMoney(finalTotalCents()));
      appendReviewDetail('Uploaded artwork', uploadNames || 'None');

      renderReviewSizes();
      renderReviewNotes();
    }

    function revokeReviewPreviewUrl() {
      if (!reviewPreviewObjectUrl) return;

      try {
        URL.revokeObjectURL(reviewPreviewObjectUrl);
      } catch (_) {
        // Ignore stale preview URLs.
      }

      reviewPreviewObjectUrl = '';
    }

    function closeReviewDesignModal() {
      if (!reviewModal) return;
      reviewModal.hidden = true;
      designer.classList.remove('is-review-modal-open');
      document.documentElement.classList.remove('inknfiber-custom-review-is-open');
      document.body.classList.remove('inknfiber-custom-review-is-open');
      setReviewStatus('');
      revokeReviewPreviewUrl();
      if (reviewPreviewImage) reviewPreviewImage.removeAttribute('src');
    }

    async function openReviewDesignModal() {
      const message = reviewValidationMessage();
      if (message) {
        setStatus(message, message.includes('custom T-shirt product') || message.includes('available variant') ? 'warning' : 'error');
        return;
      }

      if (!reviewModal) return;

      setStatus('');
      setReviewStatus('Preparing your design preview...', 'warning');
      reviewModal.hidden = false;
      designer.classList.add('is-review-modal-open');
      document.documentElement.classList.add('inknfiber-custom-review-is-open');
      document.body.classList.add('inknfiber-custom-review-is-open');
      reviewDialog?.focus();

      if (reviewAddToCartButton) reviewAddToCartButton.disabled = true;

      try {
        await refreshPrintChargeAnalysisNow({ force: true });
        await refreshArtworkEstimateNow();
        populateReviewDetails();
        const blob = await generateMockupPreviewBlob();
        revokeReviewPreviewUrl();
        reviewPreviewObjectUrl = URL.createObjectURL(blob);
        if (reviewPreviewImage) reviewPreviewImage.src = reviewPreviewObjectUrl;
        setReviewStatus('');
      } catch (error) {
        console.error('[InkNfiber custom designer] Review preview failed:', error);
        setReviewStatus('Unable to generate the review preview. Please try again.', 'error');
      } finally {
        if (reviewAddToCartButton) reviewAddToCartButton.disabled = false;
      }
    }

    function addReviewedDesignToCart() {
      const message = validate();
      if (message) {
        setReviewStatus(message, message.includes('custom T-shirt product') || message.includes('available variant') ? 'warning' : 'error');
        return;
      }

      updateCartButton();
      if (addButton.disabled) {
        setReviewStatus('Please review the highlighted order details before adding to cart.', 'error');
        return;
      }

      closeReviewDesignModal();
      addButton.click();
    }

    function syncActiveControls() {
      const element = activeElement();
      if (sizeRange) sizeRange.value = element?.scale || 42;
      if (rotationRange) rotationRange.value = element?.rotation || 0;

      if (element?.type === 'text') {
        if (textInput) textInput.value = element.content || '';
        if (textFont) textFont.value = element.fontFamily || textFont.value;
        if (textColor) textColor.value = element.color || '#ffffff';
      }
    }

    function updateSummaries() {
      const color = selectedColorData();
      const uploadNames = imageElements().map((element) => element.name).join(', ');
      const discount = bulkDiscountEstimate();
      const subtotal = discount.productSubtotalCents;
      const designFeeSubtotal = designFeeSubtotalCents();
      const aiFeeSubtotal = aiDesignFeeSubtotalCents();
      const total = finalTotalCents();
      const feeDetails = selectedPlacementFeeDetails();
      const estimatorDetails = estimatorCheckoutDetails();

      updateShirtPriceDisplays();
      summaryColor.textContent = color.label;
      summarySizes.textContent = sizeBreakdown();
      summaryQuantity.textContent = String(totalQuantity());
      if (summaryView) summaryView.textContent = viewDisplayName();
      summaryPlacement.textContent = placementDisplayName();
      summaryFile.textContent = uploadNames || 'No files selected';
      summaryUploadCount.textContent = String(imageElements().length);
      summaryDesignSurcharge.textContent = designFeeSummaryText();
      if (summaryDesignTotal) summaryDesignTotal.textContent = formatMoney(estimatorDetails.printChargeTotalCents);
      if (summaryAiDesignSurcharge) summaryAiDesignSurcharge.textContent = aiPhotoshootEnabled ? aiDesignFeeSummaryText() : '';
      summaryTextCount.textContent = String(textElements().length);
      summarySymbolCount.textContent = String(symbolElements().length);
      summaryProductSubtotal.textContent = formatMoney(subtotal);
      if (summaryEstimatorNote) {
        const sideSummary = estimatorDetails.printChargeSideSummary;
        summaryEstimatorNote.textContent = estimatorDetails.printChargeEachCents > 0
          ? `Print charge is ${formatMoney(estimatorDetails.printChargeEachCents)} per shirt${sideSummary ? ` (${sideSummary})` : ''}, based on the composed artwork. ${estimatorDetails.totalQuantity} shirt${estimatorDetails.totalQuantity === 1 ? '' : 's'} = ${formatMoney(estimatorDetails.printChargeTotalCents)}.`
          : 'Print charge will update after artwork is added.';
      }
      if (summaryBulkDiscountTier) summaryBulkDiscountTier.textContent = discount.label;
      if (summaryBulkDiscountSavings) summaryBulkDiscountSavings.textContent = discount.estimatedSavingsCents > 0 ? `-${formatMoney(discount.estimatedSavingsCents)}` : formatMoney(0);
      if (summaryProductTotalAfterDiscount) summaryProductTotalAfterDiscount.textContent = formatMoney(discount.productTotalAfterDiscountCents);
      if (summaryBulkDiscountNotice) summaryBulkDiscountNotice.hidden = !discount.requiresQuote;
      summaryTotal.textContent = formatMoney(total);

      if (debugEnabled) {
        console.debug('[InkNfiber custom designer pricing]', {
          selectedSizeQuantities: selectedSizeQuantities(),
          totalSelectedQuantity: totalQuantity(),
          sizeBreakdown: sizeBreakdown(),
          matchedVariantIds: Object.fromEntries(sizes.map((size) => [size, sizeVariantMap[size]?.id || null])),
          customDesignPriceTables,
          selectedShirtPriceCategory: usesWhiteShirtPricing() ? 'white' : 'color',
          selectedCustomDesignPriceTable: selectedCustomDesignPriceTable(),
          variantPricesUsed: priceDebugRows(),
          selectedView: state.activeView,
          selectedPlacementKey: normalizePlacementName(placementSelect?.value || ''),
          selectedPlacementLabel: feeDetails.placementLabel,
          uploadedDesignCount: uploadedDesignCount(),
          aiPhotoshootEnabled,
          aiDesignCount: aiDesignCount(),
          feeProductId: feeProductId || null,
          feeProductTitle: feeTitle,
          allFeeVariantsLoaded: feeVariants,
          matchedFeeVariantId: feeDetails.variantId || null,
          matchedFeeVariantTitle: feeDetails.variantTitle || null,
          matchedFeeVariantPriceCents: feeDetails.priceCents,
          matchedFeeVariantAvailable: feeDetails.available,
          aiFeeProductId: aiFeeProductId || null,
          aiFeeVariantId: aiFeeDetails().variantId || null,
          aiFeeQuantity: aiDesignCount(),
          aiFeeSubtotalCents: aiFeeSubtotal,
          productHasSizeVariants,
          hasMappedSizeVariants: hasMappedSizeVariants(),
          fallbackVariant: firstAvailableVariant
            ? {
                id: firstAvailableVariant.id,
                title: firstAvailableVariant.title,
                priceCents: moneyToCents(firstAvailableVariant.price),
              }
            : null,
          productSubtotalCents: subtotal,
          configuredProductSubtotalCents: productSubtotalCents(),
          shopifyVariantSubtotalCents: shopifyVariantSubtotalCents(),
          productPriceCorrectionCents: productPriceCorrectionCents(),
          checkoutProductSubtotalCents: checkoutProductSubtotalCents(),
          estimatorCheckoutPricingEnabled,
          estimatorCheckoutPriceMode,
          estimatorCheckoutDetails: estimatorDetails,
          customDesignPrintRateCentsPerSqIn,
          customDesignMinimumPrintChargeCents,
          bulkDiscountEstimate: discount,
          shirtSubtotalCents: subtotal,
          estimatedProductTotalAfterDiscountCents: discount.productTotalAfterDiscountCents,
          designFeeSubtotalCents: designFeeSubtotal,
          finalTotalCents: total,
        });
      }

      if (fileName) {
        fileName.textContent = uploadNames || '';
        fileName.hidden = !uploadNames;
      }
    }

    function renderElements() {
      const visibleElements = visibleElementsForActiveView();
      const hasVisibleElements = visibleElements.length > 0;
      const emptyPreviewUploadEnabled = !mobilePreviewMediaQuery.matches;
      elementsRoot.replaceChildren();
      emptyState.hidden = hasVisibleElements || !emptyPreviewUploadEnabled;
      emptyState.classList.toggle('is-hidden', hasVisibleElements || !emptyPreviewUploadEnabled);
      if (safeArea) {
        safeArea.classList.toggle('has-empty-upload-hotspot', !hasVisibleElements && emptyPreviewUploadEnabled);
        if (hasVisibleElements || !emptyPreviewUploadEnabled) {
          safeArea.removeAttribute('role');
          safeArea.removeAttribute('aria-label');
          safeArea.removeAttribute('tabindex');
        } else {
          safeArea.setAttribute('role', 'button');
          safeArea.setAttribute('aria-label', 'Upload artwork to preview it here');
          safeArea.setAttribute('tabindex', '0');
        }
      }
      debugLayerSelectionState('render');

      visibleElements.forEach((element) => {
        const wrapper = document.createElement('button');
        const isActive = element.id === state.activeElementId;
        wrapper.type = 'button';
        wrapper.className = `inknfiber-custom-designer__element inknfiber-custom-designer__element--${element.type}`;
        wrapper.dataset.elementId = element.id;
        wrapper.classList.toggle('is-active', isActive);
        wrapper.style.left = `${element.x}%`;
        wrapper.style.top = `${element.y}%`;
        wrapper.style.transform = `translate(-50%, -50%) rotate(${element.rotation}deg)`;

        if (element.type === 'image') {
          wrapper.style.width = `${element.scale}%`;
          if (element.src) {
            const image = document.createElement('img');
            image.alt = element.name;
            image.draggable = false;
            image.src = element.src;
            wrapper.append(image);
          } else {
            const span = document.createElement('span');
            span.textContent = 'Re-upload artwork';
            span.style.fontSize = '14px';
            wrapper.append(span);
          }
        } else {
          const span = document.createElement('span');
          span.textContent = element.content;
          span.style.fontFamily = element.fontFamily || 'Arial Black, Arial, sans-serif';
          span.style.color = element.color || '#ffffff';
          span.style.fontSize = `${Math.max(14, element.scale)}px`;
          wrapper.append(span);
        }

        if (isActive) {
          const transformFeedback = document.createElement('span');
          transformFeedback.className = 'inknfiber-custom-designer__transform-feedback';
          transformFeedback.dataset.transformFeedback = '';
          transformFeedback.setAttribute('aria-hidden', 'true');
          transformFeedback.hidden = true;
          wrapper.append(transformFeedback);

          const resizeHandle = document.createElement('span');
          resizeHandle.className = 'inknfiber-custom-designer__transform-handle inknfiber-custom-designer__transform-handle--resize';
          resizeHandle.dataset.transformHandle = 'resize';
          resizeHandle.dataset.transformAction = 'resize';
          resizeHandle.setAttribute('aria-hidden', 'true');

          wrapper.append(resizeHandle);

          if (enableRotation) {
            const rotateHandle = document.createElement('span');
            rotateHandle.className = 'inknfiber-custom-designer__transform-handle inknfiber-custom-designer__transform-handle--rotate';
            rotateHandle.dataset.transformHandle = 'rotate';
            rotateHandle.dataset.transformAction = 'rotate';
            rotateHandle.setAttribute('aria-hidden', 'true');
            wrapper.append(rotateHandle);
          }
        }

        elementsRoot.append(wrapper);
      });
    }

    function renderLayerList() {
      elementList.replaceChildren();

      if (!state.elements.length) {
        const empty = document.createElement('p');
        empty.textContent = 'No designs, text, or symbols added yet.';
        elementList.append(empty);
        return;
      }

      state.elements.forEach((element) => {
        const row = document.createElement('div');
        row.className = 'inknfiber-custom-designer__layer';
        row.classList.toggle('is-active', element.id === state.activeElementId);

        const select = document.createElement('button');
        select.type = 'button';
        select.dataset.selectElement = element.id;

        if (element.type === 'image' && element.src) {
          const thumbnail = document.createElement('img');
          thumbnail.className = 'inknfiber-custom-designer__layer-thumbnail';
          thumbnail.src = element.src;
          thumbnail.alt = '';
          thumbnail.loading = 'lazy';
          select.append(thumbnail);
        }

        const label = document.createElement('span');
        label.textContent = `${element.type}: ${element.name || element.content}`;
        select.append(label);

        const actions = document.createElement('div');
        actions.className = 'inknfiber-custom-designer__layer-actions';

        if (aiBackgroundRemoverEnabled && element.type === 'image') {
          const removeBackground = document.createElement('button');
          removeBackground.type = 'button';
          removeBackground.dataset.removeBackgroundElement = element.id;
          removeBackground.disabled = element.backgroundRemovalStatus === 'processing' || element.backgroundRemoved;
          removeBackground.textContent = element.backgroundRemovalStatus === 'processing'
            ? 'Removing background...'
            : element.backgroundRemoved
              ? 'Background Removed'
              : 'Remove Background';
          actions.append(removeBackground);

          if (element.originalSrc && element.backgroundRemoved) {
            const useOriginal = document.createElement('button');
            useOriginal.type = 'button';
            useOriginal.dataset.useOriginalElement = element.id;
            useOriginal.textContent = 'Use Original';
            actions.append(useOriginal);
          }

          if (element.backgroundRemovalMessage) {
            const message = document.createElement('p');
            message.className = 'inknfiber-custom-designer__layer-status';
            message.classList.toggle('is-error', element.backgroundRemovalStatus === 'error');
            message.textContent = element.backgroundRemovalMessage;
            actions.append(message);
          }
        }

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.removeElement = element.id;
        remove.ariaLabel = `Remove ${element.name || element.content}`;
        remove.textContent = 'Remove';

        row.append(select);
        if (actions.childElementCount) row.append(actions);
        row.append(remove);
        elementList.append(row);
      });
    }

    function renderDesign() {
      ensureActiveStateAllowed('render-design');
      const color = selectedColorData();
      debugShirtColorRenderState();

      mockup.style.setProperty('--shirt-color', color.color);
      syncColorSwatches();
      syncPlacementOptions();

      if (viewLabel) {
        viewLabel.textContent = `${viewDisplayName()} view`;
      }

      viewButtons.forEach((button) => {
        const enabled = viewIsEnabled(button.dataset.viewButton);
        button.hidden = !enabled;
        button.disabled = !enabled;
        button.setAttribute('aria-hidden', enabled ? 'false' : 'true');
        button.classList.toggle('is-active', enabled && normalizeViewKey(button.dataset.viewButton) === state.activeView);
      });

      const usesRealMockupImage = state.activeView === 'front' || state.activeView === 'back';
      mockup.classList.toggle('has-active-mockup-image', usesRealMockupImage);
      let activeMockupSource = '';
      mockupImages.forEach((image) => {
        const isActiveMockup = usesRealMockupImage && image.dataset.mockupImage === state.activeView;
        image.classList.toggle('is-active', isActiveMockup);
        if (isActiveMockup) activeMockupSource = image.currentSrc || image.src;
      });
      if (activeMockupSource) {
        mockup.style.setProperty('--active-mockup-mask', `url("${activeMockupSource}")`);
      }
      updateSafeAreaPlacement();
      renderMockupCanvas();

      renderElements();
      refreshPlaceholderLayout('render-design');
      renderLayerList();
      syncActiveControls();
      updateSummaries();
      schedulePrintChargeAnalysis();
      scheduleArtworkEstimate();
      updateCartButton();
      updateHistoryButtons();
      scheduleAutoSave();
    }

    function setActiveElement(elementId, options = {}) {
      if (!state.elements.some((element) => element.id === elementId)) return;
      state.activeElementId = elementId;
      const element = activeElement();
      if (element?.view) element.view = normalizeViewKey(element.view);
      if (element?.view && !viewIsEnabled(element.view)) {
        const previousElementView = element.view;
        element.view = 'front';
        if (designerDebug) {
          transformDebugLog('selected layer view normalized because sleeve views are disabled', {
            selectedLayerId: element.id,
            previousView: previousElementView,
            nextView: element.view,
          });
        }
      }
      if (element) state.activeView = fallbackView(element.view || state.activeView, 'select-layer');

      if (!options.silent) {
        renderDesign();
      }

      if (designerDebug) {
        transformDebugLog('layer selected', {
          selectedLayerId: state.activeElementId,
          selectionReason: options.reason || 'user-selection',
          layerCount: state.elements.length,
          visibleLayerCount: visibleLayerCount(),
          placeholderHidden: Boolean(emptyState && emptyState.hidden),
        });
      }
    }

    function setActiveView(view) {
      const previousActiveView = state.activeView;
      if (placementSelect?.value) {
        state.placementsByView[previousActiveView] = canonicalPlacementValue(placementSelect.value);
      }
      state.activeView = fallbackView(view, 'set-active-view');
      syncPlacementOptions(state.placementsByView[state.activeView]);
      const firstVisible = state.elements.find((element) => !element.view || element.view === state.activeView);
      const activeVisible = state.elements.some((element) => element.id === state.activeElementId && (!element.view || element.view === state.activeView));
      if (!activeVisible) {
        state.activeElementId = firstVisible?.id || '';
      }
      renderDesign();

      if (designerDebug) {
        transformDebugLog('view changed', {
          selectedView: state.activeView,
          previousActiveView,
          activeViewButton: state.activeView,
          enable_sleeve_views: sleeveViewsEnabled,
          availableViews: availableViewKeys,
          visibleLayerCount: visibleLayerCount(),
          placeholderHidden: Boolean(emptyState && (emptyState.hidden || emptyState.classList.contains('is-hidden'))),
          placeholderShown: hasVisibleElementsForActiveView() === false,
        });
      }
    }

    function addElement(element) {
      const nextElement = {
        id: generateId('element'),
        type: 'image',
        name: 'Design',
        content: '',
        src: '',
        view: state.activeView,
        x: 50,
        y: 50,
        scale: 42,
        rotation: 0,
        fontFamily: 'Arial Black, Arial, sans-serif',
        color: '#ffffff',
        ...element,
      };

      state.elements.push(nextElement);
      state.activeElementId = nextElement.id;
      renderDesign();
      return nextElement.id;
    }

    function removeElement(elementId, options = {}) {
      const nextElements = state.elements.filter((element) => element.id !== elementId);
      state.elements = nextElements;
      originalUploadFiles.delete(elementId);
      artworkQualityResults.delete(elementId);

      if (state.activeElementId === elementId) {
        const nextVisibleElement = options.clearSelection
          ? null
          : state.elements.find((element) => !element.view || element.view === state.activeView);
        state.activeElementId = nextVisibleElement?.id || '';
      }

      renderDesign();
    }

    function clearActiveElement(reason = 'manual') {
      if (!state.activeElementId) return false;

      const previousLayerId = state.activeElementId;
      state.activeElementId = '';
      state.dragging = false;
      state.dragPointerId = null;
      state.dragStart = null;
      state.transformMode = '';
      state.transformPointerId = null;
      state.interactionElementId = '';
      state.transformStart = null;
      hideTransformFeedback();

      if (designerDebug) {
        transformDebugLog('layer deselected', {
          selectedLayerId: previousLayerId,
          deselectReason: reason,
          layerCount: state.elements.length,
          visibleLayerCount: visibleLayerCount(),
          placeholderHidden: Boolean(emptyState && emptyState.hidden),
        });
      }

      renderDesign();
      return true;
    }

    function resetActiveDesign() {
      if (!state.elements.length) return;

      state.elements.forEach((element) => {
        if (element?.type === 'image' && element.src && restoredObjectUrls.has(element.src)) {
          try {
            URL.revokeObjectURL(element.src);
          } catch (_) {
            // Ignore cleanup failures for stale object URLs.
          }
          restoredObjectUrls.delete(element.src);
        }
        originalUploadFiles.delete(element.id);
        artworkQualityResults.delete(element.id);
      });

      const removedElementCount = state.elements.length;
      const removedVisibleElementCount = visibleLayerCount();

      state.elements = [];
      state.activeElementId = '';
      state.dragging = false;
      state.dragPointerId = null;
      state.dragStart = null;
      state.interactionElementId = '';
      state.transformMode = '';
      state.transformPointerId = null;
      state.transformStart = null;
      state.history.undo = [];
      state.history.redo = [];

      if (designerDebug) {
        transformDebugLog('design reset', {
          removedElementCount,
          removedVisibleElementCount,
        });
      }

      artworkChecker?.reset?.(artworkQualityContainer);
      renderDesign();
    }

    function setElementPositionFromEvent(event) {
      const element = elementById(state.interactionElementId);
      const start = state.dragStart;
      if (!element || !start) return;

      const point = clientPoint(event);
      const deltaX = ((point.x - start.pointerX) / start.safeAreaWidth) * 100;
      const deltaY = ((point.y - start.pointerY) / start.safeAreaHeight) * 100;

      element.x = clamp(start.x + deltaX, 0, 100);
      element.y = clamp(start.y + deltaY, 0, 100);
      applyElementNodeTransform(element);
      updateSummaries();
      schedulePrintChargeAnalysis();

      transformDebugLog('pointermove drag', {
        pointerId: event.pointerId,
        dx: round(deltaX),
        dy: round(deltaY),
        x: round(element.x),
        y: round(element.y),
      });
    }

    function startElementDrag(elementId, event) {
      const element = elementById(elementId);
      if (!element) return;

      pushTransformHistory();

      const rect = safeArea.getBoundingClientRect();
      const point = clientPoint(event);
      state.activeElementId = element.id;
      state.interactionElementId = element.id;
      state.dragging = true;
      state.dragPointerId = event.pointerId;
      state.dragStart = {
        pointerX: point.x,
        pointerY: point.y,
        safeAreaWidth: Math.max(rect.width, 1),
        safeAreaHeight: Math.max(rect.height, 1),
        x: Number(element.x || 50),
        y: Number(element.y || 50),
      };

      try {
        safeArea.setPointerCapture(event.pointerId);
      } catch (_) {
        // Some browsers may already have pointer capture on the original target.
      }

      transformDebugLog('pointerdown drag', {
        pointerId: event.pointerId,
        elementId,
        x: round(element.x),
        y: round(element.y),
        scale: round(element.scale),
        rotation: round(element.rotation),
      });
    }

    function startElementTransform(elementId, mode, event) {
      const element = elementById(elementId);
      if (!element) return;

      const rect = safeArea.getBoundingClientRect();
      const centerX = rect.left + (rect.width * element.x) / 100;
      const centerY = rect.top + (rect.height * element.y) / 100;
      const point = clientPoint(event);
      const pointerAngle = Math.atan2(point.y - centerY, point.x - centerX) * 180 / Math.PI;
      const pointerDistance = Math.hypot(point.x - centerX, point.y - centerY);

      pushTransformHistory();

      state.transformMode = mode;
      state.transformPointerId = event.pointerId;
      state.interactionElementId = element.id;
      state.activeElementId = element.id;
      state.transformStart = {
        centerX,
        centerY,
        pointerAngle,
        pointerDistance: Math.max(pointerDistance, 1),
        scale: Number(element.scale || 42),
        rotation: Number(element.rotation || 0),
      };

      try {
        safeArea.setPointerCapture(event.pointerId);
      } catch (_) {
        // Some browsers may already have pointer capture on the original target.
      }

      transformDebugLog('pointerdown transform', {
        mode,
        pointerId: event.pointerId,
        elementId,
        centerX: round(centerX),
        centerY: round(centerY),
        startDistance: round(pointerDistance),
        startAngle: round(pointerAngle),
        scale: round(element.scale),
        rotation: round(element.rotation),
      });

      showTransformFeedback(element, mode);
    }

    function updateElementTransformFromEvent(event) {
      const element = elementById(state.interactionElementId);
      const start = state.transformStart;
      if (!element || !start) return;
      const point = clientPoint(event);

      if (state.transformMode === 'resize') {
        const nextDistance = Math.hypot(point.x - start.centerX, point.y - start.centerY);
        const nextScale = start.scale * (nextDistance / start.pointerDistance);
        element.scale = clamp(nextScale, element.type === 'image' ? 18 : 14, 90);
      }

      if (state.transformMode === 'rotate' && enableRotation) {
        const nextAngle = Math.atan2(point.y - start.centerY, point.x - start.centerX) * 180 / Math.PI;
        element.rotation = clamp(start.rotation + (nextAngle - start.pointerAngle), -180, 180);
      }

      applyElementNodeTransform(element);
      showTransformFeedback(element, state.transformMode);
      updateSummaries();
      schedulePrintChargeAnalysis();

      transformDebugLog('pointermove transform', {
        mode: state.transformMode,
        pointerId: event.pointerId,
        scale: round(element.scale),
        rotation: round(element.rotation),
        sizeInches: elementSizeInchesLabel(element),
        x: round(element.x),
        y: round(element.y),
      });
    }

    function stopElementTransform(event) {
      if (event?.pointerId && event.pointerId !== state.transformPointerId) return;
      const element = elementById(state.interactionElementId);

      transformDebugLog('pointerup transform commit', {
        mode: state.transformMode,
        pointerId: event?.pointerId,
        elementId: state.interactionElementId,
        x: element ? round(element.x) : null,
        y: element ? round(element.y) : null,
        scale: element ? round(element.scale) : null,
        rotation: element ? round(element.rotation) : null,
      });

      state.transformMode = '';
      state.transformPointerId = null;
      state.transformStart = null;
      state.interactionElementId = '';
      hideTransformFeedback();

      if (event?.pointerId) {
        try {
          safeArea.releasePointerCapture(event.pointerId);
        } catch (_) {
          // Pointer capture may already be released by the browser.
        }
      }

      renderDesign();
    }

    function safePreviewSourceForStorage(source, usedPreviewLength) {
      const value = String(source || '');
      if (!value || value.startsWith('blob:')) return { source: '', usedPreviewLength, omitted: Boolean(value) };
      if (!value.startsWith('data:')) return { source: value, usedPreviewLength, omitted: false };
      if (value.length > maxSinglePreviewStorageLength) return { source: '', usedPreviewLength, omitted: true };
      if (usedPreviewLength + value.length > maxSavedJsonLength) return { source: '', usedPreviewLength, omitted: true };
      return {
        source: value,
        usedPreviewLength: usedPreviewLength + value.length,
        omitted: false,
      };
    }

    function serializableImageElement(element, options, previewUsage) {
      const uploadFile = originalUploadFiles.get(element.id) || null;
      const stored = {
        ...element,
        src: '',
        originalSrc: '',
        fileName: element.name || uploadFile?.name || '',
        fileType: uploadFile?.type || element.fileType || element.backgroundRemovedMimeType || '',
        fileSize: Number(uploadFile?.size || element.fileSize || 0) || 0,
        naturalWidth: Number(element.naturalWidth || 0) || 0,
        naturalHeight: Number(element.naturalHeight || 0) || 0,
      };
      const hasCloudReference = Boolean(element.originalDesignKey || element.mockupPreviewKey || element.aiImageKey || element.backgroundRemovedImageKey);
      let requiresReupload = Boolean((element.src || element.originalSrc) && !hasCloudReference);

      if (options.includePreviews !== false) {
        const srcResult = safePreviewSourceForStorage(element.src, previewUsage.used);
        stored.src = srcResult.source;
        previewUsage.used = srcResult.usedPreviewLength;

        const originalResult = safePreviewSourceForStorage(
          element.originalSrc && element.originalSrc !== element.src ? element.originalSrc : '',
          previewUsage.used
        );
        stored.originalSrc = originalResult.source;
        previewUsage.used = originalResult.usedPreviewLength;

        requiresReupload = (srcResult.omitted || originalResult.omitted || (!stored.src && !stored.originalSrc && Boolean(element.src || element.originalSrc))) && !hasCloudReference;
      }

      stored.requiresReupload = requiresReupload;
      return stored;
    }

    function buildStoredDesignPayload(elements, flags = {}) {
      return {
        version: 2,
        designId: state.designId,
        activeView: state.activeView,
        activeElementId: state.activeElementId,
        elements,
        previewTooLarge: Boolean(flags.requiresReupload),
        requiresReupload: Boolean(flags.requiresReupload),
        omittedUploads: Boolean(flags.omittedUploads),
        minimalSave: Boolean(flags.minimalSave),
        shirtColor: selectedColorData().label,
        printPlacement: placementSelect?.value || 'Left Chest',
        placementsByView: { ...state.placementsByView },
        sizeQuantities: selectedSizeQuantities(),
        designNotes: { ...state.designNotes },
        savedAt: new Date().toISOString(),
      };
    }

    function serializableState(options = {}) {
      const previewUsage = { used: 0 };
      let requiresReupload = false;
      const elements = state.elements.map((element) => {
        if (element.type !== 'image') return element;
        const stored = serializableImageElement(element, options, previewUsage);
        requiresReupload = requiresReupload || stored.requiresReupload;
        return stored;
      });

      return buildStoredDesignPayload(elements, { requiresReupload });
    }

    function minimalSerializableState() {
      const hadUploads = imageElements().length > 0;
      const elements = state.elements.filter((element) => element.type !== 'image');
      return buildStoredDesignPayload(elements, {
        requiresReupload: hadUploads,
        omittedUploads: hadUploads,
        minimalSave: true,
      });
    }

    function isValidStoredDesignState(payload) {
      const hasModernElements = Array.isArray(payload?.elements);
      const hasLegacyImage = Boolean(payload?.fileName || payload?.filePreviewSrc);

      return Boolean(
        payload
        && typeof payload === 'object'
        && typeof payload.designId === 'string'
        && payload.designId.trim()
        && (hasModernElements || hasLegacyImage)
      );
    }

    function parseStoredDesignPayload(raw, storageKey = '') {
      if (!raw) return null;

      try {
        const parsed = JSON.parse(raw);
        if (!isValidStoredDesignState(parsed)) return null;
        return {
          ...parsed,
          designId: String(parsed.designId || '').trim(),
          storageKey: storageKey || canonicalStorageKey(parsed.designId),
        };
      } catch (_) {
        return null;
      }
    }

    function stripPreviewSources(payload) {
      return {
        ...payload,
        previewTooLarge: true,
        requiresReupload: true,
        elements: (payload.elements || []).map((element) => (
          element?.type === 'image'
            ? { ...element, src: '', originalSrc: '' }
            : element
        )),
      };
    }

    function writeStoredDesignPayload(payload) {
      const designId = String(payload?.designId || '').trim() || generateId('design');
      const normalizedPayload = {
        ...payload,
        version: payload.version || 2,
        designId,
        previewTooLarge: Boolean(payload.previewTooLarge || payload.requiresReupload || payload.omittedUploads),
        requiresReupload: Boolean(payload.previewTooLarge || payload.requiresReupload || payload.omittedUploads),
      };
      const storageKey = canonicalStorageKey(designId);
      const serialized = JSON.stringify(normalizedPayload);
      if (serialized.length > maxSavedJsonLength) {
        throw new Error(`Saved design payload is too large (${Math.round(serialized.length / 1024)}KB).`);
      }

      localStorage.setItem(storageKey, serialized);
      localStorage.setItem(latestStorageKey, storageKey);

      const verified = parseStoredDesignPayload(localStorage.getItem(storageKey), storageKey);
      const latestPointer = String(localStorage.getItem(latestStorageKey) || '').trim();
      if (!verified || latestPointer !== storageKey) {
        throw new Error('Saved design verification failed.');
      }

      return verified;
    }

    function cleanupCurrentDesignStorage(designId) {
      try {
        const latestRaw = String(localStorage.getItem(latestStorageKey) || '').trim();
        if (latestRaw.startsWith('{')) {
          localStorage.removeItem(latestStorageKey);
        }
        if (designId) {
          localStorage.removeItem(canonicalStorageKey(designId));
        }
      } catch (_) {
        // Storage may be unavailable; the save path will report the final result.
      }
    }

    function mergeStoredPreviewSources(payload) {
      if (!payload?.elements?.length) return payload;

      const stored = readStoredDesignState(payload.designId)?.parsed || readLatestSavedDesignState();

      if (!stored?.elements?.length) return payload;

      const storedSources = new Map(
        stored.elements
          .filter((element) => element?.type === 'image' && element.id && (element.src || element.originalSrc))
          .map((element) => [element.id, {
            src: !String(element.src || '').startsWith('blob:') ? element.src : '',
            originalSrc: !String(element.originalSrc || '').startsWith('blob:') ? element.originalSrc : '',
          }])
      );

      if (!storedSources.size) return payload;

      return {
        ...payload,
        previewTooLarge: payload.previewTooLarge || Boolean(stored.previewTooLarge),
        elements: payload.elements.map((element) => {
          if (element?.type !== 'image' || element.src || !storedSources.has(element.id)) return element;
          const storedSource = storedSources.get(element.id);
          return {
            ...element,
            src: storedSource?.src || element.src,
            originalSrc: storedSource?.originalSrc || element.originalSrc,
          };
        }),
      };
    }

    function persistDesignStateResult(options = {}) {
      const includePreviews = options.includePreviews !== false;
      const payload = serializableState({ includePreviews });
      cleanupCurrentDesignStorage(payload.designId);

      try {
        const verified = writeStoredDesignPayload(payload);
        return {
          ok: true,
          previewsStored: !verified.previewTooLarge && !verified.requiresReupload,
          requiresReupload: Boolean(verified.previewTooLarge || verified.requiresReupload),
          payload: verified,
          storageKey: verified.storageKey || canonicalStorageKey(verified.designId),
          saveLevel: verified.requiresReupload ? 'partial' : 'full',
        };
      } catch (error) {
        const fallbackPayload = stripPreviewSources(payload);

        try {
          const verified = writeStoredDesignPayload(fallbackPayload);
          return {
            ok: true,
            previewsStored: false,
            requiresReupload: true,
            payload: verified,
            storageKey: verified.storageKey || canonicalStorageKey(verified.designId),
            saveLevel: 'partial',
            error,
          };
        } catch (fallbackError) {
          const minimalPayload = minimalSerializableState();

          try {
            const verified = writeStoredDesignPayload(minimalPayload);
            restoreDebug('localStorage save used minimal fallback', {
              designId: payload.designId,
              originalSizeKb: Math.round(serializedLength(payload) / 1024),
              fallbackSizeKb: Math.round(serializedLength(fallbackPayload) / 1024),
              minimalSizeKb: Math.round(serializedLength(minimalPayload) / 1024),
            });
            return {
              ok: true,
              previewsStored: false,
              requiresReupload: Boolean(verified.requiresReupload),
              payload: verified,
              storageKey: verified.storageKey || canonicalStorageKey(verified.designId),
              saveLevel: 'minimal',
              error: fallbackError,
            };
          } catch (minimalError) {
            restoreDebug('localStorage save failed', {
              designId: payload.designId,
              originalSizeKb: Math.round(serializedLength(payload) / 1024),
              fallbackSizeKb: Math.round(serializedLength(fallbackPayload) / 1024),
              minimalSizeKb: Math.round(serializedLength(minimalPayload) / 1024),
              error: minimalError?.name || minimalError?.message || minimalError,
            });
            return {
              ok: false,
              previewsStored: false,
              requiresReupload: false,
              payload: null,
              storageKey: '',
              saveLevel: 'failed',
              error: minimalError,
            };
          }
        }
      }
    }

    function persistDesignState(options = {}) {
      const result = persistDesignStateResult(options);
      return result.ok && result.previewsStored;
    }

    function migrateLegacyState(parsed, designId) {
      if (Array.isArray(parsed.elements)) return parsed.elements;
      if (!parsed.fileName && !parsed.filePreviewSrc) return [];

      const legacyActiveView = fallbackView(parsed.activeView || 'front', 'legacy-state-active-view');

      return [{
        id: generateId('element'),
        type: 'image',
        name: parsed.fileName || 'Uploaded design',
        src: parsed.filePreviewSrc || '',
        view: legacyActiveView,
        x: parsed.sides?.[legacyActiveView]?.x || 50,
        y: parsed.sides?.[legacyActiveView]?.y || 50,
        scale: parsed.sides?.[legacyActiveView]?.scale || 42,
        rotation: parsed.sides?.[legacyActiveView]?.rotation || 0,
        designId,
      }];
    }

    function restoreDebug(message, details = {}) {
      if (!debugEnabled) return;
      console.debug('[InkNfiber custom designer restore]', message, details);
    }

    function readStoredDesignState(designId) {
      const storageKey = storageKeys(designId).find((key) => {
        try {
          return Boolean(localStorage.getItem(key));
        } catch (_) {
          return false;
        }
      });

      if (!storageKey) return null;

      try {
        const parsed = parseStoredDesignPayload(localStorage.getItem(storageKey), storageKey);
        if (!parsed) return null;
        return { storageKey, parsed };
      } catch (error) {
        restoreDebug('localStorage parse failed', {
          editDesignId: designId,
          error: error?.message || error,
        });
        return null;
      }
    }

    function readStoredDesignByKey(storageKey) {
      const key = String(storageKey || '').trim();
      if (!key) return null;

      try {
        const parsed = parseStoredDesignPayload(localStorage.getItem(key), key);
        return parsed ? { storageKey: key, parsed } : null;
      } catch (error) {
        restoreDebug('localStorage key read failed', {
          storageKey: key,
          error: error?.message || error,
        });
        return null;
      }
    }

    function latestPointerToStorageKey(pointer) {
      const value = String(pointer || '').trim();
      if (!value) return '';
      if (value === latestStorageKey) return '';
      if (value.startsWith(storagePrefix) || value.startsWith(legacyStoragePrefix)) return value;
      return canonicalStorageKey(value);
    }

    function readLatestSavedDesignState() {
      try {
        const raw = localStorage.getItem(latestStorageKey);
        if (!raw) return null;
        const trimmed = String(raw || '').trim();

        if (trimmed.startsWith('{')) {
          const legacyPayload = parseStoredDesignPayload(trimmed);
          if (!legacyPayload) return null;

          try {
            writeStoredDesignPayload(legacyPayload);
          } catch (error) {
            restoreDebug('legacy latest normalization failed', {
              designId: legacyPayload.designId,
              error: error?.name || error?.message || error,
            });
          }

          return {
            ...legacyPayload,
            storageKey: canonicalStorageKey(legacyPayload.designId),
          };
        }

        let pointer = trimmed;
        if (trimmed.startsWith('"')) {
          try {
            pointer = JSON.parse(trimmed);
          } catch (_) {
            pointer = trimmed.replace(/^"|"$/g, '');
          }
        }

        const storageKey = latestPointerToStorageKey(pointer);
        if (!storageKey) return null;
        const stored = readStoredDesignByKey(storageKey);
        return stored?.parsed || null;
      } catch (error) {
        restoreDebug('latest localStorage parse failed', {
          error: error?.message || error,
        });
        return null;
      }
    }

    function setSaveDesignStatus(message = '', type = '') {
      if (!saveDesignStatus) return;
      saveDesignStatus.textContent = message;
      saveDesignStatus.hidden = !message;
      saveDesignStatus.classList.toggle('is-warning', type === 'warning');
      saveDesignStatus.classList.toggle('is-success', type === 'success');
    }

    function updateSavedDesignControls() {
      const hasSavedDesign = Boolean(readLatestSavedDesignState());
      if (restoreSavedDesignButton) restoreSavedDesignButton.hidden = !hasSavedDesign;
      if (clearSavedDesignButton) clearSavedDesignButton.hidden = !hasSavedDesign;
      return hasSavedDesign;
    }

    function saveCurrentDesign(options = {}) {
      const includePreviews = options.includePreviews !== false;
      const result = persistDesignStateResult({ includePreviews });
      updateSavedDesignControls();

      if (options.showStatus) {
        if (!result.ok) {
          setSaveDesignStatus('Design could not be saved in this browser. Please clear browser storage or try again.', 'warning');
        } else if (result.saveLevel === 'minimal') {
          setSaveDesignStatus('Your basic layout was saved, but uploaded artwork may need to be re-uploaded.', 'warning');
        } else if (result.previewsStored) {
          setSaveDesignStatus('Design saved.', 'success');
        } else {
          setSaveDesignStatus('Your layout was saved, but uploaded artwork may need to be re-uploaded.', 'warning');
        }
      }

      return result.ok && result.previewsStored;
    }

    function scheduleAutoSave() {
      if (!autoSaveEnabled) return;
      if (autoSaveTimer) window.clearTimeout(autoSaveTimer);
      autoSaveTimer = window.setTimeout(() => {
        autoSaveTimer = 0;
        saveCurrentDesign({ includePreviews: false, showStatus: false });
      }, 700);
    }

    function savedStateRequiresReupload(parsed) {
      if (!parsed) return false;
      if (parsed.requiresReupload || parsed.previewTooLarge) return true;
      return (parsed.elements || []).some((element) => (
        element?.type === 'image'
        && !element.src
        && !element.originalSrc
        && (element.name || element.originalDesignKey || element.mockupPreviewKey || element.backgroundRemovedImageKey || element.aiImageKey)
      ));
    }

    async function restoreStoredImagePreviewsFromR2() {
      let restoredCount = 0;

      for (const element of imageElements()) {
        if (element.src) continue;
        const sourceKey = element.originalDesignKey || element.backgroundRemovedImageKey || element.aiImageKey || element.mockupPreviewKey || '';
        if (!sourceKey) continue;

        try {
          const blob = await fetchCustomDesignAssetBlob(sourceKey);
          const objectUrl = URL.createObjectURL(blob);
          restoredObjectUrls.add(objectUrl);
          element.src = objectUrl;
          if (element.originalDesignKey && !element.originalSrc) {
            element.originalSrc = objectUrl;
          }
          element.restoredFromR2 = true;
          element.restoredPreviewOnly = !element.originalDesignKey && Boolean(element.mockupPreviewKey);
          element.requiresReupload = false;
          restoredCount += 1;
        } catch (error) {
          restoreDebug('saved image preview restore failed', {
            elementId: element.id,
            sourceKey,
            error: error?.message || error,
          });
          element.requiresReupload = true;
        }
      }

      if (restoredCount > 0) {
        renderDesign();
        saveCurrentDesign({ includePreviews: false, showStatus: false });
      }

      return restoredCount;
    }

    async function restoreLatestSavedDesign() {
      const parsed = readLatestSavedDesignState();
      if (!parsed) {
        updateSavedDesignControls();
        setSaveDesignStatus('No saved custom design was found in this browser.', 'warning');
        return;
      }

      applyStoredDesignState(parsed, parsed.designId || state.designId);
      renderDesign();
      await restoreStoredImagePreviewsFromR2();
      updateSavedDesignControls();
      const requiresReupload = savedStateRequiresReupload(parsed) && imageElements().some((element) => !element.src && !element.originalDesignKey && !element.mockupPreviewKey);
      if (requiresReupload && uploadError) {
        uploadError.textContent = 'Some uploaded artwork must be re-uploaded before checkout.';
      }
      setSaveDesignStatus(
        requiresReupload ? 'Some uploaded artwork must be re-uploaded before checkout.' : 'Saved design restored.',
        requiresReupload ? 'warning' : 'success'
      );
    }

    function clearLatestSavedDesign() {
      const parsed = readLatestSavedDesignState();
      try {
        if (parsed?.designId) {
          const keysToRemove = new Set([
            canonicalStorageKey(parsed.designId),
            ...(parsed.storageKey ? [parsed.storageKey] : []),
            ...storageKeys(parsed.designId),
          ]);
          keysToRemove.forEach((key) => localStorage.removeItem(key));
        }
        localStorage.removeItem(latestStorageKey);
      } catch (_) {
        // If storage is blocked, the visible controls still update for the current session.
      }
      updateSavedDesignControls();
      setSaveDesignStatus('Saved design cleared.', 'success');
    }

    function hasRestorableImageSource(element) {
      return element?.type === 'image' && Boolean(element.src) && !String(element.src).startsWith('blob:');
    }

    function missingImageSources() {
      return imageElements().length === 0 || imageElements().some((element) => !hasRestorableImageSource(element));
    }

    function applyStoredDesignState(parsed, designId) {
      state.designId = parsed.designId || designId;
      state.activeView = fallbackView(parsed.activeView || 'front', 'stored-design-active-view');
      state.elements = migrateLegacyState(parsed, designId);
      ensureActiveStateAllowed('stored-design-elements');
      state.activeElementId = parsed.activeElementId || state.elements[0]?.id || '';

      if (state.activeElementId && !state.elements.some((element) => element.id === state.activeElementId && (!element.view || element.view === state.activeView))) {
        state.activeElementId = state.elements.find((element) => !element.view || element.view === state.activeView)?.id || '';
      }

      if (parsed.shirtColor) setSelectedShirtColor(parsed.shirtColor);
      if (parsed.placementsByView && typeof parsed.placementsByView === 'object') {
        availableViewKeys.forEach((view) => {
          const placement = canonicalPlacementValue(parsed.placementsByView[view]);
          if (availablePlacementOptions(view).includes(placement)) {
            state.placementsByView[view] = placement;
          }
        });
      } else if (parsed.printPlacement) {
        state.placementsByView[state.activeView] = canonicalPlacementValue(parsed.printPlacement);
      }
      syncPlacementOptions(state.placementsByView[state.activeView] || parsed.printPlacement);
      state.designNotes = {
        front: String(parsed.designNotes?.front || '').trim(),
        back: String(parsed.designNotes?.back || '').trim(),
        extra: String(parsed.designNotes?.extra || '').trim(),
      };
      syncDesignNotesToFields();

      sizes.forEach((size) => {
        if (sizeInputs[size]) {
          sizeInputs[size].value = parsed.sizeQuantities?.[size] || 0;
        }
      });
    }

    function normalizeCartProperties(properties) {
      if (!properties) return {};

      if (Array.isArray(properties)) {
        return properties.reduce((normalized, property) => {
          const key = String(property.name || property.key || '').trim();
          if (!key || property.value === null || property.value === undefined) return normalized;
          normalized[key] = String(property.value);
          return normalized;
        }, {});
      }

      if (typeof properties === 'object') {
        return Object.entries(properties).reduce((normalized, [key, value]) => {
          if (!key || value === null || value === undefined) return normalized;
          normalized[key] = String(value);
          return normalized;
        }, {});
      }

      return {};
    }

    function cartLineDesignId(item) {
      const properties = normalizeCartProperties(item?.properties);
      return properties['Design ID'] || properties._inknfiber_parent_design_id || '';
    }

    function cartLineMatchesDesign(item, designId) {
      return !isCustomDesignFeeCartItem(item) && cartLineDesignId(item) === designId;
    }

    function viewKeyFromCartValue(value) {
      const normalizedValue = normalize(value);
      let restoredView = 'front';
      if (normalizedValue.includes('BACK')) restoredView = 'back';
      if (normalizedValue.includes('LEFT') && normalizedValue.includes('SLEEVE')) restoredView = 'left-sleeve';
      if (normalizedValue.includes('RIGHT') && normalizedValue.includes('SLEEVE')) restoredView = 'right-sleeve';
      return fallbackView(restoredView, 'cart-line-view');
    }

    function parseTransformNumber(value, fallback) {
      const parsed = Number(String(value || '').replace(/[^0-9.-]/g, ''));
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    function parseSizeBreakdown(value) {
      return String(value || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .reduce((quantities, part) => {
          const match = part.match(/^([A-Za-z0-9]+)\s*-\s*(\d+)/);
          if (!match) return quantities;
          const size = sizes.find((candidate) => compact(candidate) === compact(match[1]));
          if (size) quantities[size] = Number(match[2] || 0);
          return quantities;
        }, {});
    }

    function restoreSizesFromCartLines(cartLines) {
      const quantities = {};

      cartLines.forEach((item) => {
        const properties = normalizeCartProperties(item.properties);
        const size = sizes.find((candidate) => compact(candidate) === compact(properties.Size || ''));
        if (size) quantities[size] = (quantities[size] || 0) + Number(item.quantity || 0);

        Object.entries(parseSizeBreakdown(properties['Size breakdown'])).forEach(([breakdownSize, quantity]) => {
          quantities[breakdownSize] = (quantities[breakdownSize] || 0) + Number(quantity || 0);
        });
      });

      sizes.forEach((size) => {
        if (sizeInputs[size] && quantities[size] !== undefined) {
          sizeInputs[size].value = clamp(Number(quantities[size] || 0), 0, 99);
        }
      });
    }

    function cartRestoreData(cartLines) {
      const primaryLine = cartLines[0] || null;
      const properties = normalizeCartProperties(primaryLine?.properties);

      return {
        properties,
        designId: properties['Design ID'] || editDesignId || state.designId,
        originalDesignKey: properties['Original Design Key'] || '',
        mockupPreviewKey: properties['Mockup Preview Key'] || '',
        uploadedFileName: properties['Uploaded file name'] || imageElements()[0]?.name || 'Restored artwork',
        shirtColor: properties['Shirt color'] || '',
        view: viewKeyFromCartValue(properties['Print view'] || properties.View || ''),
        placement: properties['Print placement'] || properties.Placement || '',
        x: parseTransformNumber(properties['_Design position X'], 50),
        y: parseTransformNumber(properties['_Design position Y'], 50),
        scale: parseTransformNumber(properties['_Design size/scale'], 42),
        rotation: parseTransformNumber(properties['_Design rotation'], 0),
        designNotes: {
          front: properties['_Design notes front'] || '',
          back: properties['_Design notes back'] || '',
          extra: properties['_Design notes extra'] || properties['Design notes'] || '',
        },
      };
    }

    function localElementRestoreData() {
      const primaryImage = imageElements()[0];
      if (!primaryImage?.originalDesignKey && !primaryImage?.mockupPreviewKey) return null;

      return {
        properties: {},
        designId: state.designId,
        originalDesignKey: primaryImage.originalDesignKey || '',
        mockupPreviewKey: primaryImage.mockupPreviewKey || '',
        uploadedFileName: primaryImage.name || 'Restored artwork',
        shirtColor: selectedColorData().label,
        view: primaryImage.view || state.activeView || 'front',
        placement: placementSelect?.value || 'Left Chest',
        x: parseTransformNumber(primaryImage.x, 50),
        y: parseTransformNumber(primaryImage.y, 50),
        scale: parseTransformNumber(primaryImage.scale, 42),
        rotation: parseTransformNumber(primaryImage.rotation, 0),
      };
    }

    function customDesignAssetUrl(key) {
      if (!key) return '';
      const url = new URL(customDesignAssetEndpoint, window.location.origin);
      url.searchParams.set('key', key);
      return url.toString();
    }

    function absoluteWorkerUrl(pathOrUrl) {
      return new URL(pathOrUrl || '/', backendBaseUrl || window.location.origin).toString();
    }

    function supportRequestSuffix(body) {
      const requestId = String(body?.request_id || body?.requestId || '').trim();
      return requestId ? ` Request ID: ${requestId}` : '';
    }

    function friendlyAiError(body, fallback) {
      const raw = String(body?.user_error || body?.error || body?.message || fallback || '').trim();
      const lower = raw.toLowerCase();
      const likelyPolicyBlock = ['copyright', 'trademark', 'policy', 'moderation', 'safety', 'blocked', 'rejected', 'not allowed', 'disallowed']
        .some((needle) => lower.includes(needle));
      const message = likelyPolicyBlock
        ? 'This prompt could not be processed. Try describing the design more generically without specific copyrighted character names.'
        : raw || fallback || 'The AI request could not be completed. Please try again.';
      return `${message}${supportRequestSuffix(body)}`;
    }

    function setAiStatus(message = '', type = 'info') {
      if (!aiStatus) return;
      aiStatus.textContent = message;
      aiStatus.hidden = !message;
      aiStatus.classList.toggle('is-error', type === 'error');
    }

    function setBackgroundRemovalStatus(message = '', type = 'info') {
      if (!backgroundRemovalStatus) return;
      backgroundRemovalStatus.textContent = message;
      backgroundRemovalStatus.hidden = !message && !aiBackgroundRemoverEnabled;
      backgroundRemovalStatus.classList.toggle('is-error', type === 'error');
    }

    async function requestArtworkBackgroundRemoval(file) {
      if (!aiBackgroundRemoverEnabled) throw new Error('AI background removal is not enabled.');
      if (!file) throw new Error('Re-upload this artwork before removing the background.');

      const maxAiFileBytes = Math.max(1, artworkCheckerOptions.maxFileSizeMb || 25) * 1024 * 1024;
      if (file.size > maxAiFileBytes) {
        throw new Error('Background removal skipped because this file is larger than the AI upload limit. The original file was kept.');
      }

      const cacheKey = aiFileCacheKey(file, aiPerformanceMode, 'custom-background');
      const cached = aiSessionCache.backgrounds.get(cacheKey);
      if (cached?.blob && cached?.imageKey) {
        const objectUrl = URL.createObjectURL(cached.blob);
        restoredObjectUrls.add(objectUrl);
        setBackgroundRemovalStatus(`Using previous background removal for ${file.name || 'artwork'}.`, 'info');
        return {
          src: objectUrl,
          backgroundRemoved: true,
          backgroundRemovedImageKey: cached.imageKey || '',
          backgroundRemovedImageUrl: cached.imageUrl || '',
          backgroundRemovedFileName: cached.fileName || 'background-removed-artwork.png',
          backgroundRemovedMimeType: cached.mimeType || cached.blob.type || 'image/png',
        };
      }

      setBackgroundRemovalStatus('Preparing image...', 'info');
      const optimizedImage = await optimizeImageForAi(file, aiBackgroundMaxDimension);
      setBackgroundRemovalStatus('Sending to AI...', 'info');

      const formData = new FormData();
      formData.set('design_id', state.designId);
      formData.set('file_name', file.name || 'uploaded-artwork.png');
      formData.set('performance_mode', aiPerformanceMode);
      formData.set('optimized_for_ai', optimizedImage.optimized ? 'true' : 'false');
      formData.set('image', optimizedImage.file || file);

      const response = await fetch(aiBackgroundRemoveEndpoint, {
        method: 'POST',
        body: formData,
      });
      const body = await response.json().catch(() => null);

      if (!response.ok || !body?.ok || !body?.image_key) {
        throw new Error(friendlyAiError(body, 'Background removal failed. The original file was kept.'));
      }

      setBackgroundRemovalStatus('Almost done...', 'info');
      const imageUrl = absoluteWorkerUrl(body.image_url || `/custom-design-preview?key=${encodeURIComponent(body.image_key || '')}`);
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) throw new Error(`Background-removed image request failed with ${imageResponse.status}${supportRequestSuffix(body)}`);

      const blob = await imageResponse.blob();
      if (!allowedTypes.includes(blob.type || body.mime_type || 'image/png')) {
        throw new Error(`Background removal returned an unsupported image type. The original file was kept.${supportRequestSuffix(body)}`);
      }

      const objectUrl = URL.createObjectURL(blob);
      restoredObjectUrls.add(objectUrl);
      aiSessionCache.backgrounds.set(cacheKey, {
        blob,
        imageKey: body.image_key || '',
        imageUrl,
        fileName: body.file_name || 'background-removed-artwork.png',
        mimeType: body.mime_type || blob.type || 'image/png',
      });
      setBackgroundRemovalStatus(`Background removed for ${file.name || 'artwork'}. Original upload is preserved.`, 'info');

      if (designerDebug) {
        transformDebugLog('background removal success', {
          fileName: file.name,
          imageKey: body.image_key,
          imagePreviewUrl: imageUrl,
        });
      }

      return {
        src: objectUrl,
        backgroundRemoved: true,
        backgroundRemovedImageKey: body.image_key || '',
        backgroundRemovedImageUrl: imageUrl,
        backgroundRemovedFileName: body.file_name || 'background-removed-artwork.png',
        backgroundRemovedMimeType: body.mime_type || blob.type || 'image/png',
      };
    }

    function fileFromDataUrl(dataUrl, fileName = 'uploaded-artwork.png') {
      const blob = dataUrlToBlob(dataUrl);
      if (!blob) return null;
      return new File([blob], fileName, { type: blob.type || 'image/png' });
    }

    async function removeBackgroundForElement(elementId) {
      const element = elementById(elementId);
      if (!element || element.type !== 'image') return;
      if (element.backgroundRemovalStatus === 'processing') return;

      const sourceFile = originalUploadFiles.get(element.id) || fileFromDataUrl(element.originalSrc || '', element.name || 'uploaded-artwork.png');
      if (!sourceFile) {
        element.backgroundRemovalStatus = 'error';
        element.backgroundRemovalMessage = 'Re-upload this artwork before removing the background.';
        renderDesign();
        return;
      }

      element.backgroundRemovalStatus = 'processing';
      element.backgroundRemovalMessage = 'Preparing image...';
      renderDesign();

      try {
        const result = await requestArtworkBackgroundRemoval(sourceFile);
        if (!element.originalSrc && element.src) element.originalSrc = element.src;
        element.src = result.src;
        element.backgroundRemoved = true;
        element.backgroundRemovedImageKey = result.backgroundRemovedImageKey;
        element.backgroundRemovedImageUrl = result.backgroundRemovedImageUrl;
        element.backgroundRemovedFileName = result.backgroundRemovedFileName;
        element.backgroundRemovedMimeType = result.backgroundRemovedMimeType;
        element.backgroundRemovalStatus = 'done';
        element.backgroundRemovalMessage = 'Background removed. Original upload is preserved.';
        setActiveElement(element.id, { reason: 'background-removed', silent: true });
        saveCurrentDesign({ includePreviews: true, showStatus: false });
      } catch (error) {
        element.backgroundRemovalStatus = 'error';
        element.backgroundRemovalMessage = error?.message || 'Background removal failed. The original file was kept.';
        setBackgroundRemovalStatus(element.backgroundRemovalMessage, 'error');

        if (designerDebug) {
          transformDebugLog('background removal failure', {
            fileName: element.name,
            error: error?.message || error,
          });
        }
      } finally {
        renderDesign();
      }
    }

    function useOriginalForElement(elementId) {
      const element = elementById(elementId);
      if (!element || element.type !== 'image' || !element.originalSrc) return;
      if (element.src && restoredObjectUrls.has(element.src)) {
        try {
          URL.revokeObjectURL(element.src);
        } catch (_) {
          // Ignore cleanup failures for stale object URLs.
        }
        restoredObjectUrls.delete(element.src);
      }
      element.src = element.originalSrc;
      element.backgroundRemoved = false;
      element.backgroundRemovedImageKey = '';
      element.backgroundRemovedImageUrl = '';
      element.backgroundRemovedFileName = '';
      element.backgroundRemovedMimeType = '';
      element.backgroundRemovalStatus = '';
      element.backgroundRemovalMessage = 'Using original artwork.';
      setBackgroundRemovalStatus('Original artwork restored for this image.', 'info');
      saveCurrentDesign({ includePreviews: true, showStatus: false });
      renderDesign();
    }

    function syncAiPreview() {
      if (!aiPreview || !aiPreviewImage || !aiAddButton) return;
      const hasDesign = Boolean(generatedAiDesign?.image_url);
      aiPreview.hidden = !hasDesign;
      aiPreviewImage.src = hasDesign ? generatedAiDesign.image_url : '';
      aiAddButton.disabled = !hasDesign;
    }

    function getAiPromptOptions() {
      return {
        style: aiDesignStyleSelect?.value || 'Clean Vector',
        outputType: aiOutputTypeSelect?.value || 'T-Shirt Design',
        includeText: Boolean(aiIncludeTextInput?.checked),
      };
    }

    function syncAiPromptWarning(message = '') {
      if (!aiPromptWarning) return;
      const warning = message || aiPromptHelper.getAiPromptSafetyWarning(aiPromptInput?.value || '');
      aiPromptWarning.textContent = warning;
      aiPromptWarning.hidden = !warning;
    }

    async function generateAiDesign() {
      if (!aiPhotoshootEnabled) return;
      if (!aiPromptInput || !aiGenerateButton) return;

      const promptRequest = aiPromptHelper.buildAiDesignPrompt(aiPromptInput.value, getAiPromptOptions());
      syncAiPromptWarning(promptRequest.warning);
      if (!promptRequest.originalPrompt) {
        setAiStatus('Enter a prompt before generating a design.', 'error');
        return;
      }
      const prompt = promptRequest.enhancedPrompt;

      generatedAiDesign = null;
      syncAiPreview();
      const cacheKey = aiPromptCacheKey(prompt, aiPerformanceMode, 'custom-ai-design');
      const cachedDesign = aiSessionCache.designs.get(cacheKey);
      if (cachedDesign?.image_key && cachedDesign?.image_url) {
        generatedAiDesign = { ...cachedDesign, ...promptRequest, prompt };
        syncAiPreview();
        setAiStatus('Using previous AI design for this prompt and mode.', 'info');
        return;
      }

      aiGenerateButton.disabled = true;
      aiGenerateButton.textContent = 'Generating design...';
      setAiStatus('Sending to AI...', 'info');

      if (designerDebug) {
        transformDebugLog('AI prompt submitted', {
          prompt,
          designId: state.designId,
          endpoint: aiDesignGenerateEndpoint,
        });
      }

      try {
        const response = await fetch(aiDesignGenerateEndpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt,
            design_id: state.designId,
            transparent_background: true,
            performance_mode: aiPerformanceMode,
          }),
        });
        setAiStatus('Generating design...', 'info');
        const body = await response.json().catch(() => null);

        if (!response.ok || !body?.ok) {
          throw new Error(friendlyAiError(body, 'Unable to generate AI design. Please try again.'));
        }

        generatedAiDesign = {
          image_key: body.image_key,
          image_url: absoluteWorkerUrl(body.image_url || `/custom-design-preview?key=${encodeURIComponent(body.image_key || '')}`),
          file_name: body.file_name || 'ai-generated-design.png',
          mime_type: body.mime_type || 'image/png',
          prompt,
          originalPrompt: promptRequest.originalPrompt,
          enhancedPrompt: prompt,
          aiPromptStyle: promptRequest.style,
          aiPromptOutputType: promptRequest.outputType,
          aiPromptIncludeText: promptRequest.includeText,
        };
        aiSessionCache.designs.set(cacheKey, { ...generatedAiDesign });
        syncAiPreview();
        setAiStatus('Almost done...', 'info');
        window.setTimeout(() => {
          if (generatedAiDesign?.image_key === body.image_key) {
            setAiStatus('AI design generated. Review it, then add it to your design.', 'info');
          }
        }, 250);

        if (designerDebug) {
          transformDebugLog('AI response success', {
            imageKey: generatedAiDesign.image_key,
            imagePreviewUrl: generatedAiDesign.image_url,
            mimeType: generatedAiDesign.mime_type,
          });
        }
      } catch (error) {
        setAiStatus(error?.message || 'Unable to generate AI design. Please try again.', 'error');

        if (designerDebug) {
          transformDebugLog('AI response failure', {
            error: error?.message || error,
          });
        }
      } finally {
        aiGenerateButton.disabled = false;
        aiGenerateButton.textContent = 'Generate AI Design';
        syncOpenAccordionPanelHeights();
      }
    }

    async function addGeneratedAiDesignToLayer() {
      if (!aiPhotoshootEnabled) return;
      if (!generatedAiDesign?.image_url || !generatedAiDesign?.image_key) return;
      if (aiAddButton) aiAddButton.disabled = true;
      setAiStatus('Adding AI design to mockup...', 'info');

      try {
        const response = await fetch(generatedAiDesign.image_url);
        if (!response.ok) throw new Error(`Generated image request failed with ${response.status}`);
        const blob = await response.blob();
        if (!allowedTypes.includes(blob.type || generatedAiDesign.mime_type)) {
          throw new Error('Generated image is not a supported image type.');
        }

        const objectUrl = URL.createObjectURL(blob);
        restoredObjectUrls.add(objectUrl);
        const layerId = addElement({
          type: 'image',
          name: generatedAiDesign.file_name || 'AI generated design.png',
          src: objectUrl,
          scale: 42,
          aiGenerated: true,
          aiPrompt: generatedAiDesign.prompt,
          aiOriginalPrompt: generatedAiDesign.originalPrompt || '',
          aiEnhancedPrompt: generatedAiDesign.enhancedPrompt || generatedAiDesign.prompt || '',
          aiPromptStyle: generatedAiDesign.aiPromptStyle || 'Clean Vector',
          aiPromptOutputType: generatedAiDesign.aiPromptOutputType || 'T-Shirt Design',
          aiPromptIncludeText: Boolean(generatedAiDesign.aiPromptIncludeText),
          aiImageKey: generatedAiDesign.image_key,
          originalDesignKey: generatedAiDesign.image_key,
          aiImageUrl: generatedAiDesign.image_url,
          aiFileName: generatedAiDesign.file_name,
          aiMimeType: generatedAiDesign.mime_type,
        });

        setAiStatus('AI design added to the mockup.', 'info');

        if (designerDebug) {
          transformDebugLog('AI layer added', {
            layerId,
            imageKey: generatedAiDesign.image_key,
            imagePreviewUrl: generatedAiDesign.image_url,
            aiDesignCount: aiDesignCount(),
          });
        }
      } catch (error) {
        setAiStatus(error?.message || 'Unable to add AI design to the mockup.', 'error');
      } finally {
        if (aiAddButton) aiAddButton.disabled = false;
        syncOpenAccordionPanelHeights();
      }
    }

    async function fetchCustomDesignAssetBlob(key, directUrl = '') {
      const fetchUrl = directUrl || customDesignAssetUrl(key);
      restoreDebug('fetching R2 design asset', {
        path: directUrl ? '/reorder/file' : '/custom-design-preview',
        hasKey: Boolean(key),
      });

      const response = await fetch(fetchUrl);
      if (!response.ok) {
        throw new Error(`Saved artwork request failed with ${response.status}`);
      }

      const blob = await response.blob();
      if (!allowedTypes.includes(blob.type)) {
        throw new Error('Saved artwork is not a supported image type.');
      }

      restoreDebug('restored R2 blob', {
        size: blob.size,
        type: blob.type,
      });

      return blob;
    }

    function restoredFileName(name, key, blob) {
      const cleanName = cleanCartPropertyValue(name, 120);
      if (cleanName && allowedExtensions.some((extension) => cleanName.toLowerCase().endsWith(extension))) {
        return cleanName;
      }

      const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
      const keyName = String(key || '').split('/').pop() || `restored-artwork.${extension}`;
      return keyName.includes('.') ? keyName : `${keyName}.${extension}`;
    }

    function upsertRestoredImageLayer({ blob, sourceKey, originalDesignKey, mockupPreviewKey, uploadedFileName, transform, previewOnly = false }) {
      const objectUrl = URL.createObjectURL(blob);
      restoredObjectUrls.add(objectUrl);

      let element = imageElements()[0] || null;
      if (!element) {
        element = {
          id: generateId('element'),
          type: 'image',
          name: uploadedFileName,
          content: '',
          src: objectUrl,
          view: fallbackView(transform.view, 'r2-restored-layer'),
          x: transform.x,
          y: transform.y,
          scale: transform.scale,
          rotation: transform.rotation,
          restoredFromR2: true,
          restoredPreviewOnly: previewOnly,
          originalDesignKey,
          mockupPreviewKey,
        };
        state.elements.push(element);
      } else {
        element.name = element.name || uploadedFileName;
        element.src = objectUrl;
        element.view = fallbackView(element.view || transform.view, 'r2-restored-layer');
        element.x = Number.isFinite(Number(element.x)) ? element.x : transform.x;
        element.y = Number.isFinite(Number(element.y)) ? element.y : transform.y;
        element.scale = Number.isFinite(Number(element.scale)) ? element.scale : transform.scale;
        element.rotation = Number.isFinite(Number(element.rotation)) ? element.rotation : transform.rotation;
        element.restoredFromR2 = true;
        element.restoredPreviewOnly = previewOnly;
        element.originalDesignKey = originalDesignKey;
        element.mockupPreviewKey = mockupPreviewKey;
      }

      if (!previewOnly && originalDesignKey) {
        element.originalDesignKey = originalDesignKey;
      }

      if (mockupPreviewKey) {
        element.mockupPreviewKey = mockupPreviewKey;
      }

      state.activeElementId = element.id;
      state.activeView = fallbackView(element.view || transform.view || state.activeView, 'r2-restored-active-view');

      restoreDebug('restored image layer', {
        restoredLayerId: element.id,
        sourceKey,
        originalDesignKey: Boolean(originalDesignKey),
        mockupPreviewKey: Boolean(mockupPreviewKey),
        restoredTransform: {
          view: element.view,
          x: element.x,
          y: element.y,
          scale: element.scale,
          rotation: element.rotation,
        },
      });

      return element;
    }

    async function restoreArtworkFromR2(restoreData) {
      const sourceKey = restoreData.originalDesignKey || restoreData.mockupPreviewKey;
      const previewOnly = !restoreData.originalDesignKey && Boolean(restoreData.mockupPreviewKey);

      if (!sourceKey && !restoreData.imageUrl) return false;

      const blob = await fetchCustomDesignAssetBlob(sourceKey, restoreData.imageUrl || '');
      const fileName = restoredFileName(restoreData.uploadedFileName, sourceKey, blob);
      const element = upsertRestoredImageLayer({
        blob,
        sourceKey,
        originalDesignKey: restoreData.originalDesignKey,
        mockupPreviewKey: restoreData.mockupPreviewKey,
        uploadedFileName: fileName,
        previewOnly,
        transform: restoreData,
      });

      if (!previewOnly) {
        uploadError.textContent = 'Artwork restored from your saved design.';
      } else {
        uploadError.textContent = 'Preview restored from your saved design. Please reupload the original artwork before checkout.';
      }

      persistDesignState();
      renderDesign();
      return Boolean(element);
    }

    async function fetchCartRestoreLines(designId) {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Cart restore request failed with ${response.status}`);

      const cart = await response.json();
      const lines = (cart.items || []).filter((item) => cartLineMatchesDesign(item, designId));

      restoreDebug('cart.js restore lookup', {
        editDesignId: designId,
        matchingLineItemFound: lines.length > 0,
        matchingLineCount: lines.length,
      });

      return lines;
    }

    async function restoreDesignState(designId) {
      if (!designId) return;

      restoreDebug('restore start', { editDesignId: designId });
      const stored = readStoredDesignState(designId);
      let localStorageFound = false;

      if (stored?.parsed) {
        localStorageFound = true;
        applyStoredDesignState(stored.parsed, designId);
        renderDesign();
      }

      restoreDebug('localStorage restore result', {
        editDesignId: designId,
        localStorageFound,
        missingImageLayer: missingImageSources(),
      });

      let cartLines = [];
      try {
        cartLines = await fetchCartRestoreLines(designId);
      } catch (error) {
        restoreDebug('cart.js restore failed', {
          editDesignId: designId,
          error: error?.message || error,
        });
      }

      if (cartLines.length) {
        const restoreData = cartRestoreData(cartLines);
        lastCustomDesignUpload = {
          design_id: restoreData.designId,
          mockup_preview_key: restoreData.mockupPreviewKey,
          original_design_key: restoreData.originalDesignKey,
          uploaded_file_name: restoreData.uploadedFileName,
        };

        if (restoreData.shirtColor) setSelectedShirtColor(restoreData.shirtColor);
        if (restoreData.view) state.activeView = fallbackView(restoreData.view, 'cart-restore-active-view');
        state.designNotes = {
          front: String(restoreData.designNotes?.front || '').trim(),
          back: String(restoreData.designNotes?.back || '').trim(),
          extra: String(restoreData.designNotes?.extra || '').trim(),
        };
        syncDesignNotesToFields();
        syncPlacementOptions(restoreData.placement);
        restoreSizesFromCartLines(cartLines);

        restoreDebug('cart line restore data', {
          originalDesignKeyFound: Boolean(restoreData.originalDesignKey),
          mockupPreviewKeyFound: Boolean(restoreData.mockupPreviewKey),
          uploadedFileName: restoreData.uploadedFileName,
          restoredTransform: {
            view: restoreData.view,
            placement: restoreData.placement,
            x: restoreData.x,
            y: restoreData.y,
            scale: restoreData.scale,
            rotation: restoreData.rotation,
          },
        });

        const firstImage = imageElements()[0];
        if (firstImage) {
          if (restoreData.originalDesignKey) firstImage.originalDesignKey = restoreData.originalDesignKey;
          if (restoreData.mockupPreviewKey) firstImage.mockupPreviewKey = restoreData.mockupPreviewKey;
          if (restoreData.uploadedFileName) firstImage.name = restoreData.uploadedFileName;
        }

        if (missingImageSources()) {
          try {
            const restored = await restoreArtworkFromR2(restoreData);
            if (restored) {
              setStatus('Artwork restored from your saved design.', 'success');
              return;
            }
          } catch (error) {
            restoreDebug('R2 artwork restore failed', {
              editDesignId: designId,
              error: error?.message || error,
            });
            uploadError.textContent = 'Your saved artwork could not be restored. Please reupload the file.';
            setStatus('Your saved artwork could not be restored. Please reupload the file.', 'warning');
            renderDesign();
            return;
          }
        }

        persistDesignState();
        setStatus('Saved design loaded. You can adjust it and add the updated order to cart.', 'success');
        uploadError.textContent = 'Artwork restored from your saved design.';
        renderDesign();
        return;
      }

      if (localStorageFound && !missingImageSources()) {
        setStatus('Saved design loaded from this browser. You can adjust it and add the updated order to cart.', 'success');
        uploadError.textContent = 'Artwork restored from your saved design.';
        renderDesign();
        return;
      }

      const localRestoreData = localStorageFound ? localElementRestoreData() : null;
      if (localRestoreData && missingImageSources()) {
        try {
          const restored = await restoreArtworkFromR2(localRestoreData);
          if (restored) {
            setStatus('Artwork restored from your saved design.', 'success');
            return;
          }
        } catch (error) {
          restoreDebug('local R2 key artwork restore failed', {
            editDesignId: designId,
            error: error?.message || error,
          });
        }
      }

      setStatus('Your saved artwork could not be restored. Please reupload the file.', 'warning');
      uploadError.textContent = 'Your saved artwork could not be restored. Please reupload the file.';
      renderDesign();
    }

    function reorderRestoreEndpoint() {
      const origin = new URL(customDesignUploadEndpoint, window.location.origin).origin;
      const url = new URL('/reorder/restore', origin);
      url.searchParams.set('reorder_id', reorderId || '');
      url.searchParams.set('token', reorderToken || '');
      return url.toString();
    }

    function restoreSizesFromBreakdown(sizeBreakdown, totalQuantity) {
      const quantities = parseSizeBreakdown(sizeBreakdown);
      const hasBreakdown = Object.keys(quantities).length > 0;

      sizes.forEach((size) => {
        if (!sizeInputs[size]) return;
        sizeInputs[size].value = hasBreakdown ? (quantities[size] || 0) : 0;
      });

      if (!hasBreakdown && Number(totalQuantity) > 0) {
        const fallbackSize = sizeInputs.M ? 'M' : sizes.find((size) => sizeInputs[size]);
        if (fallbackSize) sizeInputs[fallbackSize].value = clamp(Number(totalQuantity) || 0, 0, 99);
        return false;
      }

      return hasBreakdown;
    }

    function reorderElementRestoreData(data, element) {
      return {
        originalDesignKey: element.original_design_key || '',
        mockupPreviewKey: element.mockup_preview_key || '',
        uploadedFileName: element.file_name || data.uploaded_file_name || 'Restored artwork',
        imageUrl: element.image_url || '',
        shirtColor: data.shirt_color || '',
        view: fallbackView(element.view || data.view || 'front', 'reorder-restore-view'),
        placement: element.placement || data.placement || '',
        x: parseTransformNumber(element.x, 50),
        y: parseTransformNumber(element.y, 50),
        scale: parseTransformNumber(element.scale, 42),
        rotation: parseTransformNumber(element.rotation, 0),
      };
    }

    async function restoreFromReorderLink() {
      if (!reorderId || !reorderToken) return;

      restoreDebug('reorder restore start', { reorderId });
      setStatus('Loading reorder details...', 'info');

      try {
        const response = await fetch(reorderRestoreEndpoint(), { headers: { Accept: 'application/json' } });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.ok) {
          throw new Error(data?.error || 'Unable to load this reorder.');
        }
        if (data.source_type !== 'custom_design') {
          throw new Error('This reorder link is not for Custom Designs.');
        }

        state.designId = generateId('reorder-design');
        state.elements = [];
        state.activeElementId = '';
        if (data.shirt_color) setSelectedShirtColor(data.shirt_color);
        state.activeView = fallbackView(data.view || 'front', 'reorder-restore-active-view');
        state.placementsByView[state.activeView] = canonicalPlacementValue(data.placement || 'Full Front');
        syncPlacementOptions(state.placementsByView[state.activeView]);
        const restoredSizesExactly = restoreSizesFromBreakdown(data.size_breakdown || '', data.total_quantity);

        const imageElement = Array.isArray(data.elements)
          ? data.elements.find((element) => element?.type === 'image' && element.image_url)
          : null;

        if (imageElement) {
          const restoreData = reorderElementRestoreData(data, imageElement);
          lastCustomDesignUpload = {
            design_id: data.design_id || '',
            mockup_preview_key: restoreData.mockupPreviewKey,
            original_design_key: restoreData.originalDesignKey,
            uploaded_file_name: restoreData.uploadedFileName,
          };
          await restoreArtworkFromR2(restoreData);
        }

        const warnings = Array.isArray(data.warnings) ? data.warnings.filter(Boolean) : [];
        if (!restoredSizesExactly && Number(data.total_quantity) > 0) {
          warnings.push('Size breakdown was incomplete, so quantity was placed on a fallback size. Please review sizes before ordering.');
        }
        const warningText = warnings.length ? ` ${warnings.join(' ')}` : '';
        uploadError.textContent = warnings.length
          ? 'Some design details could not be restored. Please review before ordering.'
          : 'Reorder loaded. Review your design before adding to cart.';
        setStatus(`Reorder loaded. Review your design before adding to cart.${warningText}`, warnings.length ? 'warning' : 'success');
        persistDesignState();
        renderDesign();
      } catch (error) {
        restoreDebug('reorder restore failed', {
          reorderId,
          error: error?.message || error,
        });
        uploadError.textContent = 'This reorder could not be restored. Please reupload your artwork or contact InkNfiber.';
        setStatus(error?.message || 'This reorder could not be restored. Please reupload your artwork or contact InkNfiber.', 'warning');
        renderDesign();
      }
    }

    function elementSummary() {
      return state.elements.map((element) => {
        const source = element.aiGenerated ? ' ai-generated' : '';
        return `${element.type}:${element.name || element.content}${source} ${element.view} x${round(element.x)} y${round(element.y)} scale${round(element.scale)} rotate${round(element.rotation)}`;
      }).join(' | ');
    }

    function loadCanvasImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        if (!String(src || '').startsWith('data:')) {
          image.crossOrigin = 'anonymous';
        }
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
    }

    function canvasToBlob(canvas, type = 'image/png', quality = 0.92) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Unable to generate mockup preview image.'));
        }, type, quality);
      });
    }

    function parseHexColor(hex) {
      const normalizedHex = String(hex || '').trim().replace('#', '');
      const expandedHex = normalizedHex.length === 3
        ? normalizedHex.split('').map((character) => character + character).join('')
        : normalizedHex;
      const parsed = Number.parseInt(expandedHex, 16);

      if (!Number.isFinite(parsed) || expandedHex.length !== 6) {
        return { r: 17, g: 17, b: 17 };
      }

      return {
        r: (parsed >> 16) & 255,
        g: (parsed >> 8) & 255,
        b: parsed & 255,
      };
    }

    function colorLuminance(color) {
      return (0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b) / 255;
    }

    function containedImageRect(image, canvas) {
      const sourceWidth = image.naturalWidth || image.width || canvas.width;
      const sourceHeight = image.naturalHeight || image.height || canvas.height;
      const scale = Math.min(canvas.width / Math.max(sourceWidth, 1), canvas.height / Math.max(sourceHeight, 1));
      const width = sourceWidth * scale;
      const height = sourceHeight * scale;

      return {
        x: (canvas.width - width) / 2,
        y: (canvas.height - height) / 2,
        width,
        height,
      };
    }

    function drawRoundedRect(context, x, y, width, height, radius) {
      const nextRadius = Math.min(radius, width / 2, height / 2);
      context.beginPath();
      context.moveTo(x + nextRadius, y);
      context.lineTo(x + width - nextRadius, y);
      context.quadraticCurveTo(x + width, y, x + width, y + nextRadius);
      context.lineTo(x + width, y + height - nextRadius);
      context.quadraticCurveTo(x + width, y + height, x + width - nextRadius, y + height);
      context.lineTo(x + nextRadius, y + height);
      context.quadraticCurveTo(x, y + height, x, y + height - nextRadius);
      context.lineTo(x, y + nextRadius);
      context.quadraticCurveTo(x, y, x + nextRadius, y);
      context.closePath();
    }

    function drawMockupShirt(context, canvas) {
      const color = selectedColorData().color || DEFAULT_BLACK_HEX;
      const bodyX = canvas.width * 0.34;
      const bodyY = canvas.height * 0.18;
      const bodyWidth = canvas.width * 0.32;
      const bodyHeight = canvas.height * 0.68;
      const sleeveWidth = canvas.width * 0.18;
      const sleeveHeight = canvas.height * 0.24;
      const sleeveY = canvas.height * 0.25;

      context.fillStyle = color;
      context.strokeStyle = 'rgba(15, 23, 42, 0.16)';
      context.lineWidth = 3;

      context.save();
      context.translate(bodyX - sleeveWidth * 0.58, sleeveY);
      context.rotate(18 * Math.PI / 180);
      drawRoundedRect(context, 0, 0, sleeveWidth, sleeveHeight, 38);
      context.fill();
      context.stroke();
      context.restore();

      context.save();
      context.translate(bodyX + bodyWidth - sleeveWidth * 0.42, sleeveY);
      context.rotate(-18 * Math.PI / 180);
      drawRoundedRect(context, 0, 0, sleeveWidth, sleeveHeight, 38);
      context.fill();
      context.stroke();
      context.restore();

      drawRoundedRect(context, bodyX, bodyY, bodyWidth, bodyHeight, 56);
      context.fill();
      context.stroke();

      context.globalCompositeOperation = 'destination-out';
      context.beginPath();
      context.arc(canvas.width / 2, bodyY + 8, canvas.width * 0.045, 0, Math.PI * 2);
      context.fill();
      context.globalCompositeOperation = 'source-over';
    }

    function drawRecoloredMockupImage(context, image, canvas) {
      const sourceCanvas = document.createElement('canvas');
      sourceCanvas.width = canvas.width;
      sourceCanvas.height = canvas.height;
      const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });

      if (!sourceContext) {
        drawMockupShirt(context, canvas);
        return;
      }

      const imageRect = containedImageRect(image, canvas);
      sourceContext.drawImage(image, imageRect.x, imageRect.y, imageRect.width, imageRect.height);

      try {
        const imageData = sourceContext.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const shirtColor = parseHexColor(selectedColorData().color || DEFAULT_BLACK_HEX);
        const shirtLuminance = colorLuminance(shirtColor);
        const isWhiteShirt = shirtLuminance > 0.86;
        const isDarkShirt = shirtLuminance < 0.2;

        for (let index = 0; index < data.length; index += 4) {
          const alpha = data[index + 3];
          const isWhiteBackground = alpha > 245 && data[index] > 248 && data[index + 1] > 248 && data[index + 2] > 248;

          if (alpha < 5 || isWhiteBackground) {
            data[index + 3] = 0;
            continue;
          }

          const sourceLuminance = (0.2126 * data[index] + 0.7152 * data[index + 1] + 0.0722 * data[index + 2]) / 255;
          const shade = isWhiteShirt
            ? clamp(0.42 + sourceLuminance * 0.64, 0.36, 1.04)
            : clamp(0.48 + sourceLuminance * 0.58, 0.34, 1.06);
          const highlightLift = Math.max(0, sourceLuminance - 0.78) * (isDarkShirt ? 30 : 14);

          data[index] = clamp(Math.round(shirtColor.r * shade + highlightLift), 0, 255);
          data[index + 1] = clamp(Math.round(shirtColor.g * shade + highlightLift), 0, 255);
          data[index + 2] = clamp(Math.round(shirtColor.b * shade + highlightLift), 0, 255);
          data[index + 3] = alpha < 245 ? clamp(Math.round((255 - alpha) * 1.35), 80, 255) : alpha;
        }

        context.putImageData(imageData, 0, 0);
      } catch (error) {
        transformDebugLog('pixel recolor unavailable; using canvas multiply fallback', error);

        context.save();
        context.drawImage(image, imageRect.x, imageRect.y, imageRect.width, imageRect.height);
        context.globalCompositeOperation = 'source-in';
        context.fillStyle = selectedColorData().color || DEFAULT_BLACK_HEX;
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.globalCompositeOperation = 'multiply';
        context.drawImage(image, imageRect.x, imageRect.y, imageRect.width, imageRect.height);
        context.restore();
      }

      context.globalCompositeOperation = 'source-over';
      context.globalAlpha = 1;
    }

    async function renderMockupCanvas() {
      if (!mockupCanvas) return;

      const usesRealMockupImage = state.activeView === 'front' || state.activeView === 'back';
      if (!usesRealMockupImage) return;

      const activeMockupImage = mockupImages.find((image) => image.classList.contains('is-active'));
      if (!activeMockupImage) return;

      const renderToken = ++mockupRenderToken;
      const context = mockupCanvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;

      try {
        const image = await loadCanvasImage(activeMockupImage.currentSrc || activeMockupImage.src);
        if (renderToken !== mockupRenderToken) return;

        mockupCanvas.width = image.naturalWidth || 1200;
        mockupCanvas.height = image.naturalHeight || 900;
        context.clearRect(0, 0, mockupCanvas.width, mockupCanvas.height);
        drawRecoloredMockupImage(context, image, mockupCanvas);
      } catch (error) {
        transformDebugLog('visible mockup recolor failed', error);
        if (renderToken === mockupRenderToken) {
          context.clearRect(0, 0, mockupCanvas.width, mockupCanvas.height);
          drawMockupShirt(context, mockupCanvas);
        }
      }
    }

    async function drawMockupBaseToCanvas(context, canvas) {
      const activeMockupImage = mockupImages.find((image) => image.classList.contains('is-active'));

      if (activeMockupImage && (state.activeView === 'front' || state.activeView === 'back')) {
        try {
          const image = await loadCanvasImage(activeMockupImage.currentSrc || activeMockupImage.src);
          drawRecoloredMockupImage(context, image, canvas);
          return;
        } catch (error) {
          transformDebugLog('mockup image draw failed; falling back to simple shirt', error);
        }
      }

      drawMockupShirt(context, canvas);
    }

    async function drawDesignElementToCanvas(context, element, safeRect, canvasScale) {
      const centerX = safeRect.x + (safeRect.width * element.x) / 100;
      const centerY = safeRect.y + (safeRect.height * element.y) / 100;
      const rotation = (Number(element.rotation || 0) * Math.PI) / 180;

      context.save();
      context.translate(centerX, centerY);
      context.rotate(rotation);

      if (element.type === 'image' && element.src) {
        const image = await loadCanvasImage(element.src);
        const width = safeRect.width * (Number(element.scale || 42) / 100);
        const aspectRatio = image.naturalHeight && image.naturalWidth ? image.naturalHeight / image.naturalWidth : 1;
        const height = width * aspectRatio;
        context.drawImage(image, -width / 2, -height / 2, width, height);
      } else {
        const fontSize = Math.max(18, Number(element.scale || 30) * canvasScale);
        context.font = `900 ${fontSize}px Arial, Helvetica, sans-serif`;
        context.fillStyle = element.color || '#ffffff';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.shadowColor = 'rgba(0, 0, 0, 0.32)';
        context.shadowBlur = 8;
        context.shadowOffsetY = 3;
        context.fillText(element.content || element.name || '', 0, 0);
      }

      context.restore();
    }

    async function generateMockupPreviewBlob() {
      renderDesign();

      const mockupRect = mockup.getBoundingClientRect();
      const safeAreaRect = safeArea.getBoundingClientRect();
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 900;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Unable to create mockup preview canvas.');
      }

      const scaleX = canvas.width / Math.max(mockupRect.width, 1);
      const scaleY = canvas.height / Math.max(mockupRect.height, 1);
      const canvasScale = (scaleX + scaleY) / 2;
      const safeRect = {
        x: (safeAreaRect.left - mockupRect.left) * scaleX,
        y: (safeAreaRect.top - mockupRect.top) * scaleY,
        width: safeAreaRect.width * scaleX,
        height: safeAreaRect.height * scaleY,
      };

      const backgroundGradient = context.createLinearGradient(0, 0, 0, canvas.height);
      backgroundGradient.addColorStop(0, '#f8fbff');
      backgroundGradient.addColorStop(1, '#eef4fa');
      context.fillStyle = backgroundGradient;
      context.fillRect(0, 0, canvas.width, canvas.height);

      await drawMockupBaseToCanvas(context, canvas);

      context.save();
      context.strokeStyle = 'rgba(215, 25, 32, 0.72)';
      context.setLineDash([10, 8]);
      context.lineWidth = 3;
      drawRoundedRect(context, safeRect.x, safeRect.y, safeRect.width, safeRect.height, 14);
      context.stroke();
      context.restore();

      context.save();
      context.font = '800 24px Arial, Helvetica, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      const definition = activePrintDefinition();
      const widthLabel = `${definition.widthInches}" W`;
      const heightLabel = `${definition.heightInches}" H`;
      const widthLabelWidth = context.measureText(widthLabel).width + 28;
      const heightLabelWidth = context.measureText(heightLabel).width + 28;
      const labelHeight = 38;
      const heightLabelX = Math.min(canvas.width - heightLabelWidth - 12, safeRect.x + safeRect.width + 14);
      const heightLabelY = safeRect.y + safeRect.height / 2 - labelHeight / 2;
      const widthLabelX = safeRect.x + safeRect.width / 2 - widthLabelWidth / 2;
      const widthLabelY = Math.min(canvas.height - labelHeight - 12, safeRect.y + safeRect.height + 14);
      context.fillStyle = 'rgba(255, 255, 255, 0.94)';
      drawRoundedRect(context, heightLabelX, heightLabelY, heightLabelWidth, labelHeight, labelHeight / 2);
      context.fill();
      drawRoundedRect(context, widthLabelX, widthLabelY, widthLabelWidth, labelHeight, labelHeight / 2);
      context.fill();
      context.fillStyle = '#991b1b';
      context.fillText(heightLabel, heightLabelX + heightLabelWidth / 2, heightLabelY + labelHeight / 2);
      context.fillText(widthLabel, widthLabelX + widthLabelWidth / 2, widthLabelY + labelHeight / 2);
      context.restore();

      const visibleElements = visibleElementsForActiveView();
      for (const element of visibleElements) {
        await drawDesignElementToCanvas(context, element, safeRect, canvasScale);
      }

      return canvasToBlob(canvas, 'image/png');
    }

    async function originalDesignFileFromKey(key, fileName = 'original-design.png') {
      if (!key) return null;

      const blob = await fetchCustomDesignAssetBlob(key);
      const restoredName = restoredFileName(fileName, key, blob);
      return new File([blob], restoredName, {
        type: blob.type || 'image/png',
      });
    }

    async function uploadOriginalArtworkFileToR2(file, elementId) {
      if (!file || !elementId) return null;
      const element = elementById(elementId);
      if (!element) return null;

      const formData = new FormData();
      formData.set('design_id', state.designId);
      formData.set('file_id', elementId);
      formData.set('uploaded_file_name', file.name || element.name || 'uploaded-artwork.png');
      formData.set('original_design', file);

      try {
        const response = await fetch(customDesignFileUploadEndpoint, {
          method: 'POST',
          body: formData,
        });
        const body = await response.json().catch(() => null);

        if (!response.ok || !body?.ok || !body?.original_design_key) {
          throw new Error(body?.error || 'Unable to store uploaded artwork.');
        }

        element.originalDesignKey = body.original_design_key;
        element.designFileId = body.design_file_id || body.file_id || elementId;
        element.originalDesignUrl = absoluteWorkerUrl(body.preview_url || `/custom-design-preview?key=${encodeURIComponent(body.original_design_key || '')}`);
        element.fileName = body.original_filename || file.name || element.name || '';
        element.fileType = body.content_type || file.type || '';
        element.fileSize = Number(body.file_size || file.size || 0) || 0;
        element.restoredPreviewOnly = false;
        element.requiresReupload = false;
        saveCurrentDesign({ includePreviews: false, showStatus: false });
        return body;
      } catch (error) {
        console.warn('[InkNfiber custom designer] Temporary artwork upload failed:', error);
        if (uploadError) {
          uploadError.textContent = 'Artwork preview is available, but cloud save did not complete. Save may require re-uploading this file later.';
        }
        element.requiresReupload = true;
        saveCurrentDesign({ includePreviews: false, showStatus: false });
        return null;
      }
    }

    async function primaryOriginalDesignSource() {
      const primaryImage = imageElements()[0];
      if (!primaryImage) return null;

      if (primaryImage.originalDesignKey && !primaryImage.restoredPreviewOnly) {
        return {
          file: null,
          existingOriginalDesignKey: primaryImage.originalDesignKey,
          uploadedFileName: primaryImage.name || 'original-design.png',
          source: 'existing-r2-key',
        };
      }

      const originalFile = originalUploadFiles.get(primaryImage.id);
      if (originalFile) {
        return {
          file: originalFile,
          existingOriginalDesignKey: '',
          uploadedFileName: originalFile.name || primaryImage.name || 'original-design.png',
          source: 'fresh-file',
        };
      }

      const previewBlob = dataUrlToBlob(primaryImage.src);
      if (previewBlob) {
        return {
          file: new File([previewBlob], primaryImage.name || 'original-design.png', {
            type: previewBlob.type || 'image/png',
          }),
          existingOriginalDesignKey: '',
          uploadedFileName: primaryImage.name || 'original-design.png',
          source: 'data-url',
        };
      }

      if (primaryImage.originalDesignKey && !primaryImage.restoredPreviewOnly) {
        return {
          file: await originalDesignFileFromKey(primaryImage.originalDesignKey, primaryImage.name),
          existingOriginalDesignKey: '',
          uploadedFileName: primaryImage.name || 'original-design.png',
          source: 'r2-refetch',
        };
      }

      return null;
    }

    async function uploadCustomDesignAssets() {
      const originalSource = await primaryOriginalDesignSource();

      if (!originalSource?.file && !originalSource?.existingOriginalDesignKey) {
        throw new Error('Upload an image design before adding this custom order to cart. Text-only designs need an uploaded artwork file for production.');
      }

      const mockupBlob = await generateMockupPreviewBlob();
      const formData = new FormData();
      formData.set('design_id', state.designId);
      formData.set('mockup_preview', new File([mockupBlob], `mockup-preview-${state.designId}.png`, { type: 'image/png' }));
      if (originalSource.file) {
        formData.set('original_design', originalSource.file);
      } else {
        formData.set('existing_original_design_key', originalSource.existingOriginalDesignKey);
      }
      formData.set('uploaded_file_name', imageElements().map((element) => element.name).join(', ') || originalSource.uploadedFileName || 'original-design');
      formData.set('design_metadata', JSON.stringify(customDesignMetadata()));

      restoreDebug('upload source for re-save', {
        source: originalSource.source,
        usedRestoredBlobOrKey: originalSource.source !== 'fresh-file',
        existingOriginalDesignKey: Boolean(originalSource.existingOriginalDesignKey),
        hasOriginalFile: Boolean(originalSource.file),
      });

      const response = await fetch(customDesignUploadEndpoint, {
        method: 'POST',
        body: formData,
      });
      const responseBody = await response.json().catch(() => null);

      if (!response.ok || !responseBody?.ok) {
        throw new Error(responseBody?.error || 'Unable to upload custom design assets. Please try again.');
      }

      const primaryImage = imageElements()[0];
      if (primaryImage) {
        primaryImage.originalDesignKey = responseBody.original_design_key;
        primaryImage.mockupPreviewKey = responseBody.mockup_preview_key;
        primaryImage.restoredPreviewOnly = false;
      }

      return {
        design_id: responseBody.design_id,
        mockup_preview_key: responseBody.mockup_preview_key,
        original_design_key: responseBody.original_design_key,
        uploaded_file_name: formData.get('uploaded_file_name'),
      };
    }

    function cartDebug(message, details = {}) {
      if (!debugEnabled) return;
      console.debug('[InkNfiber custom designer cart]', message, details);
    }

    function validCartVariantId(value) {
      const id = Number(value);
      return Number.isInteger(id) && id > 0;
    }

    function cleanCartPropertyValue(value, maxLength = 255) {
      if (value === undefined || value === null) return '';

      const stringValue = String(value).trim();
      if (!stringValue) return '';

      if (stringValue.includes('data:image') || stringValue.includes(';base64,')) {
        return '';
      }

      return stringValue.length > maxLength ? `${stringValue.slice(0, maxLength - 1)}...` : stringValue;
    }

    function cleanCartProperties(properties) {
      return Object.entries(properties).reduce((cleaned, [key, value]) => {
        const cleanKey = cleanCartPropertyValue(key, 80);
        const cleanValue = cleanCartPropertyValue(value);

        if (cleanKey && cleanValue) {
          cleaned[cleanKey] = cleanValue;
        }

        return cleaned;
      }, {});
    }

    function cartPayloadHasImageData(payload) {
      return JSON.stringify(payload).includes('data:image') || JSON.stringify(payload).includes(';base64,');
    }

    function selectedSizeDebugData() {
      return selectedSizes().map((item) => ({
        size: item.size,
        quantity: item.quantity,
        variantId: sizeVariantMap[item.size]?.id || null,
      }));
    }

    function isCustomDesignFeeCartItem(item) {
      return item?.properties?._inknfiber_fee_item === 'true'
        || item?.properties?.['_inknfiber_fee_item'] === 'true'
        || item?.properties?._hidden_helper_line === 'true'
        || item?.properties?.['_hidden_helper_line'] === 'true'
        || item?.properties?._adjustment_for === 'Custom Designs'
        || item?.properties?.['_adjustment_for'] === 'Custom Designs'
        || item?.handle === 'custom-design-upload-fee'
        || item?.handle === 'ai-design-generation-fee'
        || item?.handle === 'dtf-transfer-price-adjustment-product'
        || item?.product_title === 'Custom Design Upload Fee'
        || item?.product_title === 'AI Design Generation Fee'
        || item?.product_title === 'DTF transfer price adjustment product';
    }

    function visibleCartItemCount(cart) {
      return (cart?.items || []).reduce((sum, item) => {
        return isCustomDesignFeeCartItem(item) ? sum : sum + Number(item.quantity || 0);
      }, 0);
    }

    function renderPrintChargeInternalEstimate(details = currentPrintChargeDetails()) {
      if (!artworkEstimateBody) return;
      if (!details?.sides?.length) {
        artworkEstimateBody.innerHTML = '<p class="inknfiber-artwork-estimate__empty">Add artwork to calculate the production print charge.</p>';
        return;
      }

      const sideMarkup = details.sides.map((side) => `
        <div class="inknfiber-artwork-estimate__internal">
          <h4>${side.label} - ${placementDisplayName(side.placement)}</h4>
          <dl>
            <div><dt>Actual print dimensions</dt><dd>${inches(side.widthInches)} W x ${inches(side.heightInches)} H</dd></div>
            <div><dt>Visible ink coverage</dt><dd>${Math.round(side.coveragePercent * 10) / 10}%</dd></div>
            <div><dt>Film cost</dt><dd>${formatMoney(side.filmCostCents)}</dd></div>
            <div><dt>White ink cost</dt><dd>${formatMoney(side.whiteInkCostCents)}</dd></div>
            <div><dt>Color ink cost</dt><dd>${formatMoney(side.colorInkCostCents)}</dd></div>
            <div><dt>Raw material cost</dt><dd>${formatMoney(side.rawMaterialCostCents)}</dd></div>
            <div><dt>Material after waste</dt><dd>${formatMoney(side.materialWithWasteCents)}</dd></div>
            <div><dt>Estimated print time</dt><dd>${Math.round(side.estimatedPrintTimeMinutes * 100) / 100} min</dd></div>
            <div><dt>Machine-time cost</dt><dd>${formatMoney(side.machineTimeCostCents)}</dd></div>
            <div><dt>Handling / setup</dt><dd>${formatMoney(side.handlingSetupCents)}</dd></div>
            <div><dt>Production cost</dt><dd>${formatMoney(side.productionCostCents)}</dd></div>
            <div><dt>Final print charge</dt><dd>${formatMoney(side.printChargeCents)}</dd></div>
          </dl>
          ${side.analysisFallback ? '<p>Composition analysis was unavailable, so the actual artwork bounds fallback was used.</p>' : ''}
        </div>
      `).join('');

      artworkEstimateBody.innerHTML = `
        ${sideMarkup}
        <div class="inknfiber-artwork-estimate__price">
          <span>Print charge per shirt</span>
          <strong>${formatMoney(details.totalEachCents)}</strong>
        </div>
      `;
    }

    function renderArtworkEstimate() {
      renderPrintChargeInternalEstimate();
    }

    async function refreshArtworkEstimateNow() {
      if (!artworkEstimator || !artworkEstimatorConfig?.enabled) return latestArtworkEstimate;
      window.clearTimeout(artworkEstimateTimer);

      const element = productionArtworkElement();
      if (!element?.src) {
        latestArtworkEstimate = null;
        latestArtworkEstimateKey = '';
        renderArtworkEstimate(null);
        updateSummaries();
        return null;
      }

      const production = productionMeasurements(element);
      const widthInches = Number(production.designWidthInches || 0);
      const heightInches = Number(production.designHeightInches || 0);
      if (!widthInches || !heightInches) {
        latestArtworkEstimate = null;
        latestArtworkEstimateKey = '';
        renderArtworkEstimate(null);
        updateSummaries();
        return null;
      }

      const key = `${element.src}|${widthInches}|${heightInches}`;
      if (key === latestArtworkEstimateKey && latestArtworkEstimate) {
        renderArtworkEstimate(latestArtworkEstimate);
        updateSummaries();
        return latestArtworkEstimate;
      }

      latestArtworkEstimateKey = key;
      const analysis = await artworkEstimator.analyzeImage(element.src);
      if (latestArtworkEstimateKey !== key) return latestArtworkEstimate;
      latestArtworkEstimate = artworkEstimator.calculate({
        widthInches,
        heightInches,
        analysis,
        config: artworkEstimatorConfig,
      });
      renderArtworkEstimate(latestArtworkEstimate);
      updateSummaries();
      return latestArtworkEstimate;
    }

    function scheduleArtworkEstimate() {
      if (!artworkEstimateBody || !artworkEstimator || !artworkEstimatorConfig?.enabled) return;
      window.clearTimeout(artworkEstimateTimer);
      artworkEstimateTimer = window.setTimeout(async () => {
        await refreshArtworkEstimateNow();
      }, 250);
    }

    function activePrintDefinition() {
      return placementDefinitions[canonicalPlacementValue(placementSelect?.value)] || placementDefinitions['Left Chest'];
    }

    function productionArtworkElement() {
      const active = activeElement();
      if (active?.type === 'image') return active;

      const activeViewImage = imageElements().find((element) => !element.view || element.view === state.activeView);
      return activeViewImage || imageElements()[0] || active || null;
    }

    function inches(value) {
      const rounded = Math.round(Number(value || 0) * 100) / 100;
      return `${rounded.toFixed(2).replace(/\.?0+$/, '')}"`;
    }

    function elementNodeById(elementId) {
      if (!elementId || !elementsRoot) return null;
      const safeId = window.CSS?.escape ? CSS.escape(elementId) : elementId.replace(/"/g, '\\"');
      return elementsRoot.querySelector(`[data-element-id="${safeId}"]`);
    }

    function productionMeasurements(element) {
      const definition = activePrintDefinition();
      const fallbackScaleRatio = element ? clamp(Number(element.scale || 42) / 100, 0.01, 2) : 0;
      let widthRatio = fallbackScaleRatio;
      let heightRatio = fallbackScaleRatio;
      let offsetXRatio = element ? (Number(element.x || 50) - 50) / 100 : 0;
      let offsetYRatio = element ? (Number(element.y || 50) - 50) / 100 : 0;
      let source = element ? 'model-fallback' : 'none';
      let nodeRectDebug = null;
      let safeRectDebug = null;

      if (element?.type === 'image') {
        const naturalWidth = Number(element.naturalWidth || 0);
        const naturalHeight = Number(element.naturalHeight || 0);
        const safeAspect = definition.heightInches > 0 ? definition.widthInches / definition.heightInches : 1;
        const imageAspect = naturalWidth > 0 && naturalHeight > 0 ? naturalHeight / naturalWidth : 1;
        heightRatio = widthRatio * imageAspect * safeAspect;
      }

      const node = element ? elementNodeById(element.id) : null;
      if (node && safeArea) {
        const nodeRect = node.getBoundingClientRect();
        const safeRect = safeArea.getBoundingClientRect();
        nodeRectDebug = {
          width: round(nodeRect.width),
          height: round(nodeRect.height),
          x: round(nodeRect.x),
          y: round(nodeRect.y),
        };
        safeRectDebug = {
          width: round(safeRect.width),
          height: round(safeRect.height),
          x: round(safeRect.x),
          y: round(safeRect.y),
        };

        if (safeRect.width > 0 && safeRect.height > 0) {
          const domWidthRatio = nodeRect.width / safeRect.width;
          const domHeightRatio = nodeRect.height / safeRect.height;
          if (domWidthRatio > 0 && domHeightRatio > 0) {
            widthRatio = domWidthRatio;
            heightRatio = domHeightRatio;
            offsetXRatio = ((nodeRect.left + nodeRect.width / 2) - (safeRect.left + safeRect.width / 2)) / safeRect.width;
            offsetYRatio = ((nodeRect.top + nodeRect.height / 2) - (safeRect.top + safeRect.height / 2)) / safeRect.height;
            source = 'dom-bounds';
          }
        }
      }

      const designWidthInches = Math.max(0, definition.widthInches * widthRatio);
      const designHeightInches = Math.max(0, definition.heightInches * heightRatio);
      const centerXPercent = element ? Number(element.x || 50) : 50;
      const centerYPercent = element ? Number(element.y || 50) : 50;
      const centerXInches = definition.widthInches * centerXPercent / 100;
      const centerYInches = definition.heightInches * centerYPercent / 100;
      const xToolXInches = centerXInches - designWidthInches / 2;
      const xToolYInches = centerYInches - designHeightInches / 2;
      const positionLabel = element && designWidthInches > 0 && designHeightInches > 0
        ? `${inches(xToolXInches)} X, ${inches(xToolYInches)} Y`
        : '';
      const centerPositionLabel = element ? `${round(centerXPercent)}% X, ${round(centerYPercent)}% Y` : '';
      const scaleLabel = element ? `${round(element.scale || 0)}%` : '';
      const rotationLabel = element ? `${round(element.rotation || 0)}°` : '';
      const result = {
        printArea: definition.dimensions,
        designWidthInches,
        designHeightInches,
        designWidth: designWidthInches > 0 ? inches(designWidthInches) : '',
        designHeight: designHeightInches > 0 ? inches(designHeightInches) : '',
        designSize: designWidthInches > 0 && designHeightInches > 0 ? `${inches(designWidthInches)}W x ${inches(designHeightInches)}H` : '',
        offsetX: inches(definition.widthInches * offsetXRatio),
        offsetY: inches(definition.heightInches * offsetYRatio),
        position: positionLabel,
        centerPosition: centerPositionLabel,
        centerX: inches(centerXInches),
        centerY: inches(centerYInches),
        xToolX: designWidthInches > 0 ? inches(xToolXInches) : '',
        xToolY: designHeightInches > 0 ? inches(xToolYInches) : '',
        scale: scaleLabel,
        rotation: element ? `${round(element.rotation || 0)}deg` : '',
        source,
      };

      if (designerDebug) {
        transformDebugLog('production measurements calculated', {
          selectedView: state.activeView,
          selectedPlacement: placementDisplayName(),
          printAreaConfig: definition,
          printAreaPixelRect: safeRectDebug,
          activeImageLayer: element ? {
            id: element.id,
            name: element.name,
            type: element.type,
            x: round(element.x || 0),
            y: round(element.y || 0),
            scale: round(element.scale || 0),
            rotation: round(element.rotation || 0),
            naturalWidth: element.naturalWidth || 0,
            naturalHeight: element.naturalHeight || 0,
          } : null,
          layerPixelBounds: nodeRectDebug,
          calculatedDesignWidthInches: result.designWidth,
          calculatedDesignHeightInches: result.designHeight,
          centerPositionPercent: result.centerPosition,
          centerXInches: result.centerX,
          centerYInches: result.centerY,
          xToolX: result.xToolX,
          xToolY: result.xToolY,
          rotation: result.rotation,
          position: result.position,
          scale: result.scale,
          source,
        });
      }

      return result;
    }

    function customDesignMetadata() {
      const element = productionArtworkElement();
      const production = productionMeasurements(element);
      const printDetails = currentPrintChargeDetails();

      return {
        design_id: state.designId,
        shirt_color: selectedColorData().label,
        print_view: viewDisplayName(),
        placement: placementDisplayName(),
        print_area: production.printArea,
        design_width: production.designWidth,
        design_height: production.designHeight,
        design_size: production.designSize,
        position_offset_x: production.offsetX,
        position_offset_y: production.offsetY,
        design_position: production.position,
        design_position_center_percent: production.centerPosition,
        design_position_top_left_inches: production.position,
        x_tool_x: production.xToolX,
        x_tool_y: production.xToolY,
        center_x_inches: production.centerX,
        center_y_inches: production.centerY,
        design_position_x: element ? `${round(element.x)}%` : '',
        design_position_y: element ? `${round(element.y)}%` : '',
        design_scale: production.scale,
        design_rotation: production.rotation,
        uploaded_file_name: imageElements().map((imageElement) => imageElement.name).join(', '),
        uploaded_design_count: String(uploadedDesignCount()),
        background_removed_design_count: String(backgroundRemovedImageElements().length),
        background_removed_image_keys: backgroundRemovedImageKeys().join(','),
        design_notes_front: state.designNotes.front,
        design_notes_back: state.designNotes.back,
        design_notes_extra: state.designNotes.extra,
        size_breakdown: sizeBreakdown(),
        total_quantity: String(totalQuantity()),
        print_charge_per_shirt: formatMoney(printDetails.totalEachCents),
        print_charge_total: formatMoney(printDetails.totalEachCents * totalQuantity()),
        print_charge_side_summary: printChargeSideSummary(printDetails),
        print_charge_dimensions: printDetails.sides
          .map((side) => `${side.label} ${inches(side.widthInches)} x ${inches(side.heightInches)}`)
          .join(' | '),
      };
    }

    function sharedProperties() {
      const element = productionArtworkElement();
      const production = productionMeasurements(element);
      const discount = bulkDiscountEstimate();
      const checkoutDetails = estimatorCheckoutDetails();
      const properties = cleanCartProperties({
        'Design ID': state.designId,
        'Shirt color': selectedColorData().label,
        'Print view': viewDisplayName(),
        'Print placement': placementDisplayName(),
        'Print Area': production.printArea,
        'Design Width': production.designWidth,
        'Design Height': production.designHeight,
        'Design Size': production.designSize,
        'Rotation': production.rotation,
        'Position': production.position,
        'Scale': production.scale,
        'Uploaded design count': String(uploadedDesignCount()),
        'Design notes': cleanCartPropertyValue(designNotesSummary(), 240),
        'Print charge': designFeeSummaryText(),
        '_inknfiber_builder_type': 'custom_design',
        '_inknfiber_line_role': 'parent',
        '_inknfiber_parent_design_id': state.designId,
        '_custom_design_id': state.designId,
        '_inknfiber_fee_placement': placementDisplayName(),
        '_custom_design_print_area_sq_in': String(Math.round(checkoutDetails.printAreaSqIn * 100) / 100),
        '_custom_design_print_rate_cents_per_sq_in': String(checkoutDetails.printRateCentsPerSqIn),
        '_custom_design_minimum_print_charge_cents': String(checkoutDetails.printMinimumChargeCents),
        '_custom_design_maximum_print_charge_cents': String(checkoutDetails.printMaximumChargeCents),
        '_custom_design_print_pricing_model': 'composed_production_cost_v1',
        '_custom_design_print_side_summary': cleanCartPropertyValue(checkoutDetails.printChargeSideSummary, 240),
        '_custom_design_print_side_dimensions': cleanCartPropertyValue(
          checkoutDetails.printChargeSides
            .map((side) => `${side.label} ${inches(side.widthInches)} x ${inches(side.heightInches)}`)
            .join(' | '),
          240
        ),
        '_custom_design_print_side_coverage': cleanCartPropertyValue(
          checkoutDetails.printChargeSides
            .map((side) => `${side.label} ${Math.round(side.coveragePercent * 10) / 10}%`)
            .join(' | '),
          240
        ),
        '_custom_design_print_production_breakdown': cleanCartPropertyValue(
          checkoutDetails.printChargeSides
            .map((side) => `${side.label}: material ${formatMoney(side.rawMaterialCostCents)}, time ${Math.round(side.estimatedPrintTimeMinutes * 100) / 100} min, production ${formatMoney(side.productionCostCents)}, charge ${formatMoney(side.printChargeCents)}`)
            .join(' | '),
          240
        ),
        '_custom_design_print_analysis_fallback': checkoutDetails.printChargeAnalysisFallback ? 'true' : 'false',
        '_custom_design_print_waste_multiplier_percent': String(customDesignPrintConfig.wasteMultiplierPercent),
        '_custom_design_print_machine_rate_cents_per_minute': String(customDesignPrintConfig.machineRateCentsPerMinute),
        '_custom_design_print_handling_setup_cents': String(customDesignPrintConfig.handlingSetupCents),
        '_custom_design_print_profit_multiplier_percent': String(customDesignPrintConfig.profitMultiplierPercent),
        '_custom_design_print_rounding_increment_cents': String(customDesignPrintConfig.roundingIncrementCents),
        '_custom_design_print_charge_cents': String(checkoutDetails.printChargeEachCents),
        '_custom_design_print_charge_total_cents': String(checkoutDetails.printChargeTotalCents),
        '_custom_design_print_charge': formatMoney(checkoutDetails.printChargeEachCents),
        '_custom_design_print_charge_per_shirt': formatMoney(checkoutDetails.printChargeEachCents),
        '_custom_design_print_charge_total': formatMoney(checkoutDetails.printChargeTotalCents),
        '_custom_design_artwork_price_cents': String(checkoutDetails.artworkPriceCents),
        '_custom_design_artwork_total_cents': String(checkoutDetails.artworkTotalCents),
        '_custom_design_total_quantity': String(checkoutDetails.totalQuantity),
        '_custom_design_shirt_price_category': usesWhiteShirtPricing() ? 'white' : 'color',
        '_custom_design_price_adjustment_variant_id': String(priceAdjustmentVariantId || ''),
        '_Design notes front': cleanCartPropertyValue(state.designNotes.front, 240),
        '_Design notes back': cleanCartPropertyValue(state.designNotes.back, 240),
        '_Design notes extra': cleanCartPropertyValue(state.designNotes.extra, 240),
        '_Position Offset X': production.offsetX,
        '_Position Offset Y': production.offsetY,
        '_Design center position': production.centerPosition,
        '_Design top-left X': production.xToolX,
        '_Design top-left Y': production.xToolY,
        '_inknfiber_uploaded_design_count': String(uploadedDesignCount()),
        '_Design position X': element ? `${round(element.x)}%` : '',
        '_Design position Y': element ? `${round(element.y)}%` : '',
        '_Design size/scale': production.scale,
        '_Design rotation': production.rotation,
        '_Uploaded designs count': String(imageElements().length),
        '_Text element count': String(textElements().length),
        '_Symbol element count': String(symbolElements().length),
        '_Element summary': cleanCartPropertyValue(elementSummary(), 240),
        '_inknfiber_background_removed_design_count': String(backgroundRemovedImageElements().length),
        '_inknfiber_background_removed_image_keys': cleanCartPropertyValue(backgroundRemovedImageKeys().join(','), 240),
        '_bulk_discount_eligible': discount.eligible ? 'yes' : 'no',
        '_bulk_discount_percent': String(discount.percent),
        '_bulk_discount_tier': discount.tier,
        '_bulk_discount_same_design_rule': 'same design + same print location',
        '_bulk_discount_print_location': placementDisplayName(),
        '_bulk_discount_design_id': state.designId,
        '_bulk_discount_estimated_product_subtotal': formatMoney(discount.productSubtotalCents),
        '_bulk_discount_estimated_savings': formatMoney(discount.estimatedSavingsCents),
        '_bulk_discount_estimated_product_total': formatMoney(discount.productTotalAfterDiscountCents),
        '_bulk_discount_requires_manual_approval': discount.requiresManualApproval ? 'true' : 'false',
        '_custom_design_configured_product_subtotal': formatMoney(productSubtotalCents()),
        '_custom_design_shopify_variant_subtotal': formatMoney(shopifyVariantSubtotalCents()),
        '_custom_design_product_price_correction': formatMoney(productPriceCorrectionCents()),
        '_custom_design_checkout_product_subtotal': formatMoney(checkoutProductSubtotalCents()),
        '_custom_design_product_subtotal': formatMoney(checkoutDetails.productSubtotalCents),
        '_custom_design_charge_label': 'Print charge',
        '_custom_design_artwork_price': formatMoney(checkoutDetails.artworkPriceCents),
        '_custom_design_artwork_price_per_shirt': formatMoney(checkoutDetails.artworkPriceCents),
        '_custom_design_artwork_total': formatMoney(checkoutDetails.artworkTotalCents),
        '_custom_design_existing_upload_fee': formatMoney(checkoutDetails.existingUploadFeeCents),
        '_custom_design_design_fee_adjustment': formatMoney(checkoutDetails.adjustmentCents),
        '_custom_design_final_design_charge': formatMoney(checkoutDetails.finalDesignChargeCents),
        '_custom_design_final_checkout_price': formatMoney(checkoutDetails.finalPriceCents),
        '_estimator_checkout_pricing_enabled': 'false',
        '_estimator_base_price': formatMoney(checkoutDetails.basePriceCents),
        '_estimator_suggested_retail_price': formatMoney(checkoutDetails.suggestedPriceCents),
        '_estimator_final_checkout_price': formatMoney(checkoutDetails.finalPriceCents),
        '_estimator_adjustment_amount': formatMoney(checkoutDetails.adjustmentCents),
        '_estimator_pricing_mode': 'reference_only',
      });

      if (aiPhotoshootEnabled && aiDesignCount() > 0) {
        properties['AI design generation'] = aiDesignFeeSummaryText();
        properties['_inknfiber_ai_design_count'] = String(aiDesignCount());
        properties['_inknfiber_ai_image_keys'] = cleanCartPropertyValue(aiDesignImageKeys().join(','), 240);
        properties['_inknfiber_ai_prompts'] = cleanCartPropertyValue(aiDesignPrompts().join(' | '), 240);
        properties['_inknfiber_ai_original_prompts'] = cleanCartPropertyValue(aiDesignOriginalPrompts().join(' | '), 240);
        properties['_inknfiber_ai_prompt_options'] = cleanCartPropertyValue(aiDesignPromptOptions().join(' | '), 240);
      }

      if (artworkEstimator && latestArtworkEstimate) {
        Object.assign(properties, artworkEstimator.metadata(latestArtworkEstimate));
        properties['_artwork_estimate_area_sq_in'] = String(latestArtworkEstimate.squareInches);
      }

      if (lastCustomDesignUpload?.mockup_preview_key && lastCustomDesignUpload?.original_design_key) {
        properties['Mockup Preview Key'] = lastCustomDesignUpload.mockup_preview_key;
        properties['Original Design Key'] = lastCustomDesignUpload.original_design_key;
        properties['Uploaded file name'] = cleanCartPropertyValue(
          lastCustomDesignUpload.uploaded_file_name || imageElements().map((element) => element.name).join(', ') || 'None'
        );
      }

      cartDebug('final custom design shared cart properties', properties);
      return properties;
    }

    function buildMainCartItems() {
      const properties = sharedProperties();
      const selected = selectedSizes();
      let cartItems = [];

      if (productHasSizeVariants) {
        cartItems = selected
          .map((item) => {
            const variant = sizeVariantMap[item.size] || firstAvailableVariant;
            const configuredUnitCents = linePriceForSize(item.size);
            const shopifyUnitCents = shopifyVariantPriceForSize(item.size);
            const artworkUnitCents = printChargeEachCents();
            const lineQuantity = Number(item.quantity);
            const lineProductTotalCents = Math.max(configuredUnitCents, shopifyUnitCents) * lineQuantity;
            const lineArtworkTotalCents = artworkUnitCents * lineQuantity;
            const itemProperties = {
              ...properties,
              Size: item.size,
              '_custom_design_line_configured_unit_cents': String(configuredUnitCents),
              '_custom_design_line_shopify_unit_cents': String(shopifyUnitCents),
              '_custom_design_line_print_charge_cents': String(artworkUnitCents),
              '_custom_design_line_print_charge_total_cents': String(lineArtworkTotalCents),
              '_custom_design_line_artwork_price_cents': String(artworkUnitCents),
              '_custom_design_line_artwork_total_cents': String(lineArtworkTotalCents),
              '_custom_design_line_final_total_cents': String(lineProductTotalCents + lineArtworkTotalCents),
              '_custom_design_line_configured_total': formatMoney(lineProductTotalCents),
              '_custom_design_line_shopify_total': formatMoney(shopifyUnitCents * lineQuantity),
              '_estimator_display_line': 'true',
            };

            if (!sizeVariantMap[item.size]) {
              itemProperties['Size mapping note'] = 'This size used the first available Shopify variant because no exact size variant was matched.';
            }

            return {
              id: Number(variant?.id),
              quantity: Number(item.quantity),
              properties: cleanCartProperties(itemProperties),
            };
          })
          .filter((item) => validCartVariantId(item.id) && item.quantity > 0);
      } else {
        if (!validCartVariantId(firstAvailableVariantId)) {
          throw new Error('The selected custom product does not have a valid Shopify variant ID.');
        }

        const fallbackProperties = {
          ...properties,
          'Size breakdown': sizeBreakdown(),
          '_estimator_display_line': 'true',
          '_custom_design_line_configured_unit_cents': String(totalQuantity() > 0 ? Math.round(productSubtotalCents() / totalQuantity()) : 0),
          '_custom_design_line_shopify_unit_cents': String(totalQuantity() > 0 ? Math.round(shopifyVariantSubtotalCents() / totalQuantity()) : 0),
          '_custom_design_line_print_charge_cents': String(printChargeEachCents()),
          '_custom_design_line_print_charge_total_cents': String(printChargeTotalCents()),
          '_custom_design_line_artwork_price_cents': String(printChargeEachCents()),
          '_custom_design_line_artwork_total_cents': String(printChargeTotalCents()),
          '_custom_design_line_final_total_cents': String(checkoutProductSubtotalCents() + printChargeTotalCents()),
          '_custom_design_line_configured_total': formatMoney(productSubtotalCents()),
          '_custom_design_line_shopify_total': formatMoney(shopifyVariantSubtotalCents()),
        };

        if (shouldUseFallbackMode()) {
          fallbackProperties['Size mapping note'] = 'Product size variants were not fully matched; size breakdown was captured in order notes.';
        }

        cartItems = [{
          id: Number(firstAvailableVariantId),
          quantity: Number(totalQuantity()),
          properties: cleanCartProperties(fallbackProperties),
        }];
      }

      if (!cartItems.length) {
        throw new Error('No valid Shopify cart items could be built for the selected sizes.');
      }

      return cartItems;
    }

    function buildFeeCartItem() {
      return null;
    }

    function buildAiFeeCartItem() {
      if (!aiPhotoshootEnabled) return null;

      const feeQuantity = aiDesignCount();

      if (feeQuantity <= 0 || totalQuantity() <= 0) return null;

      const details = aiFeeDetails();

      if (!details.variant) {
        throw new Error('AI design fee product is not configured.');
      }

      if (!validCartVariantId(details.variantId) || !details.available) {
        throw new Error('AI design fee product is not available.');
      }

      return {
        id: Number(details.variantId),
        quantity: feeQuantity,
        properties: cleanCartProperties({
          '_inknfiber_builder_type': 'custom_design',
          '_inknfiber_line_role': 'helper',
          '_inknfiber_fee_item': 'true',
          '_inknfiber_fee_type': 'ai_generation_fee',
          '_inknfiber_parent_design_id': state.designId,
          '_custom_design_id': state.designId,
          '_hidden_helper_line': 'true',
          '_inknfiber_fee_variant_id': String(details.variantId),
          '_inknfiber_ai_design_count': String(feeQuantity),
          '_inknfiber_ai_image_keys': aiDesignImageKeys().join(','),
        }),
      };
    }

    function buildProductPriceCorrectionCartItem() {
      const correctionCents = productPriceCorrectionCents();
      if (correctionCents <= 0 || totalQuantity() <= 0) return null;

      const adjustmentError = validateEstimatorAdjustmentProduct(estimatorCheckoutDetails());
      if (adjustmentError) throw new Error(adjustmentError);

      return {
        id: Number(priceAdjustmentVariantId),
        quantity: correctionCents,
        properties: cleanCartProperties({
          '_inknfiber_builder_type': 'custom_design',
          '_inknfiber_line_role': 'helper',
          '_adjustment_for': 'Custom Designs',
          '_adjustment_type': 'Custom design product price correction',
          '_base_price': formatMoney(shopifyVariantSubtotalCents()),
          '_suggested_price': formatMoney(productSubtotalCents()),
          '_final_price': formatMoney(checkoutProductSubtotalCents()),
          '_adjustment_amount': formatMoney(correctionCents),
          '_design_id': state.designId,
          '_inknfiber_parent_design_id': state.designId,
          '_custom_design_id': state.designId,
          '_inknfiber_fee_type': 'product_price_correction',
          '_hidden_helper_line': 'true',
        }),
      };
    }

    function buildEstimatorAdjustmentCartItem() {
      if (bulkDiscountEstimate().requiresQuote || totalQuantity() <= 0) return null;

      const checkoutDetails = estimatorCheckoutDetails();
      if (checkoutDetails.adjustmentCents <= 0) return null;

      const adjustmentError = validateEstimatorAdjustmentProduct(checkoutDetails);
      if (adjustmentError) throw new Error(adjustmentError);

      return {
        id: Number(priceAdjustmentVariantId),
        quantity: checkoutDetails.adjustmentCents,
        properties: cleanCartProperties({
          'Charge': 'Custom print charge',
          'Print charge': formatMoney(checkoutDetails.printChargeTotalCents),
          '_inknfiber_builder_type': 'custom_design',
          '_inknfiber_line_role': 'helper',
          '_adjustment_for': 'Custom Designs',
          '_adjustment_type': 'Artwork/design price adjustment',
          '_custom_design_adjustment_type': 'print_charge',
          '_base_upload_fee': formatMoney(checkoutDetails.existingUploadFeeCents),
          '_print_charge_per_shirt': formatMoney(checkoutDetails.printChargeEachCents),
          '_print_charge_per_shirt_cents': String(checkoutDetails.printChargeEachCents),
          '_print_charge_total': formatMoney(checkoutDetails.printChargeTotalCents),
          '_print_charge_area_sq_in': String(Math.round(checkoutDetails.printAreaSqIn * 100) / 100),
          '_print_charge_rate_cents_per_sq_in': String(checkoutDetails.printRateCentsPerSqIn),
          '_print_charge_minimum_cents': String(checkoutDetails.printMinimumChargeCents),
          '_print_charge_maximum_cents': String(checkoutDetails.printMaximumChargeCents),
          '_print_charge_pricing_model': 'composed_production_cost_v1',
          '_print_charge_side_summary': cleanCartPropertyValue(checkoutDetails.printChargeSideSummary, 240),
          '_print_charge_side_dimensions': cleanCartPropertyValue(
            checkoutDetails.printChargeSides
              .map((side) => `${side.label} ${inches(side.widthInches)} x ${inches(side.heightInches)}`)
              .join(' | '),
            240
          ),
          '_print_charge_analysis_fallback': checkoutDetails.printChargeAnalysisFallback ? 'true' : 'false',
          '_artwork_suggested_price': formatMoney(checkoutDetails.artworkPriceCents),
          '_artwork_price_per_shirt': formatMoney(checkoutDetails.artworkPriceCents),
          '_artwork_price_per_shirt_cents': String(checkoutDetails.artworkPriceCents),
          '_artwork_total_quantity': String(checkoutDetails.totalQuantity),
          '_artwork_design_total': formatMoney(checkoutDetails.artworkTotalCents),
          '_adjustment_amount': formatMoney(checkoutDetails.adjustmentCents),
          '_final_design_charge': formatMoney(checkoutDetails.finalDesignChargeCents),
          '_base_price': formatMoney(checkoutDetails.basePriceCents),
          '_suggested_price': formatMoney(checkoutDetails.suggestedPriceCents),
          '_final_price': formatMoney(checkoutDetails.finalPriceCents),
          '_design_id': state.designId,
          '_inknfiber_parent_design_id': state.designId,
          '_custom_design_id': state.designId,
          '_inknfiber_fee_type': 'artwork_design_charge',
          '_hidden_helper_line': 'true',
        }),
      };
    }

    let lastCartAddSections = null;

    async function postCartAdd(payload, label) {
      const feeDetails = selectedPlacementFeeDetails();
      const aiDetails = aiFeeDetails();
      const debugDetails = {
        payload,
        uploadedDesignCount: uploadedDesignCount(),
        aiPhotoshootEnabled,
        aiDesignCount: aiDesignCount(),
        feeProductId: feeProductId || null,
        feeProductTitle: feeTitle,
        allFeeVariantsLoaded: feeVariants,
        selectedView: state.activeView,
        selectedPlacementKey: normalizePlacementName(placementSelect?.value || ''),
        selectedPlacementLabel: feeDetails.placementLabel,
        matchedFeeVariantId: feeDetails.variantId || null,
        matchedFeeVariantTitle: feeDetails.variantTitle || null,
        matchedFeeVariantPriceCents: feeDetails.priceCents,
        matchedFeeVariantAvailable: feeDetails.available,
        aiFeeProductId: aiFeeProductId || null,
        aiFeeVariantId: aiDetails.variantId || null,
        aiFeeQuantity: aiDesignCount(),
        aiFeeCartPayload: label.includes('AI') ? payload : null,
        shirtSubtotalCents: productSubtotalCents(),
        designFeeSubtotalCents: designFeeSubtotalCents(),
        aiDesignFeeSubtotalCents: aiDesignFeeSubtotalCents(),
        finalTotalCents: finalTotalCents(),
        designId: state.designId,
        selectedSizes: selectedSizeDebugData(),
        matchedVariantIds: Object.fromEntries(sizes.map((size) => [size, sizeVariantMap[size]?.id || null])),
        fallbackVariantId: firstAvailableVariantId || null,
        defaultVariantId: defaultVariantId || null,
        productVariantIdUsed: payload.items?.map((item) => item.id) || payload.id || null,
        payloadContainsImageData: cartPayloadHasImageData(payload),
      };

      cartDebug(`${label} payload`, debugDetails);

      const addResult = window.InkNfiberCart?.addJson
        ? await window.InkNfiberCart.addJson(payload, {
          refresh: false,
          source: 'inknfiber-custom-designer',
          errorMessage: 'Unable to add custom order to cart.',
        })
        : null;
      let responseJson = addResult?.body || null;

      if (!addResult) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const responseText = await response.text();

        try {
          responseJson = responseText ? JSON.parse(responseText) : null;
        } catch (_) {
          responseJson = null;
        }

        cartDebug(`${label} response`, {
          status: response.status,
          ok: response.ok,
          body: responseJson || responseText,
        });

        if (!response.ok) {
          const message = responseJson?.description || responseJson?.message || responseText || 'Unable to add custom order to cart.';
          throw new Error(message);
        }
      }

      if (addResult) {
        cartDebug(`${label} response`, {
          status: 200,
          ok: true,
          body: responseJson,
        });
      }

      lastCartAddSections = responseJson?.sections || lastCartAddSections;

      return responseJson;
    }

    async function removeDesignItemsByDesignId(designId) {
      if (!designId) return;

      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const matchingItems = cart.items.filter((item) => {
        return item.properties?.['Design ID'] === designId
          || item.properties?._inknfiber_parent_design_id === designId;
      });

      cartDebug('cart sync/remove design items', {
        designId,
        matchingLineKeys: matchingItems.map((item) => item.key),
      });

      for (const item of matchingItems) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
    }

    async function removeExistingDesignItems() {
      if (!editDesignId) return;
      await removeDesignItemsByDesignId(editDesignId);
    }

    function dispatchCartUpdate(cart) {
      if (window.InkNfiberCart?.dispatchCartUpdate) {
        window.InkNfiberCart.dispatchCartUpdate(cart, {
          source: 'inknfiber-custom-designer',
          sections: lastCartAddSections || {},
        });
        return;
      }

      const visibleItemCount = visibleCartItemCount(cart);

      try {
        sessionStorage.setItem(
          'cart-count',
          JSON.stringify({
            value: String(visibleItemCount),
            timestamp: Date.now(),
          })
        );
      } catch (_) {
        // Storage can be unavailable in private browsing modes.
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'inknfiber-custom-designer',
            data: {
              itemCount: visibleItemCount,
              source: 'inknfiber-custom-designer',
            },
          },
        })
      );
    }

    async function processUploadFiles(files) {
      uploadError.textContent = '';

      if (!files.length) {
        await runArtworkQualityCheck(null);
        return;
      }

      const invalid = files.find((file) => !fileIsAllowed(file));
      if (invalid) {
        await runArtworkQualityCheck(invalid);
        uploadInput.value = '';
        uploadError.textContent = 'Upload PNG, JPG, JPEG, or WEBP images only.';
        return;
      }

      for (const file of files) {
        const originalSrc = await readFileAsDataUrl(file);
        const dimensions = await imageDimensionsFromSrc(originalSrc);
        const elementId = addElement({
          type: 'image',
          name: file.name,
          src: originalSrc,
          originalSrc,
          content: '',
          scale: 42,
          naturalWidth: dimensions.naturalWidth,
          naturalHeight: dimensions.naturalHeight,
          backgroundRemoved: false,
          backgroundRemovedImageKey: '',
          backgroundRemovedImageUrl: '',
          backgroundRemovedFileName: '',
          backgroundRemovedMimeType: '',
          backgroundRemovalStatus: '',
          backgroundRemovalMessage: '',
          originalUploadPreserved: true,
        });
        originalUploadFiles.set(elementId, file);
        await runArtworkQualityCheck(file, elementId);
        uploadOriginalArtworkFileToR2(file, elementId);

        if (designerDebug) {
          transformDebugLog('layer added', {
            layerType: 'image',
            layerId: elementId,
            layerSource: 'upload',
            activeView: state.activeView,
            visibleLayerCount: visibleLayerCount(),
            placeholderHidden: Boolean(emptyState && (emptyState.hidden || emptyState.classList.contains('is-hidden'))),
            placeholderVisible: !Boolean(emptyState && (emptyState.hidden || emptyState.classList.contains('is-hidden'))),
          });
        }
      }

      saveCurrentDesign({ includePreviews: true, showStatus: false });
    }

    uploadInput.addEventListener('change', async () => {
      const files = Array.from(uploadInput.files || []);
      await processUploadFiles(files);
      uploadInput.value = '';
    });

    function openArtworkUploadPicker() {
      uploadInput?.click();
    }

    function eventHasFiles(event) {
      return Array.from(event.dataTransfer?.types || []).includes('Files');
    }

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
      window.addEventListener(eventName, (event) => {
        if (eventHasFiles(event)) event.preventDefault();
      });
    });

    uploadDropZone?.addEventListener('dragenter', (event) => {
      if (!eventHasFiles(event)) return;
      event.preventDefault();
      uploadDropZone.classList.add('is-drag-over');
    });

    uploadDropZone?.addEventListener('dragover', (event) => {
      if (!eventHasFiles(event)) return;
      event.preventDefault();
      uploadDropZone.classList.add('is-drag-over');
    });

    uploadDropZone?.addEventListener('dragleave', (event) => {
      if (uploadDropZone.contains(event.relatedTarget)) return;
      uploadDropZone.classList.remove('is-drag-over');
    });

    uploadDropZone?.addEventListener('drop', async (event) => {
      if (!eventHasFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      uploadDropZone.classList.remove('is-drag-over');
      const files = Array.from(event.dataTransfer?.files || []);
      await processUploadFiles(files);
    });

    emptyState?.addEventListener('click', (event) => {
      if (mobilePreviewMediaQuery.matches) return;
      if (hasVisibleElementsForActiveView()) return;
      event.preventDefault();
      event.stopPropagation();
      openArtworkUploadPicker();
    });

    safeArea?.addEventListener('click', (event) => {
      if (mobilePreviewMediaQuery.matches) return;
      if (hasVisibleElementsForActiveView()) return;
      event.preventDefault();
      event.stopPropagation();
      openArtworkUploadPicker();
    });

    safeArea?.addEventListener('keydown', (event) => {
      if (mobilePreviewMediaQuery.matches) return;
      if (hasVisibleElementsForActiveView()) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openArtworkUploadPicker();
    });

    addTextButton?.addEventListener('click', () => {
      const content = textInput?.value.trim();
      if (!content) {
        uploadError.textContent = 'Enter text before adding it to the design.';
        return;
      }

      uploadError.textContent = '';
      addElement({
        type: 'text',
        name: content.slice(0, 32),
        content,
        fontFamily: textFont?.value || 'Arial Black, Arial, sans-serif',
        color: textColor?.value || '#ffffff',
        scale: 30,
      });

      if (designerDebug) {
        transformDebugLog('layer added', {
          layerType: 'text',
          trigger: 'add-text-button',
          activeView: state.activeView,
          visibleLayerCount: visibleLayerCount(),
          placeholderHidden: Boolean(emptyState && (emptyState.hidden || emptyState.classList.contains('is-hidden'))),
          placeholderVisible: !Boolean(emptyState && (emptyState.hidden || emptyState.classList.contains('is-hidden'))),
        });
      }
    });

    textInput?.addEventListener('input', () => {
      const element = activeElement();
      if (element?.type !== 'text') return;
      element.content = textInput.value || 'Text';
      element.name = element.content.slice(0, 32);
      renderDesign();
    });

    textFont?.addEventListener('change', () => {
      const element = activeElement();
      if (element?.type !== 'text') return;
      element.fontFamily = textFont.value;
      renderDesign();
    });

    textColor?.addEventListener('input', () => {
      const element = activeElement();
      if (element?.type !== 'text' && element?.type !== 'symbol') return;
      element.color = textColor.value;
      renderDesign();
    });

    symbolPicker?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-symbol]');
      if (!button) return;

      addElement({
        type: 'symbol',
        name: button.dataset.symbol,
        content: button.dataset.symbol,
        color: textColor?.value || '#ffffff',
        scale: 34,
      });

      if (designerDebug) {
        transformDebugLog('layer added', {
          layerType: 'symbol',
          trigger: 'add-symbol',
          symbol: button.dataset.symbol,
          activeView: state.activeView,
          visibleLayerCount: visibleLayerCount(),
          placeholderHidden: Boolean(emptyState && (emptyState.hidden || emptyState.classList.contains('is-hidden'))),
          placeholderVisible: !Boolean(emptyState && (emptyState.hidden || emptyState.classList.contains('is-hidden'))),
        });
      }
    });

    if (aiPhotoshootEnabled) {
      aiPromptInput?.addEventListener('input', () => syncAiPromptWarning());
      aiDesignStyleSelect?.addEventListener('change', () => syncAiPromptWarning());
      aiOutputTypeSelect?.addEventListener('change', () => syncAiPromptWarning());
      aiIncludeTextInput?.addEventListener('change', () => syncAiPromptWarning());
      aiGenerateButton?.addEventListener('click', generateAiDesign);
      aiAddButton?.addEventListener('click', addGeneratedAiDesignToLayer);
    }

    elementList?.addEventListener('click', (event) => {
      const selectButton = event.target.closest('[data-select-element]');
      const removeButton = event.target.closest('[data-remove-element]');
      const removeBackgroundButton = event.target.closest('[data-remove-background-element]');
      const useOriginalButton = event.target.closest('[data-use-original-element]');

      if (removeBackgroundButton) {
        removeBackgroundForElement(removeBackgroundButton.dataset.removeBackgroundElement);
        return;
      }

      if (useOriginalButton) {
        useOriginalForElement(useOriginalButton.dataset.useOriginalElement);
        return;
      }

      if (selectButton) {
        setActiveElement(selectButton.dataset.selectElement);
      }

      if (removeButton) {
        removeElement(removeButton.dataset.removeElement);
      }
    });

    designer.addEventListener('pointerdown', (event) => {
      const target = event.target.closest('[data-element-id]');
      const isPreviewSurface = (mockup?.contains(event.target) || safeArea?.contains(event.target));
      const handle = event.target.closest('[data-transform-handle]');

      if ((!target || !elementsRoot?.contains(target)) && isPreviewSurface && state.activeElementId && !handle) {
        clearActiveElement('click-away');
        return;
      }

      if (!target || !elementsRoot?.contains(target)) return;

      const elementId = target.dataset.elementId;
      if (!state.elements.some((element) => element.id === elementId)) return;

      const wasActive = state.activeElementId === elementId;
      if (!wasActive) {
        setActiveElement(elementId, {
          reason: 'preview-layer-click',
        });

        // The first touch selects an object but remains a page-scroll gesture.
        // A subsequent touch on the active object performs drag/resize.
        if (event.pointerType === 'touch') return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (handle) {
        startElementTransform(elementId, handle.dataset.transformHandle, event);
        return;
      }

      startElementDrag(elementId, event);
    });

    function handleCanvasPointerMove(event) {
      if (state.transformMode && event.pointerId === state.transformPointerId) {
        event.preventDefault();
        updateElementTransformFromEvent(event);
        return;
      }

      if (!state.dragging || event.pointerId !== state.dragPointerId) return;
      event.preventDefault();
      setElementPositionFromEvent(event);
    }

    function handleCanvasPointerEnd(event) {
      if (state.transformMode && event.pointerId === state.transformPointerId) {
        event.preventDefault();
        stopElementTransform(event);
        return;
      }

      if (event.pointerId !== state.dragPointerId) return;
      event.preventDefault();
      state.dragging = false;
      state.dragPointerId = null;
      state.dragStart = null;
      state.interactionElementId = '';

      try {
        safeArea.releasePointerCapture(event.pointerId);
      } catch (_) {
        // Pointer capture may already be released by the browser.
      }

      const element = elementById(state.interactionElementId);
      transformDebugLog('pointerup drag commit', {
        pointerId: event.pointerId,
        elementId: state.interactionElementId,
        x: element ? round(element.x) : null,
        y: element ? round(element.y) : null,
        scale: element ? round(element.scale) : null,
        rotation: element ? round(element.rotation) : null,
      });

      renderDesign();
    }

    function cancelCanvasPointer() {
      state.dragging = false;
      state.dragPointerId = null;
      state.dragStart = null;
      state.interactionElementId = '';
      stopElementTransform();
    }

    safeArea.addEventListener('pointermove', handleCanvasPointerMove);
    window.addEventListener('pointermove', handleCanvasPointerMove);
    safeArea.addEventListener('pointerup', handleCanvasPointerEnd);
    window.addEventListener('pointerup', handleCanvasPointerEnd);
    safeArea.addEventListener('pointercancel', cancelCanvasPointer);
    window.addEventListener('pointercancel', cancelCanvasPointer);
    const handleMobilePreviewModeChange = () => renderElements();
    if (typeof mobilePreviewMediaQuery.addEventListener === 'function') {
      mobilePreviewMediaQuery.addEventListener('change', handleMobilePreviewModeChange);
    } else {
      mobilePreviewMediaQuery.addListener(handleMobilePreviewModeChange);
    }
    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (notesModal && !notesModal.hidden) {
        closeDesignNotesModal();
        return;
      }
      if (reviewModal && !reviewModal.hidden) {
        closeReviewDesignModal();
        return;
      }
      clearActiveElement('keyboard-escape');
    });

    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if ((notesModal && !notesModal.hidden) || (reviewModal && !reviewModal.hidden)) return;
      if (!state.activeElementId) return;
      if (isTypingOrControlTarget(event.target)) return;
      if (!elementById(state.activeElementId)) return;

      event.preventDefault();
      event.stopPropagation();
      removeElement(state.activeElementId, { clearSelection: true });
    });

    toolbarButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.dataset.customizerTool || button.dataset.customizerToolbarTarget || 'products';
        openToolbarTarget(target);
      });
    });

    notesOpenButtons
      .filter((button) => !button.dataset.customizerTool && !button.dataset.customizerToolbarTarget)
      .forEach((button) => button.addEventListener('click', openDesignNotesModal));
    notesCloseButtons.forEach((button) => button.addEventListener('click', closeDesignNotesModal));
    notesSaveButton?.addEventListener('click', saveDesignNotes);
    reviewButton?.addEventListener('click', openReviewDesignModal);
    reviewCloseButtons.forEach((button) => button.addEventListener('click', closeReviewDesignModal));
    reviewAddToCartButton?.addEventListener('click', addReviewedDesignToCart);
    [designNoteFront, designNoteBack, designNoteExtra].forEach((field) => {
      field?.addEventListener('input', () => {
        syncDesignNotesFromFields();
        scheduleAutoSave();
      });
    });
    saveDesignButton?.addEventListener('click', () => {
      saveCurrentDesign({ includePreviews: true, showStatus: true });
    });
    restoreSavedDesignButton?.addEventListener('click', restoreLatestSavedDesign);
    clearSavedDesignButton?.addEventListener('click', clearLatestSavedDesign);

    viewButtons.forEach((button) => {
      button.addEventListener('click', () => setActiveView(button.dataset.viewButton));
    });

    colorSwatchButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!colorSelect) return;
        const selectedByLabel = button.dataset.value || '';
        const selectedByHex = button.dataset.color || '';
        const requestedColor = selectedByLabel || selectedByHex || 'Black';
        setSelectedShirtColor(requestedColor);
        renderDesign();

        if (designerDebug) {
          const requestedHex = normalizeSwatchHex(selectedByHex);
          const selected = selectedColorData();
          transformDebugLog('shirt swatch selected', {
            requestedColor,
            requestedHex,
            resolvedLabel: selected.label,
            resolvedHex: selected.color,
            fallbackUsed: selected.fallbackUsed,
          });
        }
      });
    });

    [colorSelect, placementSelect].forEach((control) => {
      control?.addEventListener('input', () => {
        if (control === colorSelect) {
          setSelectedShirtColor(control.value);
        } else if (control === placementSelect) {
          state.placementsByView[state.activeView] = canonicalPlacementValue(control.value);
        }
        renderDesign();
      });
      control?.addEventListener('change', () => {
        if (control === colorSelect) {
          setSelectedShirtColor(control.value);
        } else if (control === placementSelect) {
          state.placementsByView[state.activeView] = canonicalPlacementValue(control.value);
        }
        renderDesign();
      });
    });

    sizeRows.forEach((row) => {
      const input = row.querySelector('[data-size-quantity]');
      const minus = row.querySelector('[data-quantity-minus]');
      const plus = row.querySelector('[data-quantity-plus]');

      const update = (nextValue) => {
        input.value = clamp(Number(nextValue || 0), 0, 99);
        renderDesign();
      };

      input.addEventListener('input', () => update(input.value));
      minus.addEventListener('click', () => update(Number(input.value || 0) - 1));
      plus.addEventListener('click', () => update(Number(input.value || 0) + 1));
    });

    sizeRange?.addEventListener('input', () => {
      const element = activeElement();
      if (!element) return;
      element.scale = Number(sizeRange.value);
      renderDesign();
    });

    rotationRange?.addEventListener('input', () => {
      if (!enableRotation) return;
      const element = activeElement();
      if (!element) return;
      element.rotation = Number(rotationRange.value);
      renderDesign();
    });

    undoButton?.addEventListener('click', undoTransform);
    redoButton?.addEventListener('click', redoTransform);

    resetButton?.addEventListener('click', resetActiveDesign);

    initAccordionControls();
    openToolbarTarget('products');
    if (!editDesignId && !(reorderId && reorderToken)) {
      syncPlacementOptions('Full Front');
    }

    addButton.addEventListener('click', async () => {
      await refreshPrintChargeAnalysisNow({ force: true });
      const validationMessage = validate();

      if (validationMessage) {
        setStatus(validationMessage, defaultVariantId ? 'error' : 'warning');
        return;
      }

      if (designerDebug) {
        transformDebugLog('add to cart clicked - running artwork quality gate', {
          uploadedFileCount: imageElements().filter((element) => originalUploadFiles.has(element.id)).length,
        });
      }
      const qualityApproved = await confirmArtworkQualityBeforeCart();
      if (!qualityApproved) {
        setStatus('Review or reupload artwork before adding this custom order to cart.', 'warning');
        return;
      }

      const printChargeAdjustmentError = validateEstimatorAdjustmentProduct(estimatorCheckoutDetails());
      if (printChargeAdjustmentError) {
        setStatus(printChargeAdjustmentError, 'error');
        return;
      }

      addButton.disabled = true;
      addButton.textContent = 'Uploading design...';
      setStatus('');
      viewCartLink.hidden = true;

      const storedPreview = persistDesignState();

      try {
        setStatus('Uploading mockup preview and original design file...', 'warning');
        lastCustomDesignUpload = await uploadCustomDesignAssets();
        const mainCartItems = buildMainCartItems();
        const feeCartItem = buildFeeCartItem();
        const aiFeeCartItem = buildAiFeeCartItem();
        const productPriceCorrectionCartItem = buildProductPriceCorrectionCartItem();
        const estimatorAdjustmentCartItem = buildEstimatorAdjustmentCartItem();

        addButton.textContent = editDesignId ? 'Updating...' : 'Adding...';
        lastCartAddSections = null;
        await removeExistingDesignItems();

        await postCartAdd({ items: mainCartItems }, 'custom product');
        if (feeCartItem) {
          try {
            await postCartAdd({ items: [feeCartItem] }, 'hidden custom design fee');
          } catch (feeError) {
            cartDebug('hidden custom design fee add failed after shirt add', {
              error: feeError?.message || feeError,
              designId: state.designId,
              feeCartItem,
              mainCartItems,
            });
            await removeDesignItemsByDesignId(state.designId);
            throw new Error('The custom shirt was not added because the design fee product could not be added. Please check the fee product configuration and try again.');
          }
        }
        if (aiFeeCartItem) {
          try {
            await postCartAdd({ items: [aiFeeCartItem] }, 'hidden AI design generation fee');
          } catch (feeError) {
            cartDebug('hidden AI design fee add failed after shirt add', {
              error: feeError?.message || feeError,
              designId: state.designId,
              aiFeeCartItem,
              mainCartItems,
            });
            await removeDesignItemsByDesignId(state.designId);
            throw new Error('The custom shirt was not added because the AI design fee product could not be added. Please check the AI fee product configuration and try again.');
          }
        }
        if (productPriceCorrectionCartItem) {
          try {
            await postCartAdd({ items: [productPriceCorrectionCartItem] }, 'hidden custom design product price correction');
          } catch (feeError) {
            cartDebug('hidden custom design product price correction add failed after shirt add', {
              error: feeError?.message || feeError,
              designId: state.designId,
              productPriceCorrectionCartItem,
              mainCartItems,
            });
            await removeDesignItemsByDesignId(state.designId);
            throw new Error('The custom shirt was not added because the product price adjustment could not be added. Please check the adjustment product configuration and try again.');
          }
        }
        if (estimatorAdjustmentCartItem) {
          try {
            await postCartAdd({ items: [estimatorAdjustmentCartItem] }, 'hidden custom design print charge');
          } catch (feeError) {
            cartDebug('hidden custom print charge add failed after shirt add', {
              error: feeError?.message || feeError,
              designId: state.designId,
              estimatorAdjustmentCartItem,
              mainCartItems,
            });
            await removeDesignItemsByDesignId(state.designId);
            throw new Error('The custom shirt was not added because the custom print charge could not be added. Please check the adjustment product configuration and try again.');
          }
        }

        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        dispatchCartUpdate(cart);

        const storageNote = storedPreview
          ? ''
          : ' Some preview images were too large for browser storage, so editing may require uploading artwork again.';

        setStatus(`Custom order details were added to cart.${storageNote}`, 'success');
        viewCartLink.hidden = false;
      } catch (error) {
        console.error('[InkNfiber custom designer] Add to cart failed:', error);
        setStatus(error.message || 'Something went wrong adding this custom order to cart. Please try again.', 'error');
      } finally {
        addButton.textContent = editDesignId ? 'Update Custom Order in Cart' : 'Add Custom Order to Cart';
        updateCartButton();
      }
    });

    window.addEventListener('resize', () => {
      renderDesign();
      syncOpenAccordionPanelHeights();
    });

    if (reorderId && reorderToken) {
      restoreFromReorderLink();
    } else if (editDesignId) {
      addButton.textContent = 'Update Custom Order in Cart';
      restoreDesignState(editDesignId);
    }

    if (updateSavedDesignControls() && !editDesignId && !(reorderId && reorderToken)) {
      setSaveDesignStatus('You have a saved custom design. Restore it?', 'warning');
    }

    renderDesign();
    autoSaveEnabled = true;
  });
  }

  function init(root = document) {
    initPlacementGuidelines(root);
    initCustomDesigners(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
